import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS, apiClient } from '../../config/api';
import { auth } from '../../firebase/config';
import { getActiveRoleFromStorage } from '../../utils/getActiveRole';
import { hasFormAccess, hasStateFormLibrary } from '../../utils/docusignFormAccess';
import { formatUtcNaiveInstantForViewer, getViewerTimezone } from '../../utils/dateTime';
import { isTdsSpqLoneWolfForm } from '../../utils/loneWolfForms';
import { filterParticipantsForSignature } from '../../utils/signatureParticipants';
import ColoradoFormPdfEditor from './ColoradoFormPdfEditor';
import NotesPanel from '../notes/NotesPanel';
import './TransactionDocuments.css';
import useDocAiSummaries from '../../features/docAiSummary/useDocAiSummaries';
import AiSummaryMenuItem from '../../features/docAiSummary/AiSummaryMenuItem';
import DocAiSummaryModal from '../../features/docAiSummary/DocAiSummaryModal';

// Display names for Colorado FormService categories — mapped from FormService's own
// `category` field (e.g. "Listing", "Contract", "Amendment") to the fuller names used by
// the source CO forms collection's own folder structure (e.g. "Listing Contracts",
// "Sales Contracts", "Amend Extend"). Verified 1:1 against every form name in each
// category before mapping — same 83 forms, just friendlier labels. Mirrors mobile's
// CO_FORM_CATEGORY_LABELS (DocsTab.tsx) — keep both in sync if this list changes.
const CO_FORM_CATEGORY_LABELS = {
  Listing: 'Listing Contracts',
  Contract: 'Sales Contracts',
  Addendum: 'Addenda',
  Disclosure: 'Disclosures',
  Notice: 'Notices',
  Counterproposal: 'Counterproposal',
  Amendment: 'Amend Extend',
  Closing: 'Closing',
  'Deed of Trust': 'Deeds of Trust',
  Deed: 'Deeds',
  'Promissory Note': 'Promissory Notes',
  'Bill of Sale': 'Bills of Sale',
  Optional: 'Optional',
};

// DocuSign readiness gate copy shown before creating a DocuSign room — mirrors
// mobile's DocuSignReadinessModal variants (not-connected / pending / no-provider),
// always called with hideContinueAnyway since room creation requires DocuSign.
const DOC_GATE_CONFIG = {
  not_connected: {
    icon: 'link-off',
    title: 'DocuSign Not Connected',
    body: 'DocuSign helps you e-sign forms as part of the transaction. Connect now, or continue without it and connect later from Connected Services.',
    primaryLabel: 'Connect DocuSign',
  },
  pending: {
    icon: 'clock',
    title: 'Invitation Pending',
    body: 'Your DocuSign invitation is still pending. Accept it from your email to unlock e-signing, or continue without DocuSign for now.',
    primaryLabel: 'Resend Invite',
  },
  no_provider: {
    icon: 'file-alert',
    title: 'No Form Provider',
    body: "You don't have any form providers set up. Add one in Connected Services to load state-specific forms, or continue without one for now.",
    primaryLabel: 'Add Form Provider',
  },
};

// Lone Wolf readiness gate copy — same visual treatment as DOC_GATE_CONFIG above (same
// modal markup/CSS classes, rendered separately below), for the non-Colorado equivalent of
// Add Form / Send For Signature. No 'no_provider' variant — Lone Wolf's a single form
// library per state, not a set of providers to choose from.
const LONEWOLF_GATE_CONFIG = {
  not_connected: {
    icon: 'link-off',
    title: 'Lone Wolf Not Connected',
    body: 'Lone Wolf helps you e-sign forms as part of the transaction. Connect your account from Connected Services to continue.',
    primaryLabel: 'Connect Lone Wolf',
  },
  pending: {
    icon: 'clock',
    title: 'Lone Wolf Invitation Pending',
    body: 'Your Lone Wolf account exists but is not yet Active. Accept your invitation from your email, or connect a different email from Connected Services.',
    primaryLabel: 'Go to Connected Services',
  },
};

// sessionStorage stash so an Add Form / Send for Signature attempt blocked by a
// disconnected DocuSign account can resume automatically after the user reconnects.
// Needed because — unlike mobile, where DocuSign OAuth happens in an in-app browser
// overlay that never unmounts the app — web's Connect flow does a full page reload
// (ConnectedServicesPage -> DocuSign -> DocusignCallbackPage), so no in-memory ref
// survives the round trip; intent has to be persisted. Same idiom this codebase
// already uses for other full-redirect auth flows (see sessionStorage usage in
// InvitePage.js / RegisterPage.js / LoginPage.js).
const DOCUSIGN_RESUME_KEY = 'docusign_resume_intent';
const DOCUSIGN_RESUME_TTL_MS = 10 * 60 * 1000;

const stashDocuSignResume = (transactionId, action) => {
  try {
    sessionStorage.setItem(DOCUSIGN_RESUME_KEY, JSON.stringify({ transactionId, action, ts: Date.now() }));
  } catch {
    // sessionStorage unavailable (private browsing etc.) — resume just won't fire
  }
};

const readDocuSignResume = () => {
  try {
    const raw = sessionStorage.getItem(DOCUSIGN_RESUME_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.transactionId || !parsed?.action || Date.now() - parsed.ts > DOCUSIGN_RESUME_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
};

const clearDocuSignResume = () => {
  try { sessionStorage.removeItem(DOCUSIGN_RESUME_KEY); } catch { /* ignore */ }
};

// Mirrors mobile's useDocuSignReadiness: checks OAuth connection, invite status, and
// form-provider access. Returns { variant, roomsUserId } — variant is null when ready.
// Shared by the Add Form gate, the Send-for-Signature gate, and the resume-effect's
// re-verify step, instead of duplicating the same three checks at each call site.
const checkDocuSignReadiness = async (firebaseToken, licenseState) => {
  try {
    const statusResp = await apiClient.get(API_ENDPOINTS.docusignOidcStatus, {
      headers: { 'Authorization': `Bearer ${firebaseToken}` }
    });
    const data = statusResp.data || {};
    const connected = data.connected === true;
    const inviteStatus = (data.inviteStatus || '').toLowerCase();
    // Mirrors mobile's hasFormAccess(): also treats a license-state default provider
    // (e.g. CAR for CA) as ready even if provisioned isn't explicitly true.
    // formProviders === null/undefined means the Rooms API was unreachable — treat
    // that as "unknown" rather than "no provider", so the gate fails open.
    let hasProvisionedProvider = data.formProviders == null
      ? null
      : hasFormAccess(data.formProviders, licenseState);

    // Fallback: some states (e.g. Colorado/CREC) have no form-provider integration on
    // this account at all — their forms are only exposed as a form library. Only check
    // this when the provider check definitively failed (not "unknown").
    if (hasProvisionedProvider === false) {
      const hasLibrary = await hasStateFormLibrary(licenseState, apiClient, API_ENDPOINTS, firebaseToken);
      if (hasLibrary) hasProvisionedProvider = true;
    }

    if (!connected) {
      return { variant: 'not_connected', roomsUserId: data.roomsUserId || null };
    }
    if (inviteStatus === 'pending') {
      return { variant: 'pending', roomsUserId: data.roomsUserId || null };
    }
    if (inviteStatus === 'active' && hasProvisionedProvider === false) {
      return { variant: 'no_provider', roomsUserId: null };
    }
    return { variant: null, roomsUserId: null };
  } catch (err) {
    console.error('DocuSign readiness check failed:', err);
    // Unknown status — fail open (backend enforces on create), same as before this
    // was extracted from handleCreateRoomPrimaryClick's inline try/catch.
    return { variant: null, roomsUserId: null };
  }
};

// DocuSign Rooms rejects room creation without Address 1, City, Postal code — calling
// sync-to-room on a transaction missing those fields always 500s with a generic
// "Failed to create DocuSign room" message. Precheck client-side so the user gets an
// actionable message instead of that dead end.
const ADDRESS_MISSING_MSG = 'Add a property address, city, and postal code before opening DocuSign.';
const hasCompleteAddress = (t) =>
  !!(t?.propertyAddress?.trim() && t?.propertyCity?.trim() && t?.propertyZip?.trim());

// ── Feature flag ───────────────────────────────────────────────────────────
// Set to false to revert "Add Form" to the standard Select-Forms modal for all agents.
const DOCUSIGN_ROOMS_REDIRECT_FOR_CA = true;
const DOCUSIGN_ROOMS_TARGET_STATE    = 'CA'; // only agents licensed in this state are redirected

// CMA reports are published to GCS as "CMA — <address> - <report date>.pdf" (mirrors
// mobile DocsTab.tsx's displayFileName). Strip the prefix for display and use it to
// detect CMA files for cascade-delete.
const CMA_FILENAME_PREFIX = /^CMA\s+[—-]\s+/i;
const isCmaFileName = (fileName) => CMA_FILENAME_PREFIX.test(fileName || '');
const displayFileName = (fileName) => (fileName || '').replace(CMA_FILENAME_PREFIX, '');

// ── Dynamic document card sizing ────────────────────────────────────────────
// Rows render at a constant height regardless of document count; the card
// caps at DYN_CARD_HEIGHT and scrolls internally past that (see
// renderDynamicDocs) instead of shrinking rows down to an icon-only grid.
const DYN_CARD_HEIGHT = 460;
const DYN_ROW_MAX = 84;

// ── Folders (mirrors mobile DocsTab.tsx) ────────────────────────────────────
// The "service-request" auto-folder (spec §5) needs a two-word display so
// the folder card reads "Service Request" not "Service-request".
// "disclosure" is pluralised in the display label so the card reads
// "Disclosures" (matches the nav tab name) even when the backend folder
// slug is the singular form.
const displayFolderName = (raw) => {
  const trimmed = (raw || '').trim();
  if (!trimmed) return '';
  if (trimmed.toLowerCase() === 'service-request') return 'Service Request';
  if (trimmed.toLowerCase() === 'disclosure') return 'Disclosures';
  if (trimmed.length <= 3) return trimmed.toUpperCase(); // acronyms like "cma", "nhd"
  return trimmed[0].toUpperCase() + trimmed.slice(1).toLowerCase();
};

// Matches backend regex ^[a-z0-9-]{1,40}$
const sanitizeFolderName = (raw) =>
  (raw || '').trim().toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40);

// Document uploadedAt/createdAt comes back from transaction-service as a bare
// LocalDateTime (UTC digits, no 'Z') — render in the viewer's own
// profile-state timezone, not the device's.
const formatFolderDateTime = (iso) => {
  if (!iso) return '';
  const { dateLabel, timeLabel } = formatUtcNaiveInstantForViewer(iso);
  if (!dateLabel) return '';
  return timeLabel ? `${dateLabel} · ${timeLabel}` : dateLabel;
};
// The report date is the text after the last " - " in the (prefix-stripped) filename,
// e.g. "123 Main St, San Francisco, CA 94105 - Jan 5.pdf" -> "Jan 5".
const parseCmaReportDate = (fileName) => {
  const stripped = displayFileName(fileName).replace(/\.pdf$/i, '');
  const idx = stripped.lastIndexOf(' - ');
  return idx === -1 ? null : stripped.slice(idx + 3).trim();
};
const formatCmaReportDay = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: getViewerTimezone() });
};
const buildCmaAddress = (transaction) => [
  transaction?.propertyAddress,
  transaction?.propertyCity,
  transaction?.propertyState,
  transaction?.propertyZip,
].filter(Boolean).join(', ');

// Share-recipient helpers — mirrors mobile DocsTab.tsx's recipient picker over
// transaction.participants (formatRoleDisplay / avatar initials / id resolution).
const SHARE_ROLE_LABEL = {
  BUYER: 'Buyer',
  SELLER: 'Seller',
  BROKER: 'Broker',
  BUYER_AGENT: "Buyer's Agent",
  BUYERS_AGENT: "Buyer's Agent",
  LISTING_AGENT: 'Listing Agent',
  CO_LISTING_AGENT: 'Co-Listing Agent',
  CO_BUYER_AGENT: "Co-Buyer's Agent",
  CO_BUYERS_AGENT: "Co-Buyer's Agent",
  AGENT: 'Agent',
  TRANSACTION_COORDINATOR: 'Transaction Coordinator',
  ESCROW_OFFICER: 'Escrow Officer',
  TITLE_OFFICER: 'Title Officer',
  LENDER: 'Lender',
  LOAN_OFFICER: 'Loan Officer',
  APPRAISER: 'Appraiser',
  INSPECTOR: 'Inspector',
  ATTORNEY: 'Attorney',
};
const formatShareRole = (role) => SHARE_ROLE_LABEL[(role || '').toUpperCase()] || role || '';
const getParticipantId = (p) => p.id || p.userId || p.email;
const getParticipantName = (p) => p.fullName || p.name || [p.firstName, p.lastName].filter(Boolean).join(' ') || p.email || 'Unnamed';
const getParticipantInitials = (p) => {
  const name = getParticipantName(p);
  const initials = name.split(/\s+/).filter(Boolean).map(s => s.charAt(0).toUpperCase()).join('').slice(0, 2);
  return initials || 'U';
};
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const TransactionDocuments = ({ transaction, participants = [], accessControl, onDocumentsChange, onNavigateToAuditTrail }) => {
  const navigate = useNavigate();
  const [docFilter, setDocFilter] = useState('all');
  const [activeDocTab, setActiveDocTab] = useState('transaction-documents'); // 'forms' | 'transaction-documents'
  const [showCreateDocModal, setShowCreateDocModal] = useState(false);
  const [isFetchingRoomId, setIsFetchingRoomId] = useState(false);
  // Bulk Draft/Sent/Signed status for every custom PDF ever sent for signature via Lone Wolf on
  // this transaction — keyed by documentId, each value is that document's full send history
  // (most recent attempt first). Populated once per transaction load, same pattern as DocuSign's
  // recipientsByEnvelope bulk fetch.
  const [lonewolfDocSignings, setLonewolfDocSignings] = useState({});
  const [createdRoomId, setCreatedRoomId] = useState(null); // local cache so a freshly-created room is usable immediately
  const [docGateVariant, setDocGateVariant] = useState(null); // null | 'not_connected' | 'pending' | 'no_provider'
  const [docGateAction, setDocGateAction] = useState(null); // 'addForm' | 'sendForSignature' — picks the gate's body copy
  const [docGateRoomsUserId, setDocGateRoomsUserId] = useState(null);
  const [docGateResendingInvite, setDocGateResendingInvite] = useState(false);
  // Lone Wolf readiness gate — non-Colorado equivalent of the DocuSign gate above, same
  // modal treatment (see LONEWOLF_GATE_CONFIG). No roomsUserId/resend-invite concept —
  // Lone Wolf has no per-invite resend API here, just a link to Connected Services.
  const [loneWolfGateVariant, setLoneWolfGateVariant] = useState(null); // null | 'not_connected' | 'pending'
  const [loneWolfGateAction, setLoneWolfGateAction] = useState(null); // 'addForm' | 'sendForSignature'
  // CA-DRE permission-required modal (TC-only) — { title, body } | null
  const [permissionModal, setPermissionModal] = useState(null);

  // Documents list state
  const [documents, setDocuments] = useState([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(true);
  const [documentsError, setDocumentsError] = useState(null);

  // Select Forms Modal State
  const [activeModalTab, setActiveModalTab] = useState('browse');
  const [searchQuery, setSearchQuery] = useState('');
  const [formLibraries, setFormLibraries] = useState([]); // All libraries from API
  const [librarySearchQuery, setLibrarySearchQuery] = useState(''); // Search within libraries sidebar
  const [selectedLibrary, setSelectedLibrary] = useState(null); // Currently selected library
  const [availableForms, setAvailableForms] = useState([]); // Forms in selected library
  const [selectedForms, setSelectedForms] = useState([]);
  const [isLoadingForms, setIsLoadingForms] = useState(false);
  const [isLoadingLibraryForms, setIsLoadingLibraryForms] = useState(false); // Loading state for library forms
  const [formsError, setFormsError] = useState(null);
  // Colorado form editor (FormService manual overrides)
  const [editingCoForm, setEditingCoForm] = useState(null);
  const [editCoFields, setEditCoFields] = useState({});
  // New PDF-based editor target: { packageId, instanceId, name } | null
  const [editingCoPdf, setEditingCoPdf] = useState(null);
  const [isSavingCoForm, setIsSavingCoForm] = useState(false);
  const [libraryFormsError, setLibraryFormsError] = useState(null); // Error state for library forms

  // Function to get Firebase token
  const getFirebaseToken = useCallback(() => {
    return new Promise((resolve, reject) => {
      // If user is already available, get token directly
      if (auth.currentUser) {
        auth.currentUser.getIdToken(true).then(resolve).catch(reject);
        return;
      }
      // Otherwise wait for auth state to be ready (with timeout)
      const timeoutId = setTimeout(() => {
        unsubscribe();
        reject(new Error('Firebase auth timeout'));
      }, 10000);
      const unsubscribe = auth.onAuthStateChanged(async (user) => {
        clearTimeout(timeoutId);
        unsubscribe();
        if (user) {
          try {
            const token = await user.getIdToken(true);
            resolve(token);
          } catch (error) {
            reject(error);
          }
        } else {
          reject(new Error('No authenticated user'));
        }
      });
    });
  }, []);

  // Resolves which email to use for a Lone Wolf status check — prefers the caller's saved
  // loneWolfEmail override (set via ConnectedServicesPage.js) over their Rialty login email,
  // since a Rialty user's Lone Wolf account isn't assumed to share their login email. The
  // backend's own self-check (LoneWolfController.getUserStatus) compares against this same
  // override, not the login email — sending the wrong one gets a 403 ("attempted to look up
  // X") even though it's the same person, which silently falls through to DocuSign below.
  // Falls back to login email if fetching the profile fails or no override is saved.
  const resolveLoneWolfCheckEmail = useCallback(async (firebaseToken, loginEmail) => {
    try {
      const meResp = await apiClient.get(API_ENDPOINTS.userMe, {
        headers: { 'Authorization': `Bearer ${firebaseToken}` }
      });
      const savedLoneWolfEmail = meResp.data?.loneWolfEmail || meResp?.loneWolfEmail || null;
      return savedLoneWolfEmail || loginEmail;
    } catch (e) {
      console.warn('[LoneWolf] Failed to fetch saved loneWolfEmail override, using login email:', e);
      return loginEmail;
    }
  }, []);

  // Fetch documents from API
  const fetchDocuments = useCallback(async () => {
    if (!transaction?.id) {
      setIsLoadingDocuments(false);
      return;
    }

    setIsLoadingDocuments(true);
    setDocumentsError(null);

    try {
      const firebaseToken = await getFirebaseToken();

      // Colorado: the Forms list comes from FormService (our generated forms), not DocuSign.
      if ((transaction?.propertyState || '').toUpperCase() === 'CO') {
        const auth = { headers: { 'Authorization': `Bearer ${firebaseToken}` } };
        let packageId;
        try {
          const pkgResp = await apiClient.get(API_ENDPOINTS.formsPackagesByTransaction(transaction.id), auth);
          const pkgs = Array.isArray(pkgResp.data) ? pkgResp.data : (pkgResp.data?.data || []);
          packageId = pkgs[0]?.packageId || pkgs[0]?.id;
        } catch (e) { /* no package yet — nothing generated */ }

        if (!packageId) {
          setDocuments([]);
          setIsLoadingDocuments(false);
          return;
        }

        const currentPropertyId = getCurrentInterestedPropertyId();
        const formsUrl = API_ENDPOINTS.formsPackageForms(packageId)
          + (currentPropertyId ? `?currentPropertyId=${encodeURIComponent(currentPropertyId)}` : '');
        const formsResp = await apiClient.get(formsUrl, auth);
        const instances = Array.isArray(formsResp.data) ? formsResp.data : (formsResp.data?.data || []);
        // Show TWO kinds of rows per form, mirroring the CA Original+Envelope card model: the
        // Original Form (the FormService record itself, unaffected by envelope state, always
        // shown, always View/Edit + Send for Signature) plus one Envelope row per version ever
        // sent (from `versions` when the form was sent more than once, else a single synthetic
        // row from the instance's own envelopeId/status). Both share the real instanceId
        // (doc.id/documentId) since View/Edit resolve the PDF by that id — only _isEnvelopeRow
        // distinguishes them for status/visibility/rendering purposes.
        const docsList = [];
        instances.forEach((fi) => {
          const st = (fi.status || '').toString().toLowerCase();
          const base = {
            id: fi.id || fi.instanceId,
            documentId: fi.id || fi.instanceId,
            formId: fi.formId,
            name: fi.formName || fi.formId || 'Colorado Form',
            type: 'Colorado Form',
            isDynamic: true,
            status: st,
            isSigned: st === 'signed' || st === 'submitted',
            createdDate: fi.lastPopulatedAt || fi.createdAt,
            _formService: true,
            _packageId: packageId,
            _outputPath: fi.populatedGcsPath,
            _coverage: fi.populationCoverage,
            _fields: { ...(fi.populatedFields || {}), ...(fi.manualOverrides || {}) },
            // Marks this row as the permanent, always-editable Original — same contract as CA's
            // Original card: unconditional Edit/Send for Signature, never reflects envelope state.
            // Without this, the shared cfg below would fall through to the plain-doc branch and key
            // off `status`, which DOES advance to "sent" once any envelope exists, wrongly hiding
            // Send for Signature on the very card that's supposed to always offer it.
            _isOriginalRow: true,
          };
          docsList.push(base);

          const versions = Array.isArray(fi.versions) && fi.versions.length > 0
            ? fi.versions
            : (fi.docusignEnvelopeId ? [{ version: null, envelopeId: fi.docusignEnvelopeId, status: st }] : []);
          const showVersionNumbers = versions.length > 1;
          versions.forEach((v) => {
            docsList.push({
              ...base,
              id: `${base.id}-env-${v.version || v.envelopeId}`,
              envelopeId: v.envelopeId,
              version: v.version,
              status: (v.status || st || '').toString().toLowerCase(),
              isSigned: (v.status || '').toString().toLowerCase() === 'signed' || (v.status || '').toString().toLowerCase() === 'submitted',
              _isOriginalRow: false,
              _isEnvelopeRow: true,
              _showVersionNumber: showVersionNumbers,
            });
          });
        });

        // For forms that have a live DocuSign envelope, pull the real recipient list so we can
        // show the true signing state (Received/Signed) instead of the stored FormService status,
        // which only ever advances to SENT — nothing calls markSigned() to flip it to SIGNED, so
        // it goes stale the moment DocuSign reports completion. Also used below to gate buyer/
        // seller visibility to only the forms where they're an actual recipient (email match),
        // not just any form that happens to be sent on this transaction.
        const currentUserEmail = (() => {
          try { return (JSON.parse(localStorage.getItem('user') || '{}')).email || ''; } catch { return ''; }
        })();
        const envelopeIds = [...new Set(docsList.map(d => d.envelopeId).filter(Boolean))];
        if (envelopeIds.length > 0) {
          const recipientsByEnvelope = new Map();
          await Promise.all(envelopeIds.map(async (envId) => {
            try {
              const recResp = await apiClient.get(API_ENDPOINTS.envelopeRecipients(envId), auth);
              recipientsByEnvelope.set(envId, recResp.data?.signers || []);
            } catch (e) {
              // Fail open per-envelope — that form just keeps its stored status/visibility.
              console.warn('[CO Forms] Failed to fetch recipients for envelope', envId, e);
            }
          }));
          docsList.forEach((d) => {
            if (!d.envelopeId) return;
            const signers = recipientsByEnvelope.get(d.envelopeId);
            if (!signers) return;
            const matched = currentUserEmail
              ? signers.find(s => (s.email || '').toLowerCase() === currentUserEmail.toLowerCase())
              : null;
            d._isCurrentUserRecipient = !!matched;
            d._envelopeRecipientStatus = matched ? (matched.status || '').toLowerCase() : null;
            // Agent/TC view: they're the sender, not a recipient, so there's no personal match to
            // key off. Their badge instead summarizes the whole envelope — Signed only once every
            // recipient has completed, Sent for any other in-progress state (including the
            // freshly-sent case where every recipient is still sitting at 'created').
            d._envelopeAllCompleted = signers.length > 0 &&
              signers.every(s => (s.status || '').toLowerCase() === 'completed');
          });
        }

        setDocuments(docsList);
        setIsLoadingDocuments(false);
        return;
      }

      // Mirrors mobile DocsTab.tsx: non-agent roles (BUYER and SELLER) use the
      // shared-with endpoint which only returns documents sent to that user.
      // Agents use the main endpoint which returns all room documents.
      const _fetchRole = (getActiveRoleFromStorage() || '').toUpperCase();
      const isClientRole = _fetchRole === 'BUYER' || _fetchRole === 'SELLER';
      // A view-only Broker has NO Lone Wolf account of their own — they view this
      // transaction's forms through the owning Agent's account, which the backend proxies
      // automatically (LoneWolfController.resolveDocViewAuth detects the Broker participant
      // and acts as primaryAgentId). So skip the caller's own active-user status check for a
      // broker and fetch the forms directly. View-only: canManage is false for a broker, so
      // the kebab already shows only "View" (no Edit / Send for Signature / Delete).
      const isBrokerViewerRole = ['BROKER', 'BROKER_MANAGER', 'BROKER_ADMIN'].includes(_fetchRole);

      // Which provider this transaction actually uses was decided once, at creation time,
      // and pinned server-side (EsignatureServiceRouter.pinProvider) — read that directly
      // instead of re-deriving a guess from the agent's state every time. A Lone-Wolf-pinned
      // transaction always attempts Lone Wolf and surfaces a real error on failure rather than
      // silently falling back to DocuSign (previously: a status-check failure silently routed
      // a Lone Wolf transaction's forms through DocuSign instead — see 2026-09-04 fix). Clients
      // still use the DocuSign shared-with view regardless of provider (Lone Wolf has no
      // per-recipient signing status yet, so there's nothing accurate to show a buyer/seller).
      // Missing/null esignatureProvider (transactions created before this field existed) is
      // treated as DocuSign, same as it always was.
      const isLoneWolfTransaction = transaction?.esignatureProvider === 'LONEWOLF';
      if (isLoneWolfTransaction && !isClientRole) {
        const currentUserEmail = (() => {
          try { return (JSON.parse(localStorage.getItem('user') || '{}')).email || ''; } catch { return ''; }
        })();
        let isLoneWolfActiveUser = isBrokerViewerRole;
        let statusCheckFailed = false;
        if (!isBrokerViewerRole && currentUserEmail) {
          try {
            const emailToCheck = await resolveLoneWolfCheckEmail(firebaseToken, currentUserEmail);
            const statusResp = await apiClient.get(API_ENDPOINTS.lonewolfUserStatus(emailToCheck), {
              headers: { 'Authorization': `Bearer ${firebaseToken}` }
            });
            isLoneWolfActiveUser = statusResp.data?.active === true;
          } catch (e) {
            console.error('[LW Forms] Lone Wolf status check failed for a Lone-Wolf-pinned transaction:', e);
            statusCheckFailed = true;
          }
        }

        if (isLoneWolfActiveUser) {
          const formsResp = await apiClient.get(API_ENDPOINTS.lonewolfTransactionForms(transaction.id), {
            headers: { 'Authorization': `Bearer ${firebaseToken}` }
          });
          const formsData = Array.isArray(formsResp.data) ? formsResp.data : [];

          // Signing status (Agent/TC only — LoneWolfController's role gate returns this same
          // endpoint's data pre-scoped: status included for Agent/TC, omitted entirely for
          // other callers). Matched to a form by formVersionId === the form's own versionId.
          // Failure here doesn't block showing the forms themselves — just means no status badge.
          let signingByVersionId = {};
          let signings = [];
          try {
            const signingResp = await apiClient.get(API_ENDPOINTS.lonewolfSigningDocuments(transaction.id), {
              headers: { 'Authorization': `Bearer ${firebaseToken}` }
            });
            signings = Array.isArray(signingResp.data) ? signingResp.data : [];
            signingByVersionId = Object.fromEntries(
              signings.filter(s => s.formVersionId).map(s => [s.formVersionId, s])
            );
          } catch (e) {
            console.warn('[LW Forms] Failed to fetch signing status:', e);
          }

          // strikeOut:true means voided — excluded outright, same as agents never seeing
          // voided/declined DocuSign envelopes via getVisibleForms() below. NOT filtering on
          // visible:false — a freshly-attached form comes back visible:false by default (Lone
          // Wolf's own internal flag, confirmed live), so filtering on it would hide a form the
          // instant after adding it.
          // Two separate rows per attached form, once a signing exists for it — mirrors the
          // DocuSign Original/Envelope split: the form row IS the original fillable form and its
          // status always stays 'Draft' (editing it doesn't change once sent), while a second
          // "Lone Wolf Signing" row carries the actual live signing progress (Sent/In
          // Progress/Signed), matched to this form by formVersionId === the form's own versionId.
          const docsList = formsData
            .filter(f => !f.strikeOut)
            .flatMap(f => {
              const signing = signingByVersionId[f.versionId];
              const baseId = f.documentId || f.formId;
              const originalRow = {
                id: baseId,
                documentId: baseId,
                name: f.name,
                type: 'Lone Wolf Form',
                isDynamic: false,
                status: 'draft',
                isSigned: false,
                createdDate: f.modified || f.added,
                userDisplayStatus: 'Draft',
                _loneWolf: true,
                _packageId: f.packageId,
                _versionId: f.versionId,
                _acronym: f.acronym,
                _signing: null,
              };
              if (!signing) return [originalRow];
              const signingRow = {
                id: `${baseId}-signing-${signing.signingId}`,
                documentId: baseId,
                name: f.name,
                type: 'Lone Wolf Signing',
                isDynamic: false,
                status: 'signing',
                isSigned: signing.finalized === true,
                createdDate: f.modified || f.added,
                _loneWolf: true,
                _loneWolfSigningRow: true,
                _packageId: f.packageId,
                _versionId: f.versionId,
                _acronym: f.acronym,
                _signing: signing,
              };
              return [originalRow, signingRow];
            });
          // Signings that don't match any attached form (e.g. sent from an uploaded PDF via
          // "Send for signature", which has no Lone Wolf form version) still get their own
          // "Lone Wolf Signing" row — otherwise they never show, even once fully signed.
          // Same row shape as above minus the form-only fields; its open/menu/status
          // handling only reads _signing.
          // Matched against every attached form (voided ones too), so a voided form's
          // signing stays hidden exactly as before.
          const formVersionIds = new Set(formsData.map(f => f.versionId).filter(Boolean));
          const unmatchedSigningRows = signings
            .filter(s => s?.signingId && !(s.formVersionId && formVersionIds.has(s.formVersionId)))
            .map(s => ({
              id: `signing-${s.signingId}`,
              documentId: s.signingId,
              name: s.name || 'Lone Wolf Signing',
              type: 'Lone Wolf Signing',
              isDynamic: false,
              status: 'signing',
              isSigned: s.finalized === true,
              _loneWolf: true,
              _loneWolfSigningRow: true,
              _signing: s,
            }));
          setDocuments([...docsList, ...unmatchedSigningRows]);
          setIsLoadingDocuments(false);
          return;
        }

        // This transaction is pinned to Lone Wolf — never silently fall back to DocuSign.
        setDocumentsError(
          statusCheckFailed
            ? "Could not verify your Lone Wolf connection. Please check Connected Services and try again."
            : "Connect your Lone Wolf account (Connected Services) to view this transaction's forms."
        );
        setIsLoadingDocuments(false);
        return;
      }

      if (isClientRole && isLoneWolfTransaction) {
        // Buyer/Seller/other client on a Lone-Wolf-pinned transaction: show ONLY fully-signed
        // documents, never in-progress status — LoneWolfController.getSigningDocumentsForTransaction
        // already enforces this server-side (finalized-only, matched to caller's own email via
        // Lone Wolf Contacts), so this call is safe to make unconditionally for any client role;
        // it simply returns an empty list if nothing's finalized yet or the caller isn't a party
        // to any signing on this transaction.
        try {
          const signingResp = await apiClient.get(API_ENDPOINTS.lonewolfSigningDocuments(transaction.id), {
            headers: { 'Authorization': `Bearer ${firebaseToken}` }
          });
          const signings = Array.isArray(signingResp.data) ? signingResp.data : [];
          const docsList = signings.map(s => ({
            id: s.signingId,
            documentId: s.signingId,
            name: s.name,
            type: 'Lone Wolf Form',
            isDynamic: false,
            status: 'signed',
            isSigned: true,
            userDisplayStatus: 'Signed',
            _loneWolf: true,
            // No _packageId here (only the original-form fetch path has one) — this row IS
            // the finalized signing itself, so View/menu must route to the signed-document
            // viewer, never handleView/EditLoneWolfForm (those need a packageId this row
            // doesn't have).
            _clientSignedOnly: true,
            _versionId: s.formVersionId,
            _signing: s,
          }));
          setDocuments(docsList);
        } catch (e) {
          console.error('[LW Forms] Failed to fetch signed documents for client view:', e);
          setDocuments([]);
        }
        setIsLoadingDocuments(false);
        return;
      }

      if (isClientRole) {
        const response = await apiClient.get(
          API_ENDPOINTS.transactionDocusignDocumentsSharedWith(transaction.id),
          { headers: { 'Authorization': `Bearer ${firebaseToken}` } }
        );
        const envelopes = response.data?.envelopes || [];
        // Flatten envelopes → documents, skip DocuSign summary/certificate pages.
        // isSigned mirrors mobile: doc.signerStatus === 'signed' OR envelope completed.
        const docsList = envelopes.flatMap(envelope =>
          (envelope.documents || [])
            .filter(doc => doc.type !== 'summary' && doc.documentId !== 'certificate')
            .map(doc => ({
              documentId: `${envelope.envelopeId}_${doc.documentId}`, // unique key across envelopes
              name: doc.name,
              envelopeId: envelope.envelopeId,
              _envelopeDocumentId: doc.documentId, // original DocuSign documentId for download
              status: doc.signerStatus || envelope.status,
              isSigned: (doc.signerStatus || '').toLowerCase() === 'signed'
                     || (envelope.signerStatus || '').toLowerCase() === 'completed',
              type: 'DocuSign Form',
              createdDate: envelope.signedDateTime || envelope.deliveredDateTime || envelope.sentDateTime,
              emailSubject: envelope.emailSubject,
              signedDateTime: envelope.signedDateTime,
              deliveredDateTime: envelope.deliveredDateTime,
              sentDateTime: envelope.sentDateTime,
              _sharedWith: true,
            }))
        );
        setDocuments(docsList);
      } else {
        const response = await apiClient.get(
          API_ENDPOINTS.transactionDocusignDocuments(transaction.id),
          { headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' } }
        );
        console.log('Documents fetched:', response.data);
        const docsData = response.data;
        let docsList = [];
        if (Array.isArray(docsData)) {
          docsList = docsData;
        } else if (docsData && Array.isArray(docsData.data)) {
          docsList = docsData.data;
        } else if (docsData && Array.isArray(docsData.documents)) {
          docsList = docsData.documents;
        } else if (docsData && Array.isArray(docsData.content)) {
          docsList = docsData.content;
        }
        setDocuments(docsList);
      }
    } catch (error) {
      // Fail open to the empty state (same as the Transaction Documents / GCS tab's
      // fetchGcsDocuments, which only logs on error) instead of surfacing a "Try Again"
      // error — a transaction with no forms shouldn't look like a load failure. Meaningful,
      // actionable states (e.g. the Lone Wolf "connect your account" gate above) still set
      // documentsError explicitly and keep their Try Again affordance.
      console.error('Error fetching documents:', error);
      setDocuments([]);
    } finally {
      setIsLoadingDocuments(false);
    }
  }, [transaction?.id, transaction?.propertyState, transaction?.esignatureProvider, getFirebaseToken, resolveLoneWolfCheckEmail]);

  // Fetch documents on component mount
  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Fetch available forms from API (Form Libraries)
  // Colorado: load our FormService CREC catalog (GCS templates) instead of DocuSign form
  // libraries. Maps the catalog into the same {libraries, forms} shape the picker modal
  // already renders — one "library" per form category (Listing Contracts, Sales Contracts,
  // Addenda, ...) instead of a single flat 83-form library, so the existing sidebar
  // library-picker becomes a category picker for Colorado: pick a category on the left,
  // its forms appear on the right (handleLibrarySelect already just filters to
  // library.forms client-side for _source==='formservice', no per-click API call needed).
  const fetchColoradoFormsCatalog = useCallback(async () => {
    setIsLoadingForms(true);
    setFormsError(null);
    try {
      const firebaseToken = await getFirebaseToken();
      const response = await apiClient.get(API_ENDPOINTS.formsCatalog('CO'), {
        headers: { 'Authorization': `Bearer ${firebaseToken}` }
      });
      const catalog = Array.isArray(response.data) ? response.data : (response.data?.data || []);

      const order = [];
      const byCategory = new Map();
      catalog.forEach((f) => {
        const rawCategory = f.category || 'Other';
        if (!byCategory.has(rawCategory)) {
          order.push(rawCategory);
          byCategory.set(rawCategory, []);
        }
        byCategory.get(rawCategory).push({
          id: f.formId,
          formId: f.formId,
          name: f.formName,
          title: f.formName,
          category: f.category,
          required: f.required,
          _source: 'formservice',
        });
      });

      const libraries = order.map((rawCategory) => {
        const forms = byCategory.get(rawCategory);
        const label = CO_FORM_CATEGORY_LABELS[rawCategory] || rawCategory;
        return {
          id: `crec-colorado-${rawCategory}`,
          name: label,
          libraryName: label,
          formCount: forms.length,
          forms,
          _source: 'formservice',
        };
      });

      setFormLibraries(libraries);
      const firstLibrary = libraries[0] || null;
      setSelectedLibrary(firstLibrary);
      setAvailableForms(firstLibrary?.forms || []);
    } catch (error) {
      console.error('Error fetching Colorado forms catalog:', error);
      setFormsError('Failed to load Colorado forms. Please try again.');
      setFormLibraries([]);
      setAvailableForms([]);
    } finally {
      setIsLoadingForms(false);
    }
  }, [getFirebaseToken]);

  // California, Active-on-Lone-Wolf agent: single synthetic "library" of CAR forms, mirroring
  // fetchColoradoFormsCatalog's shape ({id, name, forms, _source}) so handleLibrarySelect's
  // existing _source branch (below) shows it inline with no extra API call.
  const fetchLoneWolfCarForms = useCallback(async () => {
    setIsLoadingForms(true);
    setFormsError(null);
    try {
      const firebaseToken = await getFirebaseToken();
      const auth = { headers: { 'Authorization': `Bearer ${firebaseToken}` } };

      // Pass the transaction id so the backend reads the OWNING Agent's CAR library — a TC
      // adding a form has no libraries on their own Lone Wolf account.
      const libResp = await apiClient.get(API_ENDPOINTS.lonewolfLibraryByAcronym('CAR', transaction?.id), auth);
      const libId = libResp.data?.libId;
      if (!libId) {
        throw new Error('CAR library not found');
      }

      const formsResp = await apiClient.get(API_ENDPOINTS.lonewolfLibraryForms(libId, transaction?.id), auth);
      const rawForms = Array.isArray(formsResp.data) ? formsResp.data : [];
      const forms = rawForms.map((f) => ({
        id: f.activeFormVersion?.formId || f.id,
        formId: f.activeFormVersion?.formId || f.id,
        name: f.activeFormVersion?.name || f.name,
        title: f.activeFormVersion?.name || f.name,
        acronym: f.activeFormVersion?.acronym || f.acronym || '',
        _source: 'lonewolf',
        _libraryId: libId,
      }))
        // Display A→Z by name.
        .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base', numeric: true }));

      const library = { id: libId, name: 'California Association of REALTORS® (CAR)', forms, _source: 'lonewolf' };
      setFormLibraries([library]);
      setSelectedLibrary(library);
      setAvailableForms(forms);
    } catch (error) {
      console.error('Error fetching Lone Wolf CAR forms:', error);
      setFormsError('Failed to load California forms. Please try again.');
      setFormLibraries([]);
      setAvailableForms([]);
    } finally {
      setIsLoadingForms(false);
    }
  }, [getFirebaseToken]);

  const fetchAvailableForms = useCallback(async () => {
    // Colorado transactions use FormService (our GCS templates), not DocuSign form libraries.
    if ((transaction?.propertyState || '').toUpperCase() === 'CO') {
      return fetchColoradoFormsCatalog();
    }

    // A Lone-Wolf-pinned transaction (decided once at creation, see Transaction.
    // getEsignatureProvider javadoc) always sources forms from Lone Wolf's CAR library —
    // never silently falls back to DocuSign on a status-check failure (previously did; see
    // 2026-09-04 fix). Missing/null esignatureProvider (pre-existing transactions) is treated
    // as DocuSign, unchanged from before.
    if (transaction?.esignatureProvider === 'LONEWOLF') {
      const currentUserEmail = (() => {
        try { return (JSON.parse(localStorage.getItem('user') || '{}')).email || ''; } catch { return ''; }
      })();
      if (currentUserEmail) {
        try {
          const firebaseToken = await getFirebaseToken();
          const emailToCheck = await resolveLoneWolfCheckEmail(firebaseToken, currentUserEmail);
          const statusResp = await apiClient.get(API_ENDPOINTS.lonewolfUserStatus(emailToCheck), {
            headers: { 'Authorization': `Bearer ${firebaseToken}` }
          });
          if (statusResp.data?.active === true) {
            return fetchLoneWolfCarForms();
          }
          setIsLoadingForms(false);
          setFormsError("Connect your Lone Wolf account (Connected Services) to add forms to this transaction.");
          return;
        } catch (e) {
          console.error('[Select Forms] Lone Wolf status check failed for a Lone-Wolf-pinned transaction:', e);
          setIsLoadingForms(false);
          setFormsError('Could not verify your Lone Wolf connection. Please check Connected Services and try again.');
          return;
        }
      }
      setIsLoadingForms(false);
      setFormsError("Connect your Lone Wolf account (Connected Services) to add forms to this transaction.");
      return;
    }

    // Reaching here means: not Colorado (returned above) and not Lone-Wolf-pinned — i.e. a
    // pre-existing transaction from before esignatureProvider existed. Per design, DocuSign
    // is never called for non-Colorado states. Kept as a gated block rather than deleted —
    // the fetch logic below still exists, just permanently off for this (non-CO) path.
    const skipDocuSignForNonColorado = true;
    if (skipDocuSignForNonColorado) {
      setIsLoadingForms(false);
      setFormsError('This transaction needs to be connected to Lone Wolf to add forms.');
      return;
    }

    setIsLoadingForms(true);
    setFormsError(null);

    try {
      const firebaseToken = await getFirebaseToken();

      // Get property state from transaction (e.g., "CA", "NY")
      const state = transaction?.propertyState || '';
      const params = state ? { state } : {};

      console.log('Fetching form libraries with state:', state);

      const response = await apiClient.get(
        API_ENDPOINTS.docusignFormLibraries,
        {
          headers: {
            'Authorization': `Bearer ${firebaseToken}`,
            'Content-Type': 'application/json'
          },
          params
        }
      );

      console.log('Form libraries API response:', response.data);

      // Handle API response - expecting array of libraries with forms/items inside
      const responseData = response.data;
      let libraries = [];

      // Extract libraries array from response - handle various response structures
      if (Array.isArray(responseData)) {
        libraries = responseData;
      } else if (responseData && Array.isArray(responseData.data)) {
        libraries = responseData.data;
      } else if (responseData && Array.isArray(responseData.libraries)) {
        libraries = responseData.libraries;
      } else if (responseData && Array.isArray(responseData.formLibraries)) {
        libraries = responseData.formLibraries;
      } else if (responseData && Array.isArray(responseData.content)) {
        libraries = responseData.content;
      } else if (responseData && Array.isArray(responseData.results)) {
        libraries = responseData.results;
      } else if (responseData && typeof responseData === 'object') {
        // If response is an object but not matching above, check if it has any array property
        const arrayKey = Object.keys(responseData).find(key => Array.isArray(responseData[key]));
        if (arrayKey) {
          libraries = responseData[arrayKey];
          console.log(`Found libraries in response key: ${arrayKey}`);
        }
      }

      console.log('Extracted libraries:', libraries);

      // Store all libraries
      setFormLibraries(libraries);

      // If libraries exist, auto-select the first one and fetch its forms
      if (libraries.length > 0) {
        const firstLibrary = libraries[0];
        // Trigger the library selection to fetch forms via API
        handleLibrarySelect(firstLibrary);
      } else {
        setSelectedLibrary(null);
        setAvailableForms([]);
      }
    } catch (error) {
      console.error('Error fetching form libraries:', error);
      console.error('Error details:', error.response?.data || error.message);
      const status = error.response?.status;
      const errorCode = error.response?.data?.errorCode;
      if (status === 401 || errorCode === 'ACCESS_DENIED') {
        setFormsError('Your DocuSign account is not connected or does not have access to form libraries. Please connect your DocuSign account from the Dashboard.');
      } else {
        setFormsError('Failed to load form libraries. Please try again.');
      }
      setFormLibraries([]);
      setAvailableForms([]);
    } finally {
      setIsLoadingForms(false);
    }
  }, [transaction?.propertyState, transaction?.esignatureProvider, getFirebaseToken, resolveLoneWolfCheckEmail, fetchColoradoFormsCatalog, fetchLoneWolfCarForms]);

  // Filtered + sorted libraries for sidebar search (libraries with 0 forms are always hidden)
  const filteredLibraries = useMemo(() => {
    const q = librarySearchQuery.trim().toLowerCase();
    return formLibraries
      .filter(lib => {
        const count = lib.formCount ?? lib.forms?.length ?? lib.totalCount ?? lib.count ?? 0;
        if (count === 0) return false;
        if (!q) return true;
        const name = (lib.name || lib.libraryName || lib.title || '').toLowerCase();
        return name.includes(q);
      })
      .sort((a, b) => {
        const countA = a.formCount ?? a.forms?.length ?? a.totalCount ?? a.count ?? 0;
        const countB = b.formCount ?? b.forms?.length ?? b.totalCount ?? b.count ?? 0;
        return countB - countA;
      });
  }, [formLibraries, librarySearchQuery]);

  // Fetch forms when modal opens
  useEffect(() => {
    if (showCreateDocModal) {
      // Reset states first before fetching
      setSelectedForms([]);
      setSearchQuery('');
      setLibrarySearchQuery('');
      setActiveModalTab('browse');
      setSelectedLibrary(null);
      setFormLibraries([]);
      setAvailableForms([]);
      // Then fetch the form libraries
      fetchAvailableForms();
    }
  }, [showCreateDocModal, fetchAvailableForms]);

  // Handle library selection - fetch forms from API
  const handleLibrarySelect = async (library) => {
    console.log('Selected library:', library);
    setSelectedLibrary(library);
    setSearchQuery(''); // Reset search when changing library
    setLibraryFormsError(null);

    // Colorado (FormService) / Lone Wolf CAR library both carry their forms inline —
    // no DocuSign forms API call.
    if (library?._source === 'formservice' || library?._source === 'lonewolf') {
      setAvailableForms(library.forms || []);
      return;
    }

    setAvailableForms([]); // Clear current forms

    // Get the library ID
    const libraryId = library.id || library.libraryId || library.formsLibraryId;

    if (!libraryId) {
      console.error('No library ID found');
      setLibraryFormsError('Library ID not found');
      return;
    }

    setIsLoadingLibraryForms(true);

    try {
      const firebaseToken = await getFirebaseToken();

      console.log('Fetching forms for library ID:', libraryId);

      const response = await apiClient.get(
        API_ENDPOINTS.docusignFormLibraryForms(libraryId),
        {
          headers: {
            'Authorization': `Bearer ${firebaseToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('Library forms API response:', response.data);

      // Handle API response - extract forms array
      const responseData = response.data;
      let forms = [];

      if (Array.isArray(responseData)) {
        forms = responseData;
      } else if (responseData && Array.isArray(responseData.data)) {
        forms = responseData.data;
      } else if (responseData && Array.isArray(responseData.forms)) {
        forms = responseData.forms;
      } else if (responseData && Array.isArray(responseData.items)) {
        forms = responseData.items;
      } else if (responseData && Array.isArray(responseData.documents)) {
        forms = responseData.documents;
      } else if (responseData && Array.isArray(responseData.templates)) {
        forms = responseData.templates;
      } else if (responseData && Array.isArray(responseData.content)) {
        forms = responseData.content;
      } else if (responseData && Array.isArray(responseData.results)) {
        forms = responseData.results;
      } else if (responseData && typeof responseData === 'object') {
        // Auto-detect any array property
        const arrayKey = Object.keys(responseData).find(key => Array.isArray(responseData[key]));
        if (arrayKey) {
          forms = responseData[arrayKey];
          console.log(`Found forms in response key: ${arrayKey}`);
        }
      }

      console.log('Extracted forms:', forms);
      setAvailableForms(forms);
    } catch (error) {
      console.error('Error fetching library forms:', error);
      console.error('Error details:', error.response?.data || error.message);
      setLibraryFormsError('Failed to load forms for this library');
      setAvailableForms([]);
    } finally {
      setIsLoadingLibraryForms(false);
    }
  };

  // Derive role flags from accessControl (mirrors mobile canManageDocuments / isClient / isBuyerOrSeller)
  const canManage = accessControl?.canManageTransaction() ?? false;
  const isClient = accessControl?.isClient() ?? false;
  const currentUserRole = accessControl?.userRole || '';
  // Document AI Summary (self-contained feature; hidden unless enabled server-side).
  // viewerIsPrimary only decides who sees its error state — the server decides access.
  const docAiViewerIsPrimary = useMemo(() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      const uid = u.id || u.userId || u.uid || '';
      const email = (u.email || '').toLowerCase();
      return (participants || []).some((p) => p?.isPrimary === true
        && ((uid && p.userId === uid) || (email && (p.email || '').toLowerCase() === email)));
    } catch { return false; }
  }, [participants]);
  const docAi = useDocAiSummaries(transaction?.id, { viewerIsPrimary: docAiViewerIsPrimary });
  // Use the currently active role so that a dual-role user (e.g. agent + buyer) gets
  // agent/broker behaviour only when acting as an agent/broker (mirrors mobile canManageDocuments).
  // Affects: Add Form visibility + whether Upload Document shows the signature dialog.
  const _activeDocRole = (getActiveRoleFromStorage() || '').toUpperCase();
  const isAgentOrBroker = ['AGENT', 'BUYER_AGENT', 'LISTING_AGENT', 'TRANSACTION_COORDINATOR',
    'BROKER', 'BROKER_MANAGER', 'BROKER_ADMIN'].includes(_activeDocRole);

  // "Add Form" button visibility — mirrors mobile's isEffectiveAgent (DocsTab.tsx's `isAgent`
  // prop) plus the TC parity added in PR #161 (TCs get agent-equivalent document management,
  // gated further by the CA-DRE guard in handleAddFormClick). Still excludes non-participating
  // brokers, unlike the broader isAgentOrBroker above (which also gates Upload Document's
  // signature dialog and is left untouched for that).
  const canSeeAddForm = ['AGENT', 'BUYER_AGENT', 'LISTING_AGENT', 'TRANSACTION_COORDINATOR'].includes(currentUserRole);

  // CA-agent detection for DocuSign Rooms redirect (used by "Add Form" / "Edit in DocuSign")
  const _agentLicenseState = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      return (u.licenseState || u.stateCode || u.state || '').toUpperCase();
    } catch { return ''; }
  })();
  const isCAAgent = DOCUSIGN_ROOMS_REDIRECT_FOR_CA && _agentLicenseState === DOCUSIGN_ROOMS_TARGET_STATE;

  // "View in DocuSign" is shown for every role when the transaction's property state is CA.
  // Uses the property state on the transaction, not the user's license state, so sellers and
  // buyers in CA transactions see the same button as the agent.
  const isCATransaction = (transaction?.propertyState || '').toUpperCase() === DOCUSIGN_ROOMS_TARGET_STATE;

  // Which property is currently selected on the Buying-side shortlist, read from the same
  // metadata field TransactionTimeline's "Confirm Selected Property" step writes to
  // (transaction.metadata.prospectTimeline.stages[buyer_confirm_property].interestedPropertyId).
  // No prop-drilling from the Timeline needed — this component already receives `transaction`.
  // Returns null for Listing transactions or any transaction that never used that flow — the
  // backend treats null the same way (no property-scoping applied).
  const getCurrentInterestedPropertyId = () => {
    try {
      const stages = transaction?.metadata?.prospectTimeline?.stages;
      if (!Array.isArray(stages)) return null;
      const stage = stages.find(s => s.stageId === 'buyer_confirm_property');
      return stage?.interestedPropertyId || null;
    } catch {
      return null;
    }
  };

  // Navigate helper — reused by the happy path and all fallback paths
  const _openRoomsTab = (tab, roomId) => {
    const url = `https://rooms.docusign.com/rooms/${roomId}/documents`;
    if (tab) {
      // Navigate the already-open trusted tab (popup blocker safe)
      tab.location.href = url;
    } else {
      // Popup was blocked at open time — browsers are more permissive with real URLs
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // "View in DocuSign" — opens the actual document PDF in a NEW BROWSER TAB (not the in-app modal).
  // Same document the in-app "View" streams (for buyers/sellers a signed/completed envelope returns
  // the fully-signed PDF via the shared-with download), just rendered in the browser's own PDF viewer
  // in a separate tab. Works for ALL roles — no DocuSign Rooms membership required.
  const handleViewInDocuSign = async (doc, e) => {
    if (e) e.stopPropagation();
    const documentId = doc.id || doc.documentId;
    if (!transaction?.id || !documentId) {
      alert('Transaction ID or Document ID is missing');
      return;
    }
    // Open the tab synchronously within the click gesture so popup blockers allow it,
    // then navigate it to the blob URL once the download finishes.
    const newTab = window.open('', '_blank');
    if (newTab) {
      newTab.document.write(
        '<!DOCTYPE html><html><head><title>Opening document…</title></head>' +
        '<body style="font-family:sans-serif;color:#2D1B69;display:flex;align-items:center;justify-content:center;height:100vh;margin:0">' +
        '<p>Opening document…</p></body></html>'
      );
      newTab.document.close();
    }
    setViewingDocumentId(documentId);
    try {
      const firebaseToken = await getFirebaseToken();
      const downloadUrl = doc._sharedWith
        ? API_ENDPOINTS.downloadSharedDocument(transaction.id, doc.envelopeId, doc._envelopeDocumentId || documentId)
        : API_ENDPOINTS.downloadDocument(transaction.id, documentId);
      const response = await apiClient.get(downloadUrl, {
        headers: { 'Authorization': `Bearer ${firebaseToken}` },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      if (newTab) {
        newTab.location.href = blobUrl; // navigate the already-open trusted tab
      } else {
        // Popup was blocked at click time — browsers are more permissive with a real URL
        window.open(blobUrl, '_blank', 'noopener,noreferrer');
      }
      // blobUrl is intentionally not revoked — the new tab needs it for the lifetime of the view.
    } catch (error) {
      console.error('Error opening document in new tab:', error);
      if (newTab) newTab.close();
      alert(error.response?.data?.message || 'Failed to open document. Please try again.');
    } finally {
      setViewingDocumentId(null);
    }
  };

  // Opens the DocuSign Rooms document editor directly. Shared by "Add Form" (for
  // CA agents) and "Edit in DocuSign" (for CA agents AND CA TCs) — factored out
  // so the CA-DRE guard below (which only applies to *adding* a new DRE-controlled
  // form, per mobile DocsTab.tsx's handleCreateDocument) never blocks editing an
  // existing document.
  const openDocuSignRoomsEditor = async () => {
    // ── Fast path: room already known locally (mirrors mobile's openAddFormInBrowser) ──
    // Mobile checks transaction.docusignRoomId directly before anything else.
    // This skips the sync-to-room API call entirely for users whose DocuSign
    // OAuth isn't connected but whose colleague already created the room.
    // createdRoomId covers a room this function itself just silently created below —
    // the transaction prop won't reflect it until the parent page refetches, so we
    // check our own local cache too.
    const localRoomId = transaction?.docusignRoomId || createdRoomId;
    if (localRoomId) {
      const tab = window.open('', '_blank');
      _openRoomsTab(tab, localRoomId);
      return;
    }

    // ── Slow path: room ID unknown — open spinner tab and call sync-to-room ──
    // noopener must NOT be used here — it causes window.open to return null,
    // which would prevent us from navigating the tab and trigger the fallback.
    const tab = window.open('', '_blank');
    if (tab) {
      tab.document.write(
        '<!DOCTYPE html><html><head><title>Opening DocuSign Rooms…</title>' +
        '<style>body{margin:0;display:flex;align-items:center;justify-content:center;' +
        'height:100vh;font-family:sans-serif;color:#2D1B69;background:#f9f9f9;}' +
        '.msg{text-align:center}.spinner{width:36px;height:36px;border:3px solid #e0d9f7;' +
        'border-top-color:#2D1B69;border-radius:50%;animation:spin 0.8s linear infinite;' +
        'margin:0 auto 16px}@keyframes spin{to{transform:rotate(360deg)}}</style>' +
        '</head><body><div class="msg"><div class="spinner"></div>' +
        '<p>Opening DocuSign Rooms…</p></div></body></html>'
      );
      tab.document.close();
    }

    // Property-address precheck — DocuSign Rooms rejects room creation without
    // Address 1, City, Postal code (BAD_REQUEST), and the backend's minimal-fields
    // fallback fails for the same reason, so without this check the user's only
    // feedback would be a generic "Failed to create DocuSign room" dead end.
    if (!hasCompleteAddress(transaction)) {
      if (tab) tab.close();
      showNotification('error', 'Address Required', ADDRESS_MISSING_MSG);
      return;
    }

    setIsFetchingRoomId(true);
    try {
      const firebaseToken = await getFirebaseToken();
      const syncResp = await apiClient.post(
        API_ENDPOINTS.syncDocuSignRoom(transaction.id),
        {},
        { headers: { 'Authorization': `Bearer ${firebaseToken}` } }
      );
      const roomId =
        syncResp.data?.roomId ||
        syncResp.data?.data?.roomId ||
        syncResp.data?.id ||
        transaction?.docusignRoomId;
      if (roomId) {
        // Cache locally so the room is usable immediately elsewhere in this session —
        // the transaction prop itself won't reflect it until the parent page refetches.
        setCreatedRoomId(roomId);
        _openRoomsTab(tab, roomId);
        return;
      }
    } catch (err) {
      console.error('Error fetching DocuSign room for redirect:', err);

      // sync-to-room failed (e.g. 500 when caller's DocuSign OAuth isn't connected).
      // Resolve the room ID through three fallbacks before giving up:
      //  1. roomId in the 500 error response body (backend may include it)
      //  2. docusignRoomId on the local transaction state (may have been populated
      //     between the time the page loaded and now)
      //  3. fresh GET of the transaction — covers stale local state loaded before
      //     another agent's successful sync wrote the room ID to the DB
      const errData = err.response?.data;
      let fallbackRoomId =
        errData?.roomId ||
        errData?.data?.roomId ||
        transaction?.docusignRoomId;

      if (!fallbackRoomId) {
        try {
          const token2 = await getFirebaseToken();
          const txnResp = await apiClient.get(
            API_ENDPOINTS.transactionById(transaction.id),
            { headers: { 'Authorization': `Bearer ${token2}` } }
          );
          fallbackRoomId = txnResp.data?.docusignRoomId;
        } catch { /* ignore — fall through to the generic-failure toast below */ }
      }

      if (fallbackRoomId) {
        setCreatedRoomId(fallbackRoomId);
        _openRoomsTab(tab, fallbackRoomId);
        return;
      }
    } finally {
      setIsFetchingRoomId(false);
    }

    // No room ID found anywhere (create + all fallbacks exhausted) — close the
    // spinner tab and surface a toast instead of a blocking "not ready" prompt.
    // No profile-missing/access-denied specific handler exists on web yet, so this
    // is always the generic branch for now.
    if (tab) tab.close();
    showNotification('error', 'DocuSign Room Failed', 'Failed to create DocuSign room. Please try again.');
  };

  // "Add Form" click — non-Colorado agents Active on Lone Wolf get the form picker sourced
  // from Lone Wolf's CAR library; CA agents not Active on Lone Wolf redirect to DocuSign
  // Rooms (unchanged CA-specific behavior); everyone else gets the form picker modal
  // (DocuSign form libraries, or Colorado's FormService catalog). Live in every environment.
  const proceedAddForm = async () => {
    // Which provider this transaction uses was pinned once at creation (see Transaction.
    // getEsignatureProvider javadoc) — read that instead of re-deriving a guess. A
    // Lone-Wolf-pinned transaction always attempts Lone Wolf and shows a real error on
    // failure rather than silently redirecting to DocuSign (previously did — see 2026-09-04
    // fix). Non-Colorado never calls DocuSign here at all, including the old CA Rooms
    // redirect (openDocuSignRoomsEditor is intentionally not called for non-CO — the function
    // itself is untouched/still used elsewhere, just not from this path anymore).
    const isLoneWolfTransaction = transaction?.esignatureProvider === 'LONEWOLF';

    if (isLoneWolfTransaction && isAgentOrBroker) {
      let isLoneWolfActiveUser = false;
      let loneWolfAccountExists = false;
      const currentUserEmail = (() => {
        try { return (JSON.parse(localStorage.getItem('user') || '{}')).email || ''; } catch { return ''; }
      })();
      if (currentUserEmail) {
        try {
          const firebaseToken = await getFirebaseToken();
          const emailToCheck = await resolveLoneWolfCheckEmail(firebaseToken, currentUserEmail);
          const statusResp = await apiClient.get(API_ENDPOINTS.lonewolfUserStatus(emailToCheck), {
            headers: { 'Authorization': `Bearer ${firebaseToken}` }
          });
          isLoneWolfActiveUser = statusResp.data?.active === true;
          loneWolfAccountExists = statusResp.data?.exists === true;
        } catch (e) {
          console.error('[Add Form] Lone Wolf status check failed for a Lone-Wolf-pinned transaction:', e);
        }
      }

      if (!isLoneWolfActiveUser) {
        // This transaction is pinned to Lone Wolf — never silently redirect to DocuSign.
        // Same modal treatment as the DocuSign gate (see LONEWOLF_GATE_CONFIG) — 'pending'
        // when an account exists but isn't Active yet, 'not_connected' otherwise (including
        // a failed status check, which can't distinguish the two).
        setIsFetchingRoomId(false);
        triggerLoneWolfGate(loneWolfAccountExists ? 'pending' : 'not_connected', 'addForm');
        return;
      }
      // Active on Lone Wolf — fall through to the picker modal, sourced from CAR below.
    }
    // Colorado's own path is handled inside fetchAvailableForms (FormService catalog).
    // Every other non-CO case falls through to the picker modal below — no DocuSign call
    // from this function for non-Colorado, per design.

    // Success exit: hand off from the button's own spinner (set in handleAddFormClick,
    // covers the readiness gate + Lone Wolf check) to the modal's own isLoadingForms
    // spinner, which takes over once fetchAvailableForms runs.
    setIsFetchingRoomId(false);
    setShowCreateDocModal(true);
  };

  const handleAddFormClick = async () => {
    // NOTE: The former CA-DRE guard that blocked a TC from adding forms has been removed per
    // the business decision — a TC MAY add forms; the add runs under the linked Agent's Lone
    // Wolf account server-side (attach uses primaryAgentId; library lookup passes transactionId
    // so the Agent's libraries are read). The licensed Agent's account is still what's used, so
    // the DRE "licensed agent adds it" intent holds — the TC just initiates it.

    // Spinner covers the whole click-to-outcome window from here — the readiness gate,
    // the Lone Wolf status check inside proceedAddForm, and (if it goes that way)
    // openDocuSignRoomsEditor's own room-fetch, which keeps reusing this same flag rather
    // than re-triggering it, so there's no gap or flicker between them. Every exit path
    // below turns it back off before showing its own next UI (modal, gate, or alert).
    setIsFetchingRoomId(true);

    // Colorado's FormService catalog never touches DocuSign — bypass the readiness
    // gate entirely rather than checking a connection this path doesn't use.
    if ((transaction?.propertyState || '').toUpperCase() === 'CO') {
      setIsFetchingRoomId(false);
      setShowCreateDocModal(true);
      return;
    }

    // DocuSign is COLORADO-ONLY, and CO's Add Form uses the FormService catalog (bypassed
    // above) — so NO non-CO state should ever hit a DocuSign readiness check here. Every
    // non-Colorado transaction is Lone Wolf; proceedAddForm() runs its own complete Lone Wolf
    // readiness check ("Lone Wolf Not Connected" rather than silently doing nothing).
    // Previously this fell through to a DocuSign readiness gate for any non-CO transaction
    // whose esignatureProvider wasn't explicitly 'LONEWOLF' (e.g. legacy/missing), wrongly
    // firing a DocuSign status call for non-Colorado states.
    await proceedAddForm();
  };

  // Auto-resume after reconnecting DocuSign — consumes the sessionStorage stash left
  // by triggerDocGate + DocusignCallbackPage's redirect-back. Runs once per transaction
  // mount rather than on every render; deliberately does NOT re-show the gate modal if
  // the user still isn't ready (e.g. they browsed away without actually connecting) —
  // it just fires the next time they tap the button, no surprise modal-on-load.
  // Dev-only bypass means this effect is a no-op on dev (nothing ever stashes a resume
  // there), so no extra guard needed here.
  useEffect(() => {
    const stash = readDocuSignResume();
    if (!stash || !transaction?.id || stash.transactionId !== transaction.id) return;
    clearDocuSignResume(); // consume immediately so this never re-fires

    (async () => {
      let firebaseToken;
      try {
        firebaseToken = await getFirebaseToken();
      } catch {
        return;
      }
      const { variant } = await checkDocuSignReadiness(firebaseToken, _agentLicenseState);
      if (variant) return; // still not ready — let the next manual tap handle it

      if (stash.action === 'addForm') {
        await proceedAddForm();
      } else if (stash.action === 'sendForSignature') {
        // Can't resume the exact document's signature modal across the full-page DocuSign
        // redirect (signatureDocument state doesn't survive it) — just confirm the account
        // is ready now so the user knows to retry Send for Signature on the form.
        showNotification('success', 'DocuSign Connected', 'You can now retry Send for Signature on your document.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transaction?.id]);

  const closeDocGate = () => {
    setDocGateVariant(null);
    setDocGateAction(null);
    setDocGateResendingInvite(false);
  };

  // Lone Wolf equivalent of triggerDocGate/closeDocGate/handleDocGatePrimary above — same
  // modal treatment, simpler primary action (Lone Wolf's "connect" is just an email entry
  // on Connected Services, not an OAuth redirect, so there's no resume-after-reconnect
  // stash needed here).
  const triggerLoneWolfGate = (variant, action) => {
    setLoneWolfGateVariant(variant);
    setLoneWolfGateAction(action || null);
  };
  const closeLoneWolfGate = () => {
    setLoneWolfGateVariant(null);
    setLoneWolfGateAction(null);
  };
  const handleLoneWolfGatePrimary = () => {
    closeLoneWolfGate();
    navigate('/connected-services');
  };

  // Opens the readiness gate modal AND stashes resume intent, so every call site that
  // blocks an action on a disconnected/not-ready DocuSign account does both consistently
  // in one place. `action` is 'addForm' | 'sendForSignature' — consumed by the resume
  // effect below once the user reconnects and lands back on this transaction.
  const triggerDocGate = (variant, roomsUserId, action) => {
    setDocGateRoomsUserId(roomsUserId || null);
    setDocGateVariant(variant);
    setDocGateAction(action || null);
    if (transaction?.id) stashDocuSignResume(transaction.id, action);
  };

  // Primary action inside the not-connected/pending/no-provider readiness gate —
  // mirrors mobile useDocuSignReadiness's handlePrimary
  const handleDocGatePrimary = async () => {
    if (docGateVariant === 'not_connected' || docGateVariant === 'no_provider') {
      closeDocGate();
      navigate('/connected-services');
      return;
    }
    if (docGateVariant === 'pending') {
      if (!docGateRoomsUserId || docGateResendingInvite) return;
      setDocGateResendingInvite(true);
      try {
        const firebaseToken = await getFirebaseToken();
        await apiClient.post(API_ENDPOINTS.docusignRoomsReinvite(docGateRoomsUserId), {}, {
          headers: { 'Authorization': `Bearer ${firebaseToken}` }
        });
        closeDocGate();
        alert('The invitation email has been resent.');
      } catch (err) {
        console.error('Failed to resend DocuSign invite:', err);
        alert('Failed to resend invitation.');
      } finally {
        setDocGateResendingInvite(false);
      }
    }
  };

  // Document status helpers — mirrors mobile getDocStatusLabel / getDocStatusColor
  const getDocStatusLabel = (doc) => {
    // Live DocuSign recipient status wins over the stored FormService status for Colorado
    // forms — nothing ever calls markSigned() to advance the stored status, so it goes stale
    // the moment DocuSign reports the envelope sent/completed. See fetchDocuments' CO branch.
    // Client (Buyer/Seller): their own recipient status. Agent/TC: they're the sender, not a
    // recipient, so their badge summarizes the whole envelope instead.
    if (isClient) {
      if (doc._envelopeRecipientStatus === 'completed') return 'Signed';
      if (doc._envelopeRecipientStatus === 'sent') return 'Received';
    } else if (doc.envelopeId) {
      return doc._envelopeAllCompleted ? 'Signed' : 'Sent';
    }
    if (doc.isSigned) return 'Signed';
    const status = doc.status?.toLowerCase();
    switch (status) {
      case 'signed':
      case 'completed': return 'Signed';
      case 'draft_pending_tagging': {
        // Only custom PDFs need tagging — fillable forms have pre-placed tabs
        const isCustomPdf = !doc.isDynamic && !doc.formId;
        return isCustomPdf ? 'Pending Tagging' : 'Draft';
      }
      case 'sent': return isClient ? 'Received' : 'Sent';
      case 'waitingforothers': return isClient ? 'Received' : 'Pending';
      case 'delivered': return 'Delivered';
      case 'pending': return 'Pending';
      case 'declined': return 'Declined';
      case 'not_required': return isClient ? 'Info' : 'No Signature Required';
      default: return 'Draft';
    }
  };

  const getDocStatusStyle = (doc) => {
    if (isClient) {
      if (doc._envelopeRecipientStatus === 'completed') return { background: '#e8f5e9', color: '#2e7d32' };
      if (doc._envelopeRecipientStatus === 'sent') return { background: '#ede7f6', color: '#5B47D6' };
    } else if (doc.envelopeId) {
      return doc._envelopeAllCompleted
        ? { background: '#e8f5e9', color: '#2e7d32' }
        : { background: '#ede7f6', color: '#5B47D6' };
    }
    if (doc.isSigned) return { background: '#e8f5e9', color: '#2e7d32' };
    const status = doc.status?.toLowerCase();
    switch (status) {
      case 'signed':
      case 'completed': return { background: '#e8f5e9', color: '#2e7d32' };
      case 'draft_pending_tagging': {
        const isCustomPdf = !doc.isDynamic && !doc.formId;
        return isCustomPdf
          ? { background: '#fff8e1', color: '#b26a00' }   // amber — needs tagging
          : { background: '#f5f5f5', color: '#9e9e9e' };  // gray — just draft
      }
      case 'sent':
      case 'waitingforothers':
      case 'delivered': return { background: '#ede7f6', color: '#5B47D6' };
      case 'pending': return { background: '#fff3e0', color: '#f57c00' };
      case 'declined': return { background: '#fdecea', color: '#c62828' };
      case 'not_required': return { background: '#e3f2fd', color: '#1565c0' };
      default: return { background: '#f5f5f5', color: '#9e9e9e' };
    }
  };

  // Role-based document visibility — mirrors mobile visibleDocuments logic
  // Clients (BUYER/SELLER) only see docs that have been sent/signed (not drafts)
  const getVisibleDocuments = () => {
    let docuSignDocs = documents;
    if (isClient) {
      docuSignDocs = documents.filter(doc => {
        const status = doc.status?.toLowerCase();
        return doc.envelopeId ||
               doc.isSigned ||
               status === 'pending' ||
               status === 'sent' ||
               status === 'delivered' ||
               status === 'signed' ||
               status === 'completed';
      });
    }

    // Merge GCS documents into the list
    return [...docuSignDocs, ...gcsDocuments];
  };

  // Returns true for docs awaiting signature (sent or delivered but not yet signed)
  const isPendingSignature = (d) => {
    if (d.isSigned) return false;
    const s = d.status?.toLowerCase();
    return s === 'pending' || s === 'sent' || s === 'delivered';
  };

  // Filter documents based on selected filter
  const getFilteredDocuments = () => {
    const visible = getVisibleDocuments();
    if (docFilter === 'all') return visible;
    if (docFilter === 'signed') {
      return visible.filter(d => d.isSigned || d.status === 'signed' || d.status === 'completed');
    }
    if (docFilter === 'pending') {
      return visible.filter(isPendingSignature);
    }
    return visible.filter(doc => doc.status?.toLowerCase() === docFilter);
  };

  // Get document counts
  const getDocCounts = () => {
    const visible = getVisibleDocuments();
    return {
      all: visible.length,
      signed: visible.filter(d => d.isSigned || d.status === 'signed' || d.status === 'completed').length,
      pending: visible.filter(isPendingSignature).length,
      draft: visible.filter(d => !d.isSigned && (!d.status || d.status?.toLowerCase() === 'draft' || (d.status?.toLowerCase() === 'draft_pending_tagging' && !d.isDynamic && !d.formId))).length
    };
  };

  // Filter forms based on search query
  const getFilteredForms = () => {
    if (!searchQuery.trim()) return availableForms;
    const query = searchQuery.toLowerCase();
    return availableForms.filter(form => {
      const formName = form.name || form.formName || form.title || form.templateName || '';
      const formDescription = form.description || '';
      // Lone Wolf forms also match on acronym (e.g. "TEC" → "TEC-2").
      const formAcronym = form.acronym || '';
      return formName.toLowerCase().includes(query)
        || formDescription.toLowerCase().includes(query)
        || formAcronym.toLowerCase().includes(query);
    });
  };

  // Get unique form identifier for display/selection
  const getFormId = (form) => {
    return form.libraryFormId || form.id || form.formId || form.documentId || form.templateId || form.name || form.title;
  };

  // Get form ID for API call (specifically libraryFormId for addDocumentForm API)
  const getFormIdForApi = (form) => {
    return form.libraryFormId || form.formId || form.id;
  };

  // Toggle form selection (radio button behavior - single selection)
  const toggleFormSelection = (form) => {
    const formId = getFormId(form);
    const isSelected = selectedForms.some(f => getFormId(f) === formId);

    if (isSelected) {
      // Deselect if already selected
      setSelectedForms([]);
    } else {
      // Select only this form (replace any previous selection)
      setSelectedForms([form]);
    }
  };

  // Check if form is selected
  const isFormSelected = (form) => {
    const formId = getFormId(form);
    return selectedForms.some(f => getFormId(f) === formId);
  };

  // Upload document modal state — files upload immediately, no signature question up front.
  const [isUploading, setIsUploading] = useState(false);

  // Duplicate-document confirmation — shown in place of the generic "Upload Failed"
  // notification when the backend rejects an upload because a document with that
  // name already exists. `target` remembers which upload path ('docusign' | 'gcs')
  // to retry on "Yes" so the retry lands in the same place the original attempt did.
  const [duplicateUploadConfirm, setDuplicateUploadConfirm] = useState({ show: false, file: null, target: null });

  const uploadFileInputRef = React.useRef(null);

  // Multi-file upload — every role (see `multiple={isClient || isAgentOrBroker}` on the file
  // input below). No signature question here either — every file uploads straight to
  // Transaction Documents, same as the single-file path via routeUploadToFolder.
  // `folder` is optional per-entry: absent for a normal multi-file pick (falls back to
  // selectedFolder at upload time, same as before); present for a folder-upload entry,
  // where it carries that file's own nested path computed from where it sat on disk —
  // see computeFolderForRelativePath. `relativePath` is display-only (shown as a caption
  // under the filename in the preview card) so a folder-upload batch is legible.
  const [multiUploadFiles, setMultiUploadFiles] = useState([]); // [{key, file, folder?, relativePath?}]
  const [showMultiUploadCard, setShowMultiUploadCard] = useState(false);
  const [isBatchUploading, setIsBatchUploading] = useState(false);
  // "Uploading X of N…" counter shown on the batch Upload button, mirrors mobile's
  // uploadDocumentBatch progress counter (LoadingOverlay message).
  const [batchUploadProgress, setBatchUploadProgress] = useState(null); // { current, total } | null
  // Per-file "Document Already Exists" prompt for the batch — mirrors mobile's
  // duplicatePrompt: the in-flight batch loop awaits this promise before continuing,
  // so a duplicate on file 2 doesn't block/skip the rest of the batch either way.
  const [batchDuplicatePrompt, setBatchDuplicatePrompt] = useState(null); // { fileName, resolve } | null
  // Past this many files, the per-file reviewable list (multi-upload-file-list, a plain
  // .map() with no virtualization) gets slow to render/scroll and the "review before
  // upload" step stops being useful anyway — nobody reads a 200-row list. Above the
  // threshold we skip the review modal entirely and go straight to uploading, tracked by
  // a compact Drive-style progress widget (compactUpload below) instead.
  const MULTI_UPLOAD_COMPACT_THRESHOLD = 20;
  // Compact large-batch upload widget — { total, current, currentFileName, phase:
  // 'starting'|'uploading'|'done', succeeded, failed, skipped, collapsed } | null.
  const [compactUpload, setCompactUpload] = useState(null);
  // Set to true to stop a running batch (small-modal or compact-widget) after its
  // in-flight files finish — checked between files, not mid-request.
  const cancelUploadRef = React.useRef(false);
  // Holds the `resolve` of whichever duplicate-confirm prompt is currently on screen, so
  // Cancel can unblock it immediately instead of waiting for the user to answer.
  const pendingDuplicateResolveRef = React.useRef(null);
  // Concurrent uploads that each hit a duplicate-name conflict would otherwise clobber
  // each other's setBatchDuplicatePrompt state — this chain serializes the prompt so only
  // one shows at a time while the other conflicting uploads wait their turn.
  const duplicatePromptMutexRef = React.useRef(Promise.resolve());
  // Upfront bulk-duplicate dialog — shown INSTEAD of the "N files selected" review card
  // whenever any selected file's name already exists in its target folder. Two checkboxes
  // (mutually exclusive in practice, even though drawn as independent checkboxes) decide
  // how every duplicate in the batch is handled; leaving both unchecked falls back to
  // today's existing per-file "Document Already Exists" prompt (batchDuplicatePrompt
  // above), unchanged. { entries, fallbackFolder, duplicateCount, onProceed } | null.
  const [duplicateUploadDialog, setDuplicateUploadDialog] = useState(null);
  const [skipDuplicatesChecked, setSkipDuplicatesChecked] = useState(false);
  const [overwriteAllChecked, setOverwriteAllChecked] = useState(false);
  // True only while "Overwrite all files" is deleting the conflicting existing documents —
  // brief, but the dialog's Upload button should reflect it rather than looking unresponsive.
  const [isResolvingOverwrite, setIsResolvingOverwrite] = useState(false);
  const multiUploadAddMoreRef = React.useRef(null);
  const uploadFolderInputRef = React.useRef(null);
  // True while recursively walking a dropped folder's contents (webkitGetAsEntry) — can
  // take a moment for a large tree, so the drop zone shows a scanning state instead of
  // looking unresponsive between drop and the preview card appearing.
  const [isScanningFolder, setIsScanningFolder] = useState(false);

  // GCS documents state
  const [gcsDocuments, setGcsDocuments] = useState([]);
  // Mirrors isLoadingDocuments (Forms tab) — without this, the Transaction
  // Documents tab briefly flashes its "No documents uploaded yet" empty state
  // before gcsDocuments/folderGroups populate, instead of showing a spinner.
  const [isLoadingGcsDocuments, setIsLoadingGcsDocuments] = useState(true);

  // Folder feature state (mirrors mobile DocsTab.tsx)
  const [backendFolders, setBackendFolders] = useState([]);       // string[] from GET /folders (includes empty folders)
  const [selectedFolder, setSelectedFolder] = useState(null);     // drilled-in folder key, or null = folder-list/flat view

  // Move-to-folder picker sheet
  const [folderPickerMode, setFolderPickerMode] = useState(null);   // null | 'move'
  const [folderPickerSelection, setFolderPickerSelection] = useState('documents');
  const [folderPickerNewName, setFolderPickerNewName] = useState('');
  const [folderPickerMoveDoc, setFolderPickerMoveDoc] = useState(null);
  const [isMovingDoc, setIsMovingDoc] = useState(false);

  // Create-folder modal (agent-only "+ New folder" button)
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [createFolderName, setCreateFolderName] = useState('');
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  // Folder card kebab (Rename/Delete) — reuses openDocMenuId/docMenuPos/openDocMenuAt
  // (defined further below) since folder keys never collide with document keys.
  const [renameFolderTarget, setRenameFolderTarget] = useState(null); // folder key being renamed, or null
  const [renameFolderNewName, setRenameFolderNewName] = useState('');
  const [isRenamingFolder, setIsRenamingFolder] = useState(false);
  const [deleteFolderTarget, setDeleteFolderTarget] = useState(null); // folder group being deleted, or null
  const [isDeletingFolder, setIsDeletingFolder] = useState(false);
  // Folder share modal — mirrors the disclosure share flow but scoped to any
  // Transaction Documents folder. shareModal holds { group, share } where share
  // is the active DisclosureShareResponse (or null while POST is in flight).
  const [shareModal, setShareModal] = useState(null);
  const [isCreatingShare, setIsCreatingShare] = useState(false);
  const [isRevokingShare, setIsRevokingShare] = useState(false);
  const [shareLinkCopied, setShareLinkCopied] = useState(false);
  // Folder ZIP download — bottom-right "Preparing download" panel, Google Drive style.
  // null while idle; { name, status: 'zipping' | 'done' | 'error' } while a download is in flight/just finished.
  const [folderDownload, setFolderDownload] = useState(null);

  // Auto-dismiss the panel a couple seconds after the download settles (Drive's own panel
  // behaves the same way) — the user can still close it early via the X regardless.
  useEffect(() => {
    if (!folderDownload || folderDownload.status === 'zipping') return undefined;
    const timer = setTimeout(() => setFolderDownload(null), 3000);
    return () => clearTimeout(timer);
  }, [folderDownload]);

  // Drag-and-drop onto the existing Upload Document button (no idle-state visual change)
  const [isDocsDragging, setIsDocsDragging] = useState(false);

  // Report combined count (Forms + Transaction Documents) to parent.
  useEffect(() => {
    if (onDocumentsChange) {
      onDocumentsChange(documents.length + gcsDocuments.length);
    }
  }, [documents, gcsDocuments, onDocumentsChange]);

  // GCS Share modal state — mirrors mobile's share modal (recipients picker over
  // transaction participants, not a free-text email field)
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareModalDoc, setShareModalDoc] = useState(null);
  const [shareRecipientIds, setShareRecipientIds] = useState(new Set());
  const [showRecipientDropdown, setShowRecipientDropdown] = useState(false);
  const [recipientSearch, setRecipientSearch] = useState('');
  const [shareMessage, setShareMessage] = useState('');
  const [isSharingFile, setIsSharingFile] = useState(false);
  // Track WHICH GCS doc is downloading (not a global boolean) so the "Downloading…"
  // label only shows on that file, not every row. Mirrors viewingDocumentId.
  const [downloadingDocId, setDownloadingDocId] = useState(null);
  const recipientPickerRef = React.useRef(null);

  // Close the recipient dropdown on outside click (mirrors the Mark-as dropdown pattern)
  useEffect(() => {
    if (!showRecipientDropdown) return undefined;
    const handleClickOutside = (e) => {
      if (recipientPickerRef.current && !recipientPickerRef.current.contains(e.target)) {
        setShowRecipientDropdown(false);
        setRecipientSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showRecipientDropdown]);

  // "Create or Upload" dropdown — consolidates New folder / Upload Document /
  // Upload Folder into one trigger button; each menu item keeps the exact
  // same onClick/disabled behavior the standalone buttons had.
  const [showCreateUploadMenu, setShowCreateUploadMenu] = useState(false);
  const createUploadMenuRef = React.useRef(null);

  useEffect(() => {
    if (!showCreateUploadMenu) return undefined;
    const handleClickOutside = (e) => {
      if (createUploadMenuRef.current && !createUploadMenuRef.current.contains(e.target)) {
        setShowCreateUploadMenu(false);
      }
    };
    const handleEsc = (e) => { if (e.key === 'Escape') setShowCreateUploadMenu(false); };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [showCreateUploadMenu]);

  // Custom delete-confirmation popup state (replaces window.confirm)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmDoc, setDeleteConfirmDoc] = useState(null);
  const [isDeletingGcsFile, setIsDeletingGcsFile] = useState(false);

  // Per-document ⋮ kebab menu — tracks which single card's menu is open (id of
  // the doc, or null) plus one shared ref, same one-ref/one-open-id pattern as
  // the recipient picker above.
  const [openDocMenuId, setOpenDocMenuId] = useState(null);
  const docMenuRef = React.useRef(null);
  // Dropdown renders position:fixed (coordinates computed from the kebab
  // button's rect on open) so it never gets clipped by the dynamic doc list's
  // fixed-height scroll container — see renderDynamicDocs below.
  const [docMenuPos, setDocMenuPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!openDocMenuId) return undefined;
    const handleClickOutside = (e) => {
      if (docMenuRef.current && !docMenuRef.current.contains(e.target)) {
        setOpenDocMenuId(null);
      }
    };
    const handleEsc = (e) => { if (e.key === 'Escape') setOpenDocMenuId(null); };
    // Fixed-position menu would visually detach from its anchor on scroll
    // (e.g. scrolling the icon-density strip), so close it instead.
    const handleScroll = () => setOpenDocMenuId(null);
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    document.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [openDocMenuId]);

  // Opens the kebab dropdown anchored under/left-of the clicked button,
  // clamped to stay on-screen — mirrors the wireframe's fixed-position menu.
  const openDocMenuAt = (e, key) => {
    e.stopPropagation();
    if (openDocMenuId === key) { setOpenDocMenuId(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuWidth = 190;
    let left = rect.right - menuWidth;
    if (left < 8) left = rect.left;
    setDocMenuPos({ top: rect.bottom + 4, left });
    setOpenDocMenuId(key);
  };

  // Step 1: Click "Upload Document" → open file picker directly (mirrors mobile)
  const openUploadPicker = () => {
    if (uploadFileInputRef.current) {
      uploadFileInputRef.current.value = '';
      uploadFileInputRef.current.click();
    }
  };

  // Step 2: File(s) picked → single file keeps the existing per-role behavior unchanged;
  // multiple files (Buyer/Seller only — the input only sets `multiple` for isClient) opens
  // the multi-upload card instead.
  // Uploads land wherever the user is — no signature question up front. Every file goes
  // straight to storage regardless of type; "Send for Signature" becomes available afterward
  // (PDF-only) from the document's own menu instead of being decided at upload time.
  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (files.length > 1) {
      openMultiUploadCard(files);
      return;
    }
    const file = files[0];
    showUploadConfirmDialog(
      [{ key: `${file.name}-${file.size}-${Date.now()}`, file }],
      selectedFolder,
      () => routeUploadToFolder(file),
    );
  };

  // Root and the literal "documents" bucket are the same folder for duplicate-matching
  // purposes — mirrors isRootFolderValue's rule, applied to both sides of the comparison.
  const folderBucketKey = (rawFolder) => {
    const trimmed = (rawFolder || '').trim().toLowerCase();
    return (!trimmed || trimmed === 'documents') ? '' : trimmed;
  };

  // Real duplicate check — exact filename match within the same folder bucket, against
  // whatever's already loaded in gcsDocuments. Mirrors the backend's own isDuplicateFileName
  // exactly (same field, same folder-bucket rule, same case-sensitive trim comparison), so
  // this never disagrees with what the upload endpoint itself would reject.
  const findExistingDuplicate = (entry, fallbackFolder) => {
    const folder = entry.folder !== undefined ? entry.folder : fallbackFolder;
    const key = folderBucketKey(folder);
    const name = entry.file.name.trim();
    return gcsDocuments.find(doc => folderBucketKey(doc.folder) === key && (doc.fileName || '').trim() === name);
  };

  // Gate in front of the file-list review card / large-batch compact upload — if any
  // selected file's name already exists in its target folder, shows the upfront
  // Skip/Overwrite dialog INSTEAD of onProceed. No duplicates -> calls onProceed(entries)
  // immediately, identical to today's behavior.
  const maybeShowDuplicateUploadDialog = (entries, fallbackFolder, onProceed) => {
    const duplicateCount = entries.reduce(
      (count, entry) => (findExistingDuplicate(entry, fallbackFolder) ? count + 1 : count), 0);
    if (duplicateCount === 0) {
      onProceed(entries);
      return;
    }
    setSkipDuplicatesChecked(false);
    setOverwriteAllChecked(false);
    setDuplicateUploadDialog({ entries, fallbackFolder, duplicateCount, onProceed });
  };

  const cancelDuplicateUploadDialog = () => {
    if (isResolvingOverwrite) return;
    setDuplicateUploadDialog(null);
    setSkipDuplicatesChecked(false);
    setOverwriteAllChecked(false);
  };

  // Independent checkboxes in the markup, but mutually exclusive in effect — "skip this
  // file" and "replace this file" are contradictory instructions for the same duplicate,
  // so checking one always clears the other rather than leaving an undefined combined state.
  const toggleSkipDuplicates = () => {
    setSkipDuplicatesChecked(prev => !prev);
    setOverwriteAllChecked(false);
  };
  const toggleOverwriteAll = () => {
    setOverwriteAllChecked(prev => !prev);
    setSkipDuplicatesChecked(false);
  };

  // "Upload N files" on the dialog itself — resolves every duplicate per the checked box,
  // then hands the (possibly filtered) entries to whichever upload path was waiting.
  const confirmDuplicateUploadDialog = async () => {
    if (!duplicateUploadDialog) return;
    const { entries, fallbackFolder, onProceed } = duplicateUploadDialog;
    setDuplicateUploadDialog(null);

    if (skipDuplicatesChecked) {
      const filtered = entries.filter(entry => !findExistingDuplicate(entry, fallbackFolder));
      onProceed(filtered);
      return;
    }

    if (overwriteAllChecked) {
      // Deleting the original source PDF here only removes our own Firestore row + GCS
      // blob — it never touches DocuSign. Once "Send for Signature" has created an
      // envelope, that envelope (and anything recipients already received) is a fully
      // independent artifact on DocuSign's side, so overwriting the source document here
      // has no effect on an in-progress or completed signature. No envelope check needed.
      setIsResolvingOverwrite(true);
      const failedKeys = new Set();
      try {
        const firebaseToken = await getFirebaseToken();
        for (const entry of entries) {
          const existing = findExistingDuplicate(entry, fallbackFolder);
          if (!existing) continue;
          try {
            await apiClient.delete(
              API_ENDPOINTS.deleteTransactionFile(transaction.id, existing.documentId),
              { headers: { 'Authorization': `Bearer ${firebaseToken}` } }
            );
          } catch (err) {
            console.error('[Bulk Overwrite] Failed to delete existing document before overwrite:', err);
            failedKeys.add(entry.key); // couldn't clear the name — don't attempt to upload over it
          }
        }
      } finally {
        setIsResolvingOverwrite(false);
      }
      if (failedKeys.size > 0) {
        showNotification('info', 'Some Files Skipped',
          `${failedKeys.size} file${failedKeys.size === 1 ? '' : 's'} could not be overwritten — please try again.`);
      }
      onProceed(entries.filter(entry => !failedKeys.has(entry.key)));
      return;
    }

    // Neither checked — proceed exactly as before; real duplicates still surface today's
    // existing per-file "Document Already Exists" prompt during the upload loop.
    onProceed(entries);
  };

  // "Transaction Documents" for root, otherwise the full breadcrumb (e.g. "Folder1 › Sub1")
  // — same label vocabulary breadcrumbSegments/fullPathDisplayName already use elsewhere.
  const uploadDestinationLabel = (folder) => (isRootFolderValue(folder) ? 'Transaction Documents' : fullPathDisplayName(folder));

  // Upfront "Upload N files?" confirmation — shown BEFORE anything else (before even the
  // duplicate check), for every multi-file batch regardless of size. This is in ADDITION to
  // the existing small-batch file-list review card and the duplicate-resolution dialog
  // below it in the flow — neither of those changes; this is just one more gate at the very
  // front, and it's also the only confirmation a large (compact-widget) batch ever gets,
  // since that path otherwise starts uploading immediately with no confirmation at all.
  const [uploadConfirmDialog, setUploadConfirmDialog] = useState(null); // { entries, folder, onProceed } | null

  const showUploadConfirmDialog = (entries, folder, onProceed) => {
    setUploadConfirmDialog({ entries, folder, onProceed });
  };

  const cancelUploadConfirmDialog = () => setUploadConfirmDialog(null);

  const confirmUploadConfirmDialog = () => {
    if (!uploadConfirmDialog) return;
    const { entries, folder, onProceed } = uploadConfirmDialog;
    setUploadConfirmDialog(null);
    onProceed(entries, folder);
  };

  const openMultiUploadCard = (files) => {
    const entries = files.map((file, i) => ({
      key: `${file.name}-${file.size}-${Date.now()}-${i}`,
      file,
    }));
    showUploadConfirmDialog(entries, selectedFolder, (confirmedEntries, folder) => {
      maybeShowDuplicateUploadDialog(confirmedEntries, folder, (finalEntries) => {
        if (finalEntries.length === 0) return;
        // The upfront "Upload N files?" dialog already confirmed this batch, so start
        // uploading straight away with the compact progress widget — regardless of count.
        // Previously a small batch (2..20) fell through to a second "{N} files selected"
        // review card with its own "Upload N files" button, forcing a redundant second
        // click; that double-dialog was the reported bug.
        startCompactUpload(finalEntries, folder);
      });
    });
  };

  const handleAddMoreMultiUploadFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const newEntries = files.map((file, i) => ({ key: `${file.name}-${file.size}-${Date.now()}-${i}`, file }));
    const combined = [...multiUploadFiles, ...newEntries];
    e.target.value = '';
    if (combined.length > MULTI_UPLOAD_COMPACT_THRESHOLD) {
      // Grew past the reviewable size while the modal was already open — hand the whole
      // combined batch to the compact widget instead of continuing to render a giant list.
      setShowMultiUploadCard(false);
      setMultiUploadFiles([]);
      startCompactUpload(combined.map(f => ({ file: f.file, folder: f.folder })), selectedFolder);
      return;
    }
    setMultiUploadFiles(combined);
  };

  // ── Folder upload (drag-and-drop a folder, or the "Upload Folder" picker) ──────────────
  // Each dropped/picked folder becomes a subfolder of wherever the user is currently
  // browsing (currentPath), keeping its own name and internal structure — same placement
  // rule "New Folder" and "Upload Document" already follow. No backend change needed: a
  // file can be uploaded straight into a nested folder path that's never been explicitly
  // created (see DocumentService.uploadDocument — folder is validated, not required to
  // pre-exist), so a whole tree uploads as one batch of ordinary per-file upload calls.

  // A relativePath like "UploadFolder1/subfolderA/report.pdf" -> folder tag
  // "<currentPath>/uploadfolder1/subfoldera", each segment run through the same
  // sanitizer manual folder names already use, so a folder full of exotic OS-legal
  // characters (spaces, uppercase, unicode) still produces a valid backend folder path.
  const computeFolderForRelativePath = (relativePath) => {
    const segments = relativePath.split('/').slice(0, -1); // drop the filename
    const sanitized = segments.map(sanitizeFolderName).filter(Boolean);
    const path = sanitized.join('/');
    if (!path) return currentPath || undefined;
    return currentPath ? `${currentPath}/${path}` : path;
  };

  // Drains a FileSystemDirectoryReader — readEntries() only returns a batch at a time
  // (capped well under a large folder's real size in some browsers) and returns an empty
  // array once truly exhausted, so this must keep calling it in a loop, not just once.
  const readAllDirectoryEntries = (reader) => new Promise((resolve, reject) => {
    let all = [];
    const readBatch = () => {
      reader.readEntries((entries) => {
        if (entries.length === 0) { resolve(all); return; }
        all = all.concat(entries);
        readBatch();
      }, reject);
    };
    readBatch();
  });

  // Recursively walks one FileSystemEntry (file or directory), pushing every real file
  // found — at any depth — onto `out` as {file, relativePath}. relativePath is rooted at
  // the entry passed to collectEntriesFromDataTransferItems, e.g. a top-level folder named
  // "UploadFolder1" produces "UploadFolder1/sub/file.pdf", matching the same shape the
  // <input webkitdirectory> picker gives via File.webkitRelativePath.
  const walkFileSystemEntry = async (entry, out) => {
    if (entry.isFile) {
      const file = await new Promise((resolve, reject) => entry.file(resolve, reject));
      // fullPath is "/UploadFolder1/sub/file.pdf" — strip the leading slash.
      out.push({ file, relativePath: entry.fullPath.replace(/^\/+/, '') });
      return;
    }
    if (entry.isDirectory) {
      const children = await readAllDirectoryEntries(entry.createReader());
      for (const child of children) {
        await walkFileSystemEntry(child, out);
      }
    }
  };

  // Top-level entry point for a drop: one or more items, each independently a loose file
  // or a folder (a user can multi-select several folders in Explorer/Finder and drag them
  // all in one gesture — each becomes its own top-level subfolder, nothing merges).
  const collectEntriesFromDataTransferItems = async (items) => {
    const entries = Array.from(items)
      .map((item) => (typeof item.webkitGetAsEntry === 'function' ? item.webkitGetAsEntry() : null))
      .filter(Boolean);
    const out = [];
    for (const entry of entries) {
      await walkFileSystemEntry(entry, out);
    }
    return out;
  };

  // Opens the preview card for a batch of {file, relativePath} pairs, computing each
  // entry's target folder up front. Loose files dropped alongside folders (relativePath
  // with no "/") land at currentPath, same as a normal upload.
  const openFolderUploadCard = (pairs) => {
    const newEntries = pairs.map((pair, i) => ({
      key: `${pair.file.name}-${pair.file.size}-${Date.now()}-${i}`,
      file: pair.file,
      folder: computeFolderForRelativePath(pair.relativePath),
      relativePath: pair.relativePath,
    }));
    const combined = [...multiUploadFiles, ...newEntries];
    showUploadConfirmDialog(combined, selectedFolder, (confirmedEntries, folder) => {
      maybeShowDuplicateUploadDialog(confirmedEntries, folder, (finalEntries) => {
        if (finalEntries.length === 0) return;
        // Same as the plain multi-file path: the upfront "Upload N files?" dialog already
        // confirmed this batch, so upload straight away with the compact progress widget
        // instead of surfacing a redundant second "{N} files selected" review card. Per-entry
        // folder is preserved (a folder upload maps each file into its computed subfolder).
        setShowMultiUploadCard(false);
        setMultiUploadFiles([]);
        startCompactUpload(finalEntries.map(f => ({ file: f.file, folder: f.folder })), folder);
      });
    });
  };

  // "Upload Folder" picker — <input webkitdirectory> flattens the whole picked folder
  // into a FileList already, each File carrying webkitRelativePath, so no manual walking
  // is needed here (unlike the drag-and-drop path above).
  const openFolderPicker = () => {
    if (uploadFolderInputRef.current) {
      uploadFolderInputRef.current.value = '';
      uploadFolderInputRef.current.click();
    }
  };

  const handleFolderInputChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    openFolderUploadCard(files.map((file) => ({ file, relativePath: file.webkitRelativePath || file.name })));
    e.target.value = '';
  };

  const removeMultiUploadFile = (key) => {
    setMultiUploadFiles(prev => prev.filter(f => f.key !== key));
  };

  const cancelMultiUpload = () => {
    if (isBatchUploading) return;
    setShowMultiUploadCard(false);
    setMultiUploadFiles([]);
  };

  // Cap the rename-retry loop so a runaway (or non-duplicate) failure eventually surfaces
  // as a normal upload error instead of hanging forever — mirrors mobile's MAX_RENAME_ATTEMPTS.
  const MAX_RENAME_ATTEMPTS = 20;

  // Single upload attempt against the given path — no duplicate handling here, that's
  // layered on in uploadOneBatchFileWithDupPrompt below. Same endpoint/request shape as
  // the single-file flow (uploadToGCS below) — there is no separate batch endpoint on the
  // backend (mobile's DocsTab.tsx uploads one file at a time too).
  const uploadOneBatchFileAttempt = async (file, folder, firebaseToken) => {
    const formData = new FormData();
    formData.append('file', file, file.name);
    if (folder) formData.append('folder', folder); // omit entirely for root — no "documents" default
    await axios.post(API_ENDPOINTS.uploadTransactionFile(transaction.id), formData, {
      headers: { 'Authorization': `Bearer ${firebaseToken}` }
    });
  };

  // Uploads one file, prompting the user per-file on a duplicate-name conflict — mirrors
  // mobile's uploadDocumentOneWithDupPrompt: Yes retries under an auto-versioned name (same
  // "name (N).ext" rule the single-file duplicate-confirm flow already uses); No skips just
  // this file — the batch continues with the next one, not counted as a failure.
  const uploadOneBatchFileWithDupPrompt = async (file, folder, firebaseToken) => {
    try {
      await uploadOneBatchFileAttempt(file, folder, firebaseToken);
      return { status: 'done' };
    } catch (error) {
      if (!isDuplicateDocumentError(error)) {
        return { status: 'failed', message: error.response?.data?.message || error.response?.data?.error || 'Upload failed.' };
      }
      // With several files uploading concurrently, more than one can hit a duplicate at
      // roughly the same time — queue behind whichever prompt is already showing instead
      // of overwriting its state (which would strand the earlier promise unresolved).
      const myTurn = duplicatePromptMutexRef.current;
      let releaseNext;
      duplicatePromptMutexRef.current = new Promise((res) => { releaseNext = res; });
      await myTurn;

      let proceed;
      if (cancelUploadRef.current) {
        proceed = false;
      } else {
        proceed = await new Promise((resolve) => {
          pendingDuplicateResolveRef.current = resolve;
          setBatchDuplicatePrompt({ fileName: file.name, resolve });
        });
      }
      pendingDuplicateResolveRef.current = null;
      setBatchDuplicatePrompt(null);
      releaseNext();

      if (!proceed) {
        return { status: 'skipped' };
      }
      const dotIndex = file.name.lastIndexOf('.');
      const base = dotIndex > 0 ? file.name.slice(0, dotIndex) : file.name;
      const ext = dotIndex > 0 ? file.name.slice(dotIndex) : '';
      for (let n = 1; n <= MAX_RENAME_ATTEMPTS; n++) {
        const renamed = new File([file], `${base} (${n})${ext}`, { type: file.type });
        try {
          await uploadOneBatchFileAttempt(renamed, folder, firebaseToken);
          return { status: 'done' };
        } catch (retryError) {
          if (isDuplicateDocumentError(retryError) && n < MAX_RENAME_ATTEMPTS) continue;
          return { status: 'failed', message: retryError.response?.data?.message || retryError.response?.data?.error || 'Upload failed.' };
        }
      }
      return { status: 'failed', message: 'Upload failed.' };
    }
  };

  // How many files upload at once. Shared by the reviewable-list modal and the compact
  // widget below — a handful of parallel requests instead of one-at-a-time cuts a large
  // batch's wall-clock time down substantially without hammering the backend.
  const UPLOAD_CONCURRENCY = 4;

  // Core upload loop — a small worker pool pulling from a shared index, so `entries` (up
  // to hundreds/thousands of files) finishes in ceil(total / UPLOAD_CONCURRENCY) rounds
  // instead of `total` sequential round-trips. `onProgress({completed, total,
  // currentFileName})` fires as each file starts and finishes; the caller decides how to
  // render that (the small modal's counter vs. the compact widget's status line).
  // entries: [{file, folder}] — folder is per-entry so a folder-upload batch (each file
  // possibly destined for a different nested subfolder) and a plain multi-file pick (every
  // file sharing fallbackFolder, i.e. wherever the user is currently browsing) go through
  // the same engine; only the folder resolution differs.
  const runUploadEngine = async (entries, fallbackFolder, onProgress) => {
    const total = entries.length;
    let firebaseToken;
    try {
      firebaseToken = await getFirebaseToken();
    } catch (err) {
      return { succeeded: 0, failed: total, skipped: 0, authError: true };
    }

    const outcomes = new Array(total);
    let completed = 0;
    let nextIndex = 0;

    const worker = async () => {
      for (;;) {
        if (cancelUploadRef.current) return;
        const i = nextIndex++;
        if (i >= total) return;
        const entry = entries[i];
        const folder = entry.folder !== undefined ? entry.folder : fallbackFolder;
        onProgress?.({ completed, total, currentFileName: entry.file.name });
        outcomes[i] = await uploadOneBatchFileWithDupPrompt(entry.file, folder, firebaseToken);
        completed += 1;
        onProgress?.({ completed, total, currentFileName: null });
      }
    };

    await Promise.all(Array.from({ length: Math.min(UPLOAD_CONCURRENCY, total) || 1 }, worker));

    const succeeded = outcomes.filter(o => o?.status === 'done').length;
    const failed = outcomes.filter(o => o?.status === 'failed').length;
    const skipped = outcomes.filter(o => o?.status === 'skipped').length;
    return { succeeded, failed, skipped, authError: false };
  };

  // Uploads every file in the batch the same way — straight to Transaction Documents,
  // no signature question, no DocuSign involvement at upload time. Drives the reviewable
  // multi-upload-card's "Uploading X of N…" counter.
  const runMultiUpload = async (entries, fallbackFolder) => {
    if (!transaction?.id) return;
    cancelUploadRef.current = false;
    setIsBatchUploading(true);

    const total = entries.length;
    const { succeeded, failed, skipped, authError } = await runUploadEngine(entries, fallbackFolder, ({ completed }) => {
      setBatchUploadProgress({ current: completed, total });
    });

    setIsBatchUploading(false);
    setBatchUploadProgress(null);
    setShowMultiUploadCard(false);
    setMultiUploadFiles([]);

    if (authError) {
      showNotification('error', 'Upload Failed', 'Authentication error. Please try again.');
      return;
    }

    await Promise.all([fetchDocuments(), fetchGcsDocuments(), fetchTransactionFolders()]);

    if (failed === 0 && succeeded > 0) {
      showNotification('success', 'Uploaded!', `${succeeded} file${succeeded === 1 ? '' : 's'} uploaded successfully.`);
    } else if (failed === 0 && succeeded === 0 && skipped > 0) {
      // Entire batch was duplicates the user declined — no toast noise, mirrors mobile:
      // they already saw the confirmation modal and made their choice for each file.
    } else if (succeeded === 0) {
      showNotification('error', 'Upload Failed', 'Failed to upload the selected files. Please try again.');
    } else {
      showNotification('error', 'Some Uploads Failed', `${succeeded} of ${total} files uploaded. ${failed} failed — please try uploading ${failed === 1 ? 'it' : 'them'} again.`);
    }
  };

  // Confirm on the multi-upload card — every file goes straight to Transaction Documents
  // (the folder the user is currently drilled into, or root if not).
  const confirmMultiUpload = () => {
    if (isBatchUploading || multiUploadFiles.length === 0) return;
    runMultiUpload(multiUploadFiles.map(f => ({ file: f.file, folder: f.folder })), selectedFolder);
  };

  // Large-batch path (> MULTI_UPLOAD_COMPACT_THRESHOLD files) — skips the reviewable list
  // entirely (nobody scrolls/reads a 200-row list before confirming) and starts uploading
  // immediately, tracked by the compact "Uploading N items" widget instead. Google Drive's
  // large-batch upload widget is the reference pattern here.
  const startCompactUpload = async (entries, fallbackFolder) => {
    if (!transaction?.id || entries.length === 0) return;
    cancelUploadRef.current = false;
    setCompactUpload({
      total: entries.length, current: 0, currentFileName: null, collapsed: false,
      phase: 'starting', succeeded: 0, failed: 0, skipped: 0,
    });

    const { succeeded, failed, skipped, authError } = await runUploadEngine(entries, fallbackFolder, ({ completed, currentFileName }) => {
      setCompactUpload(prev => (prev ? { ...prev, phase: 'uploading', current: completed, currentFileName } : prev));
    });

    if (authError) {
      setCompactUpload(prev => (prev ? { ...prev, phase: 'done', failed: prev.total, currentFileName: null } : prev));
      showNotification('error', 'Upload Failed', 'Authentication error. Please try again.');
      return;
    }

    await Promise.all([fetchDocuments(), fetchGcsDocuments(), fetchTransactionFolders()]);

    setCompactUpload(prev => (prev ? { ...prev, phase: 'done', succeeded, failed, skipped, currentFileName: null } : prev));
  };

  // Stops the batch after in-flight files finish, and — if a duplicate-confirm prompt is
  // currently blocking the loop — answers it "No" immediately so the loop doesn't hang
  // waiting for a click that's never coming.
  const cancelCompactUpload = () => {
    cancelUploadRef.current = true;
    if (pendingDuplicateResolveRef.current) {
      pendingDuplicateResolveRef.current(false);
      pendingDuplicateResolveRef.current = null;
    }
  };

  // Resolve where a picked/dropped file should land: straight into the folder
  // the user is currently drilled into, or — from the folder-list/flat view — open
  // the folder-picker sheet (mirrors mobile's routeToRialty).
  // Uploads land wherever the user currently is — the folder they've drilled into, or root
  // (no folder tag at all) if they haven't. No picker, no prompt. Root is not itself a
  // folder (Windows-drive model): a null/blank folder means the file sits loose at the
  // top level alongside folder cards, it does not get filed into a synthetic "Documents" folder.
  const routeUploadToFolder = (file) => {
    uploadToGCS(file, selectedFolder);
  };

  // Move an existing document to a different folder — offers every folder in the
  // transaction (allFolderPaths, not just the current level's children), since a document
  // can move anywhere regardless of where the user is currently browsing.
  const openMoveFolderPicker = (doc, e) => {
    e.stopPropagation();
    setOpenDocMenuId(null);
    setFolderPickerMoveDoc(doc);
    const currentKey = (doc.folder || '').trim().toLowerCase() || 'documents';
    setFolderPickerSelection(Array.from(allFolderPaths).find(k => k !== currentKey) || 'documents');
    setFolderPickerNewName('');
    setFolderPickerMode('move');
  };

  const confirmMoveToFolder = async () => {
    const doc = folderPickerMoveDoc;
    if (!doc || !transaction?.id) return;
    const currentKey = (doc.folder || '').trim().toLowerCase() || 'documents';
    let targetFolder = folderPickerSelection;
    if (folderPickerSelection === '__new__') {
      targetFolder = sanitizeFolderName(folderPickerNewName);
      if (!targetFolder) return;
    }
    if (targetFolder === currentKey) { // same-folder no-op guard
      setFolderPickerMode(null);
      setFolderPickerMoveDoc(null);
      return;
    }
    setIsMovingDoc(true);
    try {
      const firebaseToken = await getFirebaseToken();
      await apiClient.patch(
        API_ENDPOINTS.updateTransactionFile(transaction.id, doc.documentId),
        { folder: targetFolder },
        { headers: { 'Authorization': `Bearer ${firebaseToken}` } }
      );
      setFolderPickerMode(null);
      setFolderPickerMoveDoc(null);
      await Promise.all([fetchGcsDocuments(), fetchTransactionFolders()]);
      if (selectedFolder === currentKey) setSelectedFolder(null); // bounce to folder list
      showNotification('success', 'Moved!', `"${displayFileName(doc.fileName) || doc.name || 'Document'}" moved successfully.`);
    } catch (error) {
      console.error('Error moving document:', error);
      showNotification('error', 'Move Failed', error.response?.data?.message || 'Failed to move document. Please try again.');
    } finally {
      setIsMovingDoc(false);
    }
  };

  // "+ New folder" — creates an empty folder (GCS .keep placeholder) and drills into it.
  // Available at any level now, not just root: when browsing inside a folder, the new
  // folder is created as a subfolder of the one currently open (fullPath = currentPath +
  // "/" + name), matching Windows Explorer's "New Folder" working inside any directory.
  const handleCreateFolder = async () => {
    const sanitized = sanitizeFolderName(createFolderName);
    if (!sanitized || !transaction?.id) return;
    const fullPath = currentPath ? `${currentPath}/${sanitized}` : sanitized;
    if (folderGroups.has(fullPath)) {
      // Collision with an existing sibling folder — just navigate in, no POST (mirrors mobile)
      setShowCreateFolderModal(false);
      setSelectedFolder(fullPath);
      return;
    }
    setIsCreatingFolder(true);
    try {
      const firebaseToken = await getFirebaseToken();
      await apiClient.post(
        API_ENDPOINTS.transactionFolders(transaction.id),
        { folder: fullPath },
        { headers: { 'Authorization': `Bearer ${firebaseToken}` } }
      );
      await Promise.all([fetchGcsDocuments(), fetchTransactionFolders()]);
      setShowCreateFolderModal(false);
      setSelectedFolder(fullPath);
    } catch (error) {
      console.error('Error creating folder:', error);
      showNotification('error', 'Create Folder Failed', error.response?.data?.message || 'Failed to create folder. Please try again.');
    } finally {
      setIsCreatingFolder(false);
    }
  };

  const openRenameFolder = (group, e) => {
    e.stopPropagation();
    setOpenDocMenuId(null);
    setRenameFolderTarget(group.key);
    setRenameFolderNewName(group.displayName);
  };

  const confirmRenameFolder = async () => {
    const oldFolder = renameFolderTarget; // full path, e.g. "folder1/sub1"
    const sanitized = sanitizeFolderName(renameFolderNewName); // new SEGMENT name only — rename
    // never moves a folder to a different parent, it only changes its own name.
    if (!oldFolder || !sanitized || !transaction?.id) return;
    const oldLastSegment = oldFolder.includes('/') ? oldFolder.slice(oldFolder.lastIndexOf('/') + 1) : oldFolder;
    if (sanitized === oldLastSegment) {
      setRenameFolderTarget(null);
      return;
    }
    setIsRenamingFolder(true);
    try {
      const firebaseToken = await getFirebaseToken();
      await apiClient.patch(
        API_ENDPOINTS.renameTransactionFolder(transaction.id, oldFolder),
        { newFolder: sanitized },
        { headers: { 'Authorization': `Bearer ${firebaseToken}` } }
      );
      await Promise.all([fetchGcsDocuments(), fetchTransactionFolders()]);
      setRenameFolderTarget(null);
      // No navigation bounce needed here (unlike delete below) — renaming a folder is only
      // ever triggered from its own card, which only appears while browsing its PARENT
      // level, so selectedFolder can never equal (or be nested under) oldFolder mid-rename.
    } catch (error) {
      console.error('Error renaming folder:', error);
      const msg = error.response?.status === 409
        ? 'A folder with that name already exists.'
        : (error.response?.data?.message || 'Failed to rename folder. Please try again.');
      showNotification('error', 'Rename Failed', msg);
    } finally {
      setIsRenamingFolder(false);
    }
  };

  const openDeleteFolder = (group, e) => {
    e.stopPropagation();
    setOpenDocMenuId(null);
    setDeleteFolderTarget(group);
  };

  // Deleting a folder — and any nested subfolders under it — never deletes its documents;
  // everything at or under it moves up to Transaction Documents (root) instead. The
  // confirmation copy makes that explicit before the user commits. Like rename, delete is
  // only ever triggered from a folder's own card (which only appears while browsing its
  // PARENT level), so selectedFolder can never equal/nest under the deleted folder — no
  // navigation bounce needed here.
  const confirmDeleteFolder = async () => {
    const group = deleteFolderTarget;
    if (!group || !transaction?.id) return;
    setIsDeletingFolder(true);
    try {
      const firebaseToken = await getFirebaseToken();
      await apiClient.delete(
        API_ENDPOINTS.deleteTransactionFolder(transaction.id, group.key),
        { headers: { 'Authorization': `Bearer ${firebaseToken}` } }
      );
      await Promise.all([fetchGcsDocuments(), fetchTransactionFolders()]);
      setDeleteFolderTarget(null);
    } catch (error) {
      console.error('Error deleting folder:', error);
      showNotification('error', 'Delete Failed', error.response?.data?.message || 'Failed to delete folder. Please try again.');
    } finally {
      setIsDeletingFolder(false);
    }
  };

  // Download a folder (and its subfolders) as a ZIP — same fetch+blob+anchor pattern as
  // handleGcsDownload, just pointed at the folder-zip endpoint. Drives the bottom-right
  // "Preparing download" panel (folderDownload state) instead of an inline spinner, since
  // this can take a while for a folder with many/large files and the user may navigate the
  // Documents tab while it's zipping — Google Drive's own async-download UX.
  const handleDownloadFolder = async (group, e) => {
    if (e) e.stopPropagation();
    setOpenDocMenuId(null);
    if (!transaction?.id || !group?.key) return;

    setFolderDownload({ name: group.displayName, status: 'zipping' });
    try {
      const firebaseToken = await getFirebaseToken();
      const response = await fetch(
        API_ENDPOINTS.downloadTransactionFolder(transaction.id, group.key),
        { headers: { 'Authorization': `Bearer ${firebaseToken}` } }
      );
      if (!response.ok) throw new Error(`Download failed (${response.status})`);

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${group.displayName}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setFolderDownload({ name: group.displayName, status: 'done' });
    } catch (error) {
      console.error('Folder download error:', error);
      setFolderDownload({ name: group.displayName, status: 'error' });
      showNotification('error', 'Download Failed', 'Failed to download folder. Please try again.');
    }
  };

  // Share a folder — mirrors DisclosureTab's share flow. Opens a modal, POSTs
  // create-share, shows the link + Copy + Revoke buttons. If an active
  // (non-revoked) share already exists for this folder we reuse it instead of
  // minting a duplicate — one active link per folder keeps the recipient
  // experience unambiguous, same rule the disclosure share enforces.
  const openShareFolder = async (group, e) => {
    if (e) e.stopPropagation();
    setOpenDocMenuId(null);
    if (!transaction?.id || !group?.key) return;

    setShareModal({ group, share: null });
    setShareLinkCopied(false);
    setIsCreatingShare(true);
    try {
      const firebaseToken = await getFirebaseToken();
      const authHeader = { headers: { 'Authorization': `Bearer ${firebaseToken}` } };

      const existingRes = await apiClient.get(
        API_ENDPOINTS.folderSharesList(transaction.id, group.key),
        authHeader
      );
      const existing = Array.isArray(existingRes.data)
        ? existingRes.data.find(s => !s.isRevoked)
        : null;

      if (existing) {
        setShareModal({ group, share: existing });
      } else {
        const createRes = await apiClient.post(
          API_ENDPOINTS.folderShares(transaction.id),
          { folder: group.key },
          authHeader
        );
        setShareModal({ group, share: createRes.data });
      }
    } catch (error) {
      console.error('Error creating folder share:', error);
      showNotification(
        'error',
        'Share Failed',
        error.response?.data?.message || 'Failed to create share link. Please try again.'
      );
      setShareModal(null);
    } finally {
      setIsCreatingShare(false);
    }
  };

  const handleCopyShareLink = async () => {
    const url = shareModal?.share?.shareUrl;
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setShareLinkCopied(true);
      setTimeout(() => setShareLinkCopied(false), 2000);
    } catch {
      // clipboard API can fail on non-https / older browsers — fall back to prompt-select
      window.prompt('Copy the share link:', url);
    }
  };

  const handleRevokeShareLink = async () => {
    const share = shareModal?.share;
    if (!share || !transaction?.id) return;
    // Same "type REVOKE to confirm" gate would be overkill for a single link;
    // a native confirm() matches Rialty's other one-off destructive UX (delete
    // party, remove passkey) and keeps the modal focus.
    if (!window.confirm('Revoke this share link? Anyone who has it will lose access.')) return;

    setIsRevokingShare(true);
    try {
      const firebaseToken = await getFirebaseToken();
      const res = await apiClient.post(
        API_ENDPOINTS.folderShareRevoke(transaction.id, share.shareToken),
        {},
        { headers: { 'Authorization': `Bearer ${firebaseToken}` } }
      );
      setShareModal(prev => (prev ? { ...prev, share: res.data } : prev));
    } catch (error) {
      console.error('Error revoking folder share:', error);
      showNotification(
        'error',
        'Revoke Failed',
        error.response?.data?.message || 'Failed to revoke share link. Please try again.'
      );
    } finally {
      setIsRevokingShare(false);
    }
  };

  // Drag-and-drop onto the existing Upload Document button — routes through the exact
  // same duplicate/signature/folder pipeline a picked file would.
  const acceptedDocExt = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.png', '.jpg', '.jpeg'];
  const hasAcceptedExt = (fileName) =>
    acceptedDocExt.includes('.' + (fileName.split('.').pop() || '').toLowerCase());

  const handleDocsDrop = async (e) => {
    e.preventDefault();
    setIsDocsDragging(false);
    if (isUploading || isBatchUploading || isScanningFolder) return;

    // Folder drop: only detectable via dataTransfer.items + webkitGetAsEntry() — plain
    // dataTransfer.files never exposes a dropped folder's contents at all. A user can
    // multi-select several folders (and/or loose files) in Explorer/Finder and drag them
    // all together, so this checks every top-level item, not just the first.
    const items = e.dataTransfer.items;
    const hasFolder = items && Array.from(items).some((item) => {
      const entry = typeof item.webkitGetAsEntry === 'function' ? item.webkitGetAsEntry() : null;
      return entry && entry.isDirectory;
    });

    if (hasFolder) {
      setIsScanningFolder(true);
      try {
        const pairs = await collectEntriesFromDataTransferItems(items);
        const accepted = pairs.filter((p) => hasAcceptedExt(p.file.name));
        const skipped = pairs.length - accepted.length;
        if (accepted.length === 0) {
          showNotification('error', 'Unsupported Files', 'No PDF, Word, Excel, or image files were found in the dropped folder(s).');
          return;
        }
        openFolderUploadCard(accepted);
        if (skipped > 0) {
          showNotification('warning', 'Some Files Skipped', `${skipped} unsupported file${skipped === 1 ? '' : 's'} in the dropped folder(s) ${skipped === 1 ? 'was' : 'were'} not included.`);
        }
      } catch (error) {
        console.error('Error reading dropped folder:', error);
        showNotification('error', 'Folder Read Failed', 'Could not read the dropped folder. Please try again.');
      } finally {
        setIsScanningFolder(false);
      }
      return;
    }

    const file = Array.from(e.dataTransfer.files)[0];
    if (!file) return;
    if (!hasAcceptedExt(file.name)) {
      showNotification('error', 'Unsupported File', 'Please upload a PDF, Word, Excel, or image file.');
      return;
    }
    showUploadConfirmDialog(
      [{ key: `${file.name}-${file.size}-${Date.now()}`, file }],
      selectedFolder,
      () => routeUploadToFolder(file),
    );
  };

  // True when the backend rejected the upload because a document with that name
  // already exists (e.g. "A document named \"x.pdf\" already exists for this
  // transaction folder"). Other errors keep going through the normal error notification.
  const isDuplicateDocumentError = (error) => {
    // 409 mirrors mobile's isDuplicateNameError forward-compat check for a typed
    // DuplicateDocumentNameException, once the backend ships one; today's backend
    // returns 400 + an "already exists" message, which the string check below covers.
    if (error.response?.status === 409) return true;
    const msg = (error.response?.data?.message || error.response?.data?.error || '').toLowerCase();
    return msg.includes('already exists');
  };

  // Next free "name (1).ext", "name (2).ext", ... against every document name
  // currently listed for this transaction (Forms + Transaction Documents combined),
  // so the retried upload lands as a distinctly-named new file, not a collision.
  const getNextAvailableFileName = (originalName) => {
    const existingNames = new Set();
    documents.forEach(doc => {
      const n = (doc.fileName || doc.name || '').trim().toLowerCase();
      if (n) existingNames.add(n);
    });
    gcsDocuments.forEach(doc => {
      const n = (doc.fileName || doc.name || '').trim().toLowerCase();
      if (n) existingNames.add(n);
    });

    const dotIndex = originalName.lastIndexOf('.');
    const base = dotIndex > 0 ? originalName.slice(0, dotIndex) : originalName;
    const ext = dotIndex > 0 ? originalName.slice(dotIndex) : '';

    let counter = 1;
    let candidate = `${base} (${counter})${ext}`;
    while (existingNames.has(candidate.toLowerCase())) {
      counter += 1;
      candidate = `${base} (${counter})${ext}`;
    }
    return candidate;
  };

  // Duplicate confirm — "No": close the popup, no upload, existing document untouched
  // (mirrors what the old "Upload Failed" OK button already did).
  const cancelDuplicateUploadConfirm = () => {
    setDuplicateUploadConfirm({ show: false, file: null, target: null });
  };

  // Duplicate confirm — "Yes": rename the file to the next available name and retry
  // the retried upload. The existing document is never touched — this only adds a
  // new, distinctly-named file.
  const confirmDuplicateUploadAsNewVersion = () => {
    const { file } = duplicateUploadConfirm;
    setDuplicateUploadConfirm({ show: false, file: null, target: null });
    if (!file) return;
    const renamedFile = new File([file], getNextAvailableFileName(file.name), { type: file.type });
    uploadToGCS(renamedFile, duplicateUploadConfirm.folder);
  };

  // Upload to GCS (Transaction Documents) — the only upload path now; every file lands
  // here regardless of type, no signature question asked at upload time.
  // FormData fields: file (+ folder, only when the user is drilled into a real folder —
  // a null/blank folder means the file stays loose at root, it is never defaulted to a
  // synthetic "documents" folder).
  const uploadToGCS = async (file, folder = null) => {
    if (!file || !transaction?.id) return;
    setIsUploading(true);

    try {
      const firebaseToken = await getFirebaseToken();
      const formData = new FormData();
      formData.append('file', file, file.name);
      if (folder) formData.append('folder', folder);

      await axios.post(
        API_ENDPOINTS.uploadTransactionFile(transaction.id),
        formData,
        { headers: { 'Authorization': `Bearer ${firebaseToken}` } }
      );

      showNotification('success', 'Uploaded!', `"${file.name}" has been uploaded successfully.`);
      // Refresh all three — mirrors mobile which calls fetchDocuments() + fetchGcsDocuments()
      // (+ folders, since an upload can affect a folder's doc count/empty state)
      await Promise.all([fetchDocuments(), fetchGcsDocuments(), fetchTransactionFolders()]);
    } catch (error) {
      console.error('Error uploading to GCS:', error);
      if (isDuplicateDocumentError(error)) {
        setDuplicateUploadConfirm({ show: true, file, target: 'gcs', folder });
      } else {
        // Prefer `message` over `error` — backend puts the specific reason in `message`
        // and a generic HTTP status label (e.g. "Bad Request") in `error`.
        showNotification('error', 'Upload Failed', error.response?.data?.message || error.response?.data?.error || 'Failed to upload document. Please try again.');
      }
    } finally {
      setIsUploading(false);
    }
  };

  // Fetch GCS documents — mirrors mobile DocsTab.tsx: no folder filter, so any
  // folder (documents, cma, nhd, etc.) is picked up automatically without code changes.
  const fetchGcsDocuments = useCallback(async () => {
    if (!transaction?.id) {
      setIsLoadingGcsDocuments(false);
      return;
    }
    setIsLoadingGcsDocuments(true);
    try {
      const firebaseToken = await getFirebaseToken();
      const response = await apiClient.get(
        API_ENDPOINTS.transactionFiles(transaction.id),
        {
          headers: { 'Authorization': `Bearer ${firebaseToken}` },
        }
      );
      const data = response.data;
      let gcsDocs = [];
      if (Array.isArray(data)) {
        gcsDocs = data;
      } else if (data && Array.isArray(data.data)) {
        gcsDocs = data.data;
      } else if (data && Array.isArray(data.files)) {
        gcsDocs = data.files;
      } else if (data && Array.isArray(data.documents)) {
        gcsDocs = data.documents;
      } else if (data && Array.isArray(data.content)) {
        gcsDocs = data.content;
      }
      // Tag GCS docs so they can be distinguished from DocuSign docs in the list.
      // envelopeStatus/callerRecipientStatus are already live-resolved server-side
      // (DocumentService.resolveLiveEnvelopeStatus) — no client-side DocuSign polling needed.
      const taggedDocs = gcsDocs.map(doc => ({ ...doc, _source: 'gcs' }));
      setGcsDocuments(taggedDocs);
    } catch (error) {
      console.error('Error fetching GCS documents:', error);
    } finally {
      setIsLoadingGcsDocuments(false);
    }
  }, [transaction?.id, getFirebaseToken]);

  // Non-fatal, non-Colorado only — Draft/Sent/Signed status for every custom PDF ever sent for
  // signature via Lone Wolf on this transaction, keyed by documentId. Falls back to {} so a
  // failed status check never blocks the rest of the Documents tab from rendering.
  const fetchLonewolfDocumentSignings = useCallback(async () => {
    if (!transaction?.id || transaction?.esignatureProvider !== 'LONEWOLF') {
      return;
    }
    try {
      const firebaseToken = await getFirebaseToken();
      const response = await apiClient.get(
        API_ENDPOINTS.lonewolfDocumentSignings(transaction.id),
        { headers: { 'Authorization': `Bearer ${firebaseToken}` } }
      );
      setLonewolfDocSignings(response.data && typeof response.data === 'object' ? response.data : {});
    } catch (error) {
      console.error('Error fetching Lone Wolf document signing statuses:', error);
      setLonewolfDocSignings({});
    }
  }, [transaction?.id, transaction?.esignatureProvider, getFirebaseToken]);

  // Non-fatal fetch — mirrors mobile's getTransactionFolders, silently falls back to []
  const fetchTransactionFolders = useCallback(async () => {
    if (!transaction?.id) return;
    try {
      const firebaseToken = await getFirebaseToken();
      const response = await apiClient.get(
        API_ENDPOINTS.transactionFolders(transaction.id),
        { headers: { 'Authorization': `Bearer ${firebaseToken}` } }
      );
      const data = response.data;
      setBackendFolders(Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : []);
    } catch (error) {
      console.error('Error fetching transaction folders:', error);
      setBackendFolders([]);
    }
  }, [transaction?.id, getFirebaseToken]);

  useEffect(() => {
    fetchGcsDocuments();
    fetchTransactionFolders();
    fetchLonewolfDocumentSignings();
  }, [fetchGcsDocuments, fetchTransactionFolders, fetchLonewolfDocumentSignings]);

  // True when a folder value represents "root" — no real folder, just a loose file sitting
  // at the top level. Collapses null/blank AND the legacy literal "documents" (written by
  // uploads before this fix) into the same bucket so old data still renders as root, not as
  // an orphaned folder nothing routes new files into.
  const isRootFolderValue = (rawFolder) => {
    const trimmed = (rawFolder || '').trim().toLowerCase();
    return !trimmed || trimmed === 'documents';
  };

  // Full flat set of every known folder PATH in the transaction (possibly nested, e.g.
  // "folder1/sub1") — from both live documents' folder values and backendFolders (GET
  // /folders, the .keep-placeholder list, which is what makes an empty subfolder durable).
  // Also synthesizes every ANCESTOR of each path (adding "folder1/sub1" also adds "folder1"),
  // so a document is always reachable by drilling down even if some intermediate level has
  // no placeholder/direct file of its own — normal in-app usage can't produce that gap (you
  // can't create a subfolder without first browsing into an existing parent), but the backend
  // upload endpoint historically stored the folder field with no validation at all, so older
  // or externally-written data could still have an orphaned nested path without this.
  const allFolderPaths = useMemo(() => {
    const paths = new Set();
    const addWithAncestors = (raw) => {
      if (isRootFolderValue(raw)) return;
      const full = (raw || '').trim().toLowerCase();
      const segments = full.split('/');
      let acc = '';
      for (const seg of segments) {
        acc = acc ? `${acc}/${seg}` : seg;
        paths.add(acc);
      }
    };
    for (const doc of gcsDocuments) addWithAncestors(doc.folder);
    for (const f of backendFolders) addWithAncestors(f);
    // Previously always injected 'service-request' here (spec §5 wireframe 4)
    // so the folder card showed at the root even before any document existed —
    // that meant every new transaction got an empty "Service Request" folder
    // immediately on creation. Now it's purely data-driven: the folder only
    // appears once a real document has been filed into it (its `folder` field
    // is 'service-request') or a backend .keep placeholder exists for it,
    // i.e. once the requester or provider actually shares a document within
    // a service request.
    return paths;
  }, [gcsDocuments, backendFolders]);

  // "" = root/top-level; otherwise the full path of the folder currently being browsed.
  const currentPath = selectedFolder || '';

  // Windows-drive/Explorer model, extended to real nesting: folderGroups is now the set of
  // DIRECT CHILD folders one level below currentPath (not every folder in the transaction —
  // a grandchild like "folder1/sub1/sub2" only becomes visible once you've drilled into
  // "folder1/sub1"). Loose files at the current level (currentLevelDocs, below) render
  // alongside these cards — there is no synthetic wrapper folder for them.
  const folderGroups = useMemo(() => {
    const childPrefix = currentPath ? currentPath + '/' : '';
    const groups = new Map();
    for (const path of allFolderPaths) {
      const remainder = currentPath ? (path.startsWith(childPrefix) ? path.slice(childPrefix.length) : null) : path;
      // null (didn't start with the prefix), empty (the current folder itself), or
      // containing "/" (a grandchild, not a direct child) — none of those belong here.
      if (!remainder || remainder.includes('/')) continue;
      groups.set(path, { key: path, displayName: displayFolderName(remainder), docs: [], latestUploadedAt: null, subfolderCount: 0 });
    }
    // Direct-file counts/timestamps only — a folder card's badge reflects what's
    // immediately inside it, not a recursive total of nested subfolders' contents too.
    for (const doc of gcsDocuments) {
      const df = (doc.folder || '').trim().toLowerCase();
      const group = groups.get(df);
      if (!group) continue;
      group.docs.push(doc);
      const ts = doc.uploadedAt || doc.createdAt;
      if (ts && (!group.latestUploadedAt || ts > group.latestUploadedAt)) group.latestUploadedAt = ts;
    }
    // Direct-subfolder counts, same "one level deeper" logic applied from each group's own
    // key — this is what makes the "Empty" label accurate (a folder with 0 direct files but
    // 1+ subfolders is NOT empty) and lets the badge hint at nested structure.
    for (const path of allFolderPaths) {
      for (const group of groups.values()) {
        if (path.startsWith(group.key + '/')) {
          const remainder = path.slice(group.key.length + 1);
          if (remainder && !remainder.includes('/')) group.subfolderCount += 1;
        }
      }
    }
    return new Map(
      Array.from(groups.entries()).sort((a, b) => (b[1].latestUploadedAt || '').localeCompare(a[1].latestUploadedAt || ''))
    );
  }, [allFolderPaths, gcsDocuments, currentPath]);

  // Loose files sitting exactly at the current level (no deeper folder, or — at root only —
  // the legacy "documents" tag) — rendered alongside the folder cards above.
  const currentLevelDocs = useMemo(
    () => gcsDocuments.filter(doc => {
      if (!currentPath) return isRootFolderValue(doc.folder);
      return (doc.folder || '').trim().toLowerCase() === currentPath;
    }),
    [gcsDocuments, currentPath]
  );

  const docsToRender = currentLevelDocs;

  // "Folder1 › Sub1" style label for a full path, regardless of depth — used by the
  // Move-to-folder picker so nested folders sharing a name at different levels (or just
  // far from the current view) are still distinguishable without an expandable tree UI.
  const fullPathDisplayName = (path) => path.split('/').map(displayFolderName).join(' › ');

  // One level up from a given folder path — "" (root) for a top-level folder.
  const parentFolderPath = (path) => (path.includes('/') ? path.slice(0, path.lastIndexOf('/')) : '');

  // Breadcrumb trail for the current path: [{path: 'folder1', label}, {path: 'folder1/sub1', label}, ...]
  const breadcrumbSegments = useMemo(() => {
    if (!selectedFolder) return [];
    const parts = selectedFolder.split('/');
    const segments = [];
    let acc = '';
    for (const part of parts) {
      acc = acc ? `${acc}/${part}` : part;
      segments.push({ path: acc, label: displayFolderName(part) });
    }
    return segments;
  }, [selectedFolder]);

  // Total documents at-or-under a folder path (including nested subfolders) — used for the
  // delete-folder confirmation copy, since deleting cascades through all descendants, unlike
  // a folder card's own badge (direct children only).
  const countDocsUnderFolder = (path) => {
    const prefix = path + '/';
    return gcsDocuments.filter(doc => {
      const df = (doc.folder || '').trim().toLowerCase();
      return df === path || df.startsWith(prefix);
    }).length;
  };

  // Status badge for a Transaction Documents (GCS) card — reflects the real envelope
  // lifecycle once Send for Signature has been used; plain "Uploaded" beforehand.
  //
  // envelopeStatus is already live-resolved server-side (DocumentService.resolveLiveEnvelopeStatus),
  // so this is pure presentation — no client-side DocuSign calls or derived flags. Client
  // (Buyer/Seller) sees their own recipient status (callerRecipientStatus); Agent/TC sees the
  // whole-envelope summary (envelopeStatus).
  // Latest (most recent) Lone Wolf send-for-signature attempt for a custom PDF, if any —
  // lonewolfDocSignings values are already sorted most-recent-first by the backend.
  const latestLonewolfSigning = (doc) => {
    const attempts = lonewolfDocSignings[doc.documentId || doc.id];
    return Array.isArray(attempts) && attempts.length > 0 ? attempts[0] : null;
  };
  const gcsEnvelopeStatusLabel = (doc) => {
    if (isClient && doc.callerRecipientStatus) {
      if (doc.callerRecipientStatus === 'completed') return 'Signed';
      if (doc.callerRecipientStatus === 'sent' || doc.callerRecipientStatus === 'delivered') return 'Received';
    }
    if (transaction?.esignatureProvider === 'LONEWOLF') {
      const latest = latestLonewolfSigning(doc);
      if (!latest) return 'Uploaded';
      switch (latest.status) {
        case 'SIGNED': return 'Signed';
        case 'SENT': return 'Sent';
        case 'DRAFT': return 'Draft';
        default: return 'Uploaded';
      }
    }
    switch (doc.envelopeStatus) {
      case 'DRAFT_PENDING_TAGGING': return 'Draft';
      case 'SENT': return 'Sent';
      case 'COMPLETED': return 'Signed';
      case 'VOIDED': return 'Voided';
      case 'DECLINED': return 'Declined';
      default: return 'Uploaded';
    }
  };
  const gcsEnvelopeStatusStyle = (doc) => {
    if (isClient && doc.callerRecipientStatus) {
      if (doc.callerRecipientStatus === 'completed') return { background: '#e8f5e9', color: '#2e7d32' };
      if (doc.callerRecipientStatus === 'sent' || doc.callerRecipientStatus === 'delivered') return { background: '#ede7f6', color: '#5B47D6' };
    }
    if (transaction?.esignatureProvider === 'LONEWOLF') {
      const latest = latestLonewolfSigning(doc);
      if (!latest) return { background: '#E3F2FD', color: '#1565C0' };
      switch (latest.status) {
        case 'SIGNED': return { background: '#e8f5e9', color: '#2e7d32' };
        case 'SENT': return { background: '#ede7f6', color: '#5B47D6' };
        case 'DRAFT': return { background: '#fff4e6', color: '#b06a00' };
        default: return { background: '#E3F2FD', color: '#1565C0' };
      }
    }
    switch (doc.envelopeStatus) {
      case 'COMPLETED': return { background: '#e8f5e9', color: '#2e7d32' };
      case 'SENT': return { background: '#ede7f6', color: '#5B47D6' };
      case 'DRAFT_PENDING_TAGGING': return { background: '#fff4e6', color: '#b06a00' };
      case 'VOIDED': case 'DECLINED': return { background: '#fdecea', color: '#c0392b' };
      default: return { background: '#E3F2FD', color: '#1565C0' };
    }
  };

  // Self-healing: if the drilled-in folder disappears (e.g. deleted from another tab, or
  // its last file+subfolder moved out) bounce up one level rather than leaving the user on
  // a dead screen. Checked against allFolderPaths (every folder anywhere in the transaction)
  // — folderGroups itself is now level-scoped to selectedFolder's CHILDREN, so it never
  // contains selectedFolder's own path and can't be used for this check.
  useEffect(() => {
    if (selectedFolder && !allFolderPaths.has(selectedFolder)) {
      setSelectedFolder(parentFolderPath(selectedFolder) || null);
    }
  }, [selectedFolder, allFolderPaths]);

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // State for adding document
  const [isAddingDocument, setIsAddingDocument] = useState(false);

  // State for review document (track which document is being reviewed)
  const [reviewingDocumentId, setReviewingDocumentId] = useState(null);

  // State for viewing document (track which document is being downloaded)
  const [viewingDocumentId, setViewingDocumentId] = useState(null);

  // State for PDF viewer modal
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfDocumentName, setPdfDocumentName] = useState('');
  // The row shown in the PDF viewer, so its header can offer the Ria Summary action (FR-3).
  const [pdfSummaryDoc, setPdfSummaryDoc] = useState(null); // { doc, area: 'documents' | 'forms' }



  // State for Send for Signature modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureDocument, setSignatureDocument] = useState(null);
  const [signatureParticipants, setSignatureParticipants] = useState([]);
  const [isLoadingParticipants, setIsLoadingParticipants] = useState(false);
  const [isSendingEnvelope, setIsSendingEnvelope] = useState(false);
  // True while the pre-modal DocuSign readiness check runs (Colorado only), so the click
  // gives immediate feedback instead of a silent delay before the recipient dialog opens.
  const [isPreparingSignature, setIsPreparingSignature] = useState(false);
  const [selectedParticipantIds, setSelectedParticipantIds] = useState(new Set());
  const [envelopeStep, setEnvelopeStep] = useState('select'); // 'select' | 'preview'
  const [envelopeEmailSubject, setEnvelopeEmailSubject] = useState('');

  // State for notification popup
  const [notification, setNotification] = useState({
    show: false,
    type: 'success', // 'success' or 'error'
    title: '',
    message: ''
  });

  // Show notification helper
  const showNotification = (type, title, message) => {
    setNotification({
      show: true,
      type,
      title,
      message
    });
  };

  // Close notification
  const closeNotification = () => {
    setNotification(prev => ({ ...prev, show: false }));
  };

  // Handle add selected forms - POST API call
  // Colorado FormService flow: find-or-create a package for this transaction, then add the selected
  // form. FormService fills the template with transaction data and writes the working PDF to
  // gs://rialty-forms-output-{env}/{state}/{packageId}/{formId}.pdf.
  const handleAddColoradoForm = async (selectedForm, formId) => {
    setIsAddingDocument(true);
    try {
      const firebaseToken = await getFirebaseToken();
      const auth = { headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' } };

      // 1) reuse an existing package for this transaction, else create one
      let packageId;
      try {
        const pkgResp = await apiClient.get(API_ENDPOINTS.formsPackagesByTransaction(transaction.id), auth);
        const pkgs = Array.isArray(pkgResp.data) ? pkgResp.data : (pkgResp.data?.data || []);
        packageId = pkgs[0]?.packageId || pkgs[0]?.id;
      } catch (e) {
        // no package yet — fall through to create
      }

      if (!packageId) {
        const side = (transaction?.side || transaction?.transactionSide || transaction?.type || '').toString().toUpperCase();
        const transactionType = side.includes('LIST') || side.includes('SALE') ? 'LISTING' : 'PURCHASE';
        const createResp = await apiClient.post(
          API_ENDPOINTS.formsCreatePackage,
          { transactionId: transaction.id, stateCode: 'CO', transactionType },
          auth
        );
        packageId = createResp.data?.packageId || createResp.data?.id;
      }

      if (!packageId) {
        throw new Error('Could not create or find a forms package for this transaction');
      }

      // 2) add + populate the form → generates and stores the working PDF in the output bucket
      const response = await apiClient.post(API_ENDPOINTS.formsAddForm(packageId), { formId }, auth);
      console.log('FormService add-form response:', response.data);

      setShowCreateDocModal(false);
      setSelectedForms([]);
      await fetchDocuments();
    } catch (error) {
      console.error('Error adding Colorado form:', error);
      console.error('Error details:', error.response?.data || error.message);
      if (error.response?.status === 409) {
        const displayName = selectedForm.name || selectedForm.formName || selectedForm.title || selectedForm.templateName || 'This form';
        alert(`Form "${displayName}" already exists in this package.`);
      } else {
        alert(error.response?.data?.message || error.response?.data?.error || 'Failed to add form. Please try again.');
      }
    } finally {
      setIsAddingDocument(false);
    }
  };

  const handleAddSelectedForms = async () => {
    if (selectedForms.length === 0) {
      alert('Please select a form');
      return;
    }

    if (!transaction?.id) {
      alert('Transaction ID is missing');
      return;
    }

    const selectedForm = selectedForms[0];
    const formId = getFormIdForApi(selectedForm);

    if (!formId) {
      alert('Form ID is missing');
      return;
    }

    // Colorado (FormService): generate + store the working PDF instead of the DocuSign flow.
    if (selectedForm?._source === 'formservice') {
      return handleAddColoradoForm(selectedForm, formId);
    }

    // California, Active-on-Lone-Wolf agent: attach the form on Lone Wolf's transaction record
    // instead of DocuSign's add-document-form flow.
    if (selectedForm?._source === 'lonewolf') {
      setIsAddingDocument(true);
      try {
        const firebaseToken = await getFirebaseToken();
        await apiClient.post(
          `${API_ENDPOINTS.lonewolfAttachForm(transaction.id)}?libraryId=${encodeURIComponent(selectedForm._libraryId)}&formId=${encodeURIComponent(formId)}`,
          {},
          { headers: { 'Authorization': `Bearer ${firebaseToken}` } }
        );
        setShowCreateDocModal(false);
        setSelectedForms([]);
        await fetchDocuments();
      } catch (error) {
        console.error('Error attaching Lone Wolf form:', error);
        alert(error.response?.data?.error || 'Failed to add form. Please try again.');
      } finally {
        setIsAddingDocument(false);
      }
      return;
    }

    setIsAddingDocument(true);

    try {
      const firebaseToken = await getFirebaseToken();

      console.log('Adding document with formId (libraryFormId):', formId);
      console.log('Selected form details:', selectedForm);

      const response = await apiClient.post(
        API_ENDPOINTS.addDocumentForm(transaction.id),
        { formId },
        {
          headers: {
            'Authorization': `Bearer ${firebaseToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('Add document response:', response.data);

      // Close modal and reset selection
      setShowCreateDocModal(false);
      setSelectedForms([]);

      // Refresh documents list after successfully adding
      await fetchDocuments();

      console.log('Documents list refreshed successfully');
    } catch (error) {
      console.error('Error adding document:', error);
      console.error('Error details:', error.response?.data || error.message);
      alert(error.response?.data?.message || 'Failed to add document. Please try again.');
    } finally {
      setIsAddingDocument(false);
    }
  };

  // Handle preview click
  const handlePreview = (form, e) => {
    e.stopPropagation();
    console.log('Preview form:', form);
    alert(`Previewing: ${form.name || form.title}`);
  };

  // Handle View Document click - download and open in new tab
  // Colorado (FormService): open the generated PDF from the output bucket in a new browser tab.
  // Same in-app PDF viewer modal as handleViewDocument (showPdfViewer/pdfBlobUrl/
  // pdfDocumentName) — was previously opening in a new browser tab, inconsistent with
  // Edit Form's in-app dialog. Now both View and Edit stay in-app.
  const handleViewColoradoForm = async (doc, e) => {
    if (e) e.stopPropagation();
    const instanceId = doc.id || doc.documentId;
    const packageId = doc._packageId;
    // Once a form has a live envelope, View pulls the real DocuSign document (signatures
    // visible as they're collected, not just at completion) instead of FormService's own
    // static field-data render — the two are genuinely different files, and only DocuSign's
    // copy ever shows actual signatures.
    if (doc.envelopeId) {
      setViewingDocumentId(instanceId);
      try {
        const firebaseToken = await getFirebaseToken();
        const response = await apiClient.get(API_ENDPOINTS.envelopeDocuments(doc.envelopeId), {
          headers: { 'Authorization': `Bearer ${firebaseToken}` },
          responseType: 'blob'
        });
        const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/pdf' });
        const blobUrl = window.URL.createObjectURL(blob);
        setPdfDocumentName(doc.name || doc.title || 'Form');
        setPdfSummaryDoc({ doc, area: 'forms' });
        setPdfBlobUrl(blobUrl);
        setShowPdfViewer(true);
      } catch (error) {
        console.error('Error opening envelope document:', error);
        alert(error.response?.data?.message || 'Failed to open document. Please try again.');
      } finally {
        setViewingDocumentId(null);
      }
      return;
    }
    if (!packageId || !instanceId) {
      alert('Form package or id is missing');
      return;
    }
    setViewingDocumentId(instanceId);
    try {
      const firebaseToken = await getFirebaseToken();
      const response = await apiClient.get(API_ENDPOINTS.formsDownloadPdf(packageId, instanceId), {
        headers: { 'Authorization': `Bearer ${firebaseToken}` },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      setPdfDocumentName(doc.name || doc.title || 'Form');
      setPdfSummaryDoc({ doc, area: 'forms' });
      setPdfBlobUrl(blobUrl);
      setShowPdfViewer(true);
    } catch (error) {
      console.error('Error opening Colorado form:', error);
      alert(error.response?.data?.message || 'Failed to open form. Please try again.');
    } finally {
      setViewingDocumentId(null);
    }
  };

  // View/Download a Lone Wolf-attached form's PDF — same download-bytes-then-show-in-dialog
  // pattern as handleViewColoradoForm above, different data source (Lone Wolf's forms-editor
  // API instead of GCS). Needs _versionId (from fetchDocuments' CA+Lone Wolf branch), not the
  // form's own id/documentId — Lone Wolf's download endpoint 400s on those.
  // Opens Lone Wolf's own hosted forms editor for this form's package — no separate API
  // call needed, packageId already comes back on the same GET .../forms list response
  // handleViewLoneWolfForm's sibling fetch uses (see fetchDocuments' _packageId capture).
  const handleEditLoneWolfForm = (doc, e) => {
    if (e) e.stopPropagation();
    if (!doc._packageId) {
      alert('Form package is missing');
      return;
    }
    // Open Lone Wolf's hosted forms editor (fill + send both happen there) in a centered
    // popup window instead of a full background tab, so it feels contained like our own
    // View modal. It's an external, authenticated app, so it can't be embedded in an
    // iframe/modal — X-Frame-Options and third-party-cookie restrictions would blank it
    // out or break its session — hence a top-level popup rather than a true iframe view.
    // Falls back to a new tab if the popup is blocked.
    const url = `https://formseditor.lwolf.com/${doc._packageId}`;
    const w = Math.min(1100, window.screen?.availWidth || 1100);
    const h = Math.min(900, window.screen?.availHeight || 900);
    const left = Math.round(Math.max(0, ((window.screen?.availWidth || w) - w) / 2 + (window.screenX || 0)));
    const top = Math.round(Math.max(0, ((window.screen?.availHeight || h) - h) / 2 + (window.screenY || 0)));
    const popup = window.open(
      url,
      'lonewolf_forms_editor',
      `popup=yes,width=${w},height=${h},left=${left},top=${top}`
    );
    if (popup) {
      // Reverse-tabnabbing protection (equivalent to rel=noopener) without losing the
      // handle we need to detect a blocked popup below.
      popup.opener = null;
      popup.focus();
    } else {
      // Popup blocked — never silently no-op; fall back to a new tab.
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // Send For Signature on a Lone Wolf form — same target as Edit, but checked against a
  // live Lone Wolf connection first (mirrors handleSendForSignature's DocuSign gate for
  // Colorado). The form row itself only ever renders when the connection was Active at
  // load time, but this re-checks live rather than trusting that snapshot, same
  // check-before-every-action approach DocuSign's gate uses.
  const handleLoneWolfSendForSignature = async (doc, e) => {
    if (e) e.stopPropagation();
    // The Lone Wolf status check below is a backend round-trip, so show the preparing spinner
    // overlay and block re-entry until it resolves — mirrors handleSendForSignature's Colorado
    // DocuSign gate. Both toggle isPreparingSignature, which drives the spinner overlay in JSX.
    if (isPreparingSignature) return;
    setIsPreparingSignature(true);
    try {
      const currentUserEmail = (() => {
        try { return (JSON.parse(localStorage.getItem('user') || '{}')).email || ''; } catch { return ''; }
      })();
      let isLoneWolfActiveUser = false;
      let loneWolfAccountExists = false;
      if (currentUserEmail) {
        try {
          const firebaseToken = await getFirebaseToken();
          const emailToCheck = await resolveLoneWolfCheckEmail(firebaseToken, currentUserEmail);
          const statusResp = await apiClient.get(API_ENDPOINTS.lonewolfUserStatus(emailToCheck), {
            headers: { 'Authorization': `Bearer ${firebaseToken}` }
          });
          isLoneWolfActiveUser = statusResp.data?.active === true;
          loneWolfAccountExists = statusResp.data?.exists === true;
        } catch (err) {
          console.error('[Send For Signature] Lone Wolf status check failed:', err);
        }
      }
      if (!isLoneWolfActiveUser) {
        triggerLoneWolfGate(loneWolfAccountExists ? 'pending' : 'not_connected', 'sendForSignature');
        return;
      }
      handleEditLoneWolfForm(doc, e);
    } finally {
      setIsPreparingSignature(false);
    }
  };

  // Fetches the actual signed document once a signing is finalized. Confirmed live (2026-09-05)
  // against a real finalized signing: Lone Wolf returns the raw PDF bytes directly (no JSON
  // envelope) — same blob-URL pattern used for envelope documents above.
  const handleViewSignedDocument = async (doc, e) => {
    if (e) e.stopPropagation();
    if (!transaction?.id || !doc._signing?.signingId) {
      alert('Signing reference is missing');
      return;
    }
    setViewingDocumentId(doc.id || doc.documentId);
    try {
      const firebaseToken = await getFirebaseToken();
      const response = await apiClient.get(
        API_ENDPOINTS.lonewolfSignedDocument(transaction.id, doc._signing.signingId),
        { headers: { 'Authorization': `Bearer ${firebaseToken}` }, responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      setPdfDocumentName(doc.name || doc.title || 'Signed Document');
      setPdfSummaryDoc({ doc, area: 'forms' });
      setPdfBlobUrl(blobUrl);
      setShowPdfViewer(true);
    } catch (error) {
      console.error('Error fetching signed document:', error);
      alert(error.response?.data?.error || 'Failed to open the signed document. Please try again.');
    } finally {
      setViewingDocumentId(null);
    }
  };

  const handleViewLoneWolfForm = async (doc, e) => {
    if (e) e.stopPropagation();
    if (!transaction?.id || !doc._versionId) {
      alert('Form version is missing');
      return;
    }
    const instanceId = doc.id || doc.documentId;
    setViewingDocumentId(instanceId);
    try {
      const firebaseToken = await getFirebaseToken();
      // Filled preview: a TDS/SPQ comes back populated from the saved wizard (wizard is the
      // single source of truth); every other Lone Wolf form returns blank unchanged, so this
      // is safe for all form types. See LoneWolfController.getFilledFormPreview.
      const response = await apiClient.get(API_ENDPOINTS.lonewolfFilledPreview(transaction.id, doc._versionId), {
        headers: { 'Authorization': `Bearer ${firebaseToken}` },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      setPdfDocumentName(doc.name || doc.title || 'Form');
      setPdfSummaryDoc({ doc, area: 'forms' });
      setPdfBlobUrl(blobUrl);
      setShowPdfViewer(true);
    } catch (error) {
      console.error('Error opening Lone Wolf form:', error);
      alert(error.response?.data?.error || 'Failed to open form. Please try again.');
    } finally {
      setViewingDocumentId(null);
    }
  };

  const handleViewDocument = async (doc, e) => {
    e.stopPropagation();

    const documentId = doc.id || doc.documentId;

    if (!transaction?.id || !documentId) {
      alert('Transaction ID or Document ID is missing');
      return;
    }

    setViewingDocumentId(documentId);

    try {
      const firebaseToken = await getFirebaseToken();

      console.log('Downloading document:', documentId);

      const downloadUrl = doc._sharedWith
        ? API_ENDPOINTS.downloadSharedDocument(transaction.id, doc.envelopeId, doc._envelopeDocumentId || documentId)
        : API_ENDPOINTS.downloadDocument(transaction.id, documentId);

      const response = await apiClient.get(
        downloadUrl,
        {
          headers: {
            'Authorization': `Bearer ${firebaseToken}`,
          },
          responseType: 'blob'
        }
      );

      console.log('Download response:', response);

      // Create a blob URL from the response
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);

      // Open in PDF viewer modal
      setPdfDocumentName(doc.name || doc.title || 'Document');
      setPdfSummaryDoc({ doc, area: 'forms' });
      setPdfBlobUrl(blobUrl);
      setShowPdfViewer(true);

    } catch (error) {
      console.error('Error downloading document:', error);
      console.error('Error details:', error.response?.data || error.message);
      alert(error.response?.data?.message || 'Failed to download document. Please try again.');
    } finally {
      setViewingDocumentId(null);
    }
  };

  // Close PDF viewer modal and clean up blob URL
  const handleClosePdfViewer = () => {
    setShowPdfViewer(false);
    if (pdfBlobUrl) {
      window.URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
    setPdfDocumentName('');
    setPdfSummaryDoc(null);
  };

  // Handle Review Document click - create fill session and open in modal
  // Colorado (FormService): open a field editor to correct auto-filled values, then re-generate the PDF.
  const handleEditColoradoForm = (doc, e) => {
    if (e) e.stopPropagation();
    // Open the in-browser PDF editor against the editable (unflattened) GCS copy.
    setEditingCoPdf({
      packageId: doc._packageId,
      instanceId: doc.id || doc.documentId,
      name: doc.name || doc.title || 'Colorado Form',
    });
  };

  const handleSaveColoradoOverrides = async () => {
    if (!editingCoForm) return;
    const instanceId = editingCoForm.id || editingCoForm.documentId;
    const packageId = editingCoForm._packageId;
    setIsSavingCoForm(true);
    try {
      const firebaseToken = await getFirebaseToken();
      await apiClient.patch(
        API_ENDPOINTS.formsApplyOverrides(packageId, instanceId),
        { overrides: editCoFields },
        { headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' } }
      );
      setEditingCoForm(null);
      await fetchDocuments(); // reflect the re-generated PDF / updated coverage
    } catch (error) {
      console.error('Error saving Colorado form overrides:', error);
      alert(error.response?.data?.message || 'Failed to save changes. Please try again.');
    } finally {
      setIsSavingCoForm(false);
    }
  };

  const handleReviewDocument = async (doc, e) => {
    e.stopPropagation();

    const documentId = doc.id || doc.documentId;
    const formId = doc.docuSignFormId;

    if (!transaction?.id || !documentId) {
      alert('Transaction ID or Document ID is missing');
      return;
    }

    if (!formId) {
      alert('DocuSign Form ID is missing for this document');
      return;
    }

    setReviewingDocumentId(documentId);

    try {
      const firebaseToken = await getFirebaseToken();

      console.log('Creating fill session for document:', documentId, 'with formId:', formId);

      const response = await apiClient.post(
        API_ENDPOINTS.documentFillSession(transaction.id, documentId),
        { formId },
        {
          headers: {
            'Authorization': `Bearer ${firebaseToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('Fill session response:', response.data);

      // Handle response - typically returns a URL to open
      const responseData = response.data;
      const sessionUrl = responseData.url || responseData.sessionUrl || responseData.redirectUrl || responseData.fillUrl || responseData.data?.url;

      if (sessionUrl) {
        // Open the fill session URL in a centered popup window
        const width = Math.min(1100, window.screen.width - 100);
        const height = Math.min(750, window.screen.height - 100);
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;
        window.open(
          sessionUrl,
          'ReviewDocument',
          `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,toolbar=no,menubar=no,location=no,status=no`
        );
      } else {
        console.log('Fill session created:', responseData);
        alert('Fill session created successfully. Please check the response for details.');
      }

    } catch (error) {
      console.error('Error creating fill session:', error);
      console.error('Error details:', error.response?.data || error.message);
      alert(error.response?.data?.message || 'Failed to create fill session. Please try again.');
    } finally {
      setReviewingDocumentId(null);
    }
  };


  // Handle Send for Signature button click - open modal and fetch participants
  const handleSendForSignature = async (doc, e) => {
    e.stopPropagation();

    if (!transaction?.id) {
      alert('Transaction ID is missing');
      return;
    }

    // Guard against a second click while the readiness check below is still in flight —
    // the kebab menu closes on click, so without this a double-tap could fire two checks.
    if (isPreparingSignature) return;

    // DocuSign connection is only required for Colorado — every other state's forms are
    // Lone Wolf-native (Lone Wolf docs never reach this function anyway — doc._loneWolf
    // routes to handleEditLoneWolfForm instead). Skip the readiness gate entirely for
    // non-Colorado rather than requiring a connection this send doesn't use.
    const isColoradoTransaction = (transaction?.propertyState || '').toUpperCase() === 'CO';

    if (isColoradoTransaction) {
      // DocuSign readiness gate — applies identically on dev/stg/prod. Check once here
      // before ever opening the modal, same pattern as handleAddFormClick. Shows the same
      // not-connected/pending/no-provider gate instead of letting the send attempt fail
      // later with a generic backend error.
      //
      // This gate is a backend round-trip, so show the preparing spinner overlay (the kebab
      // menu has already closed on click) and block re-entry until it resolves — mirrors the
      // isFetchingRoomId spinner on the Add Form flow. isPreparingSignature drives the overlay.
      setIsPreparingSignature(true);
      let firebaseToken;
      try {
        firebaseToken = await getFirebaseToken();
      } catch (err) {
        setIsPreparingSignature(false);
        alert('Authentication error. Please try again.');
        return;
      }
      // checkDocuSignReadiness never throws (it fails open internally), so no try needed.
      const { variant, roomsUserId } = await checkDocuSignReadiness(firebaseToken, _agentLicenseState);
      setIsPreparingSignature(false);
      if (variant) {
        triggerDocGate(variant, roomsUserId, 'sendForSignature');
        return;
      }
    }

    // Non-Colorado, Lone-Wolf-pinned transaction: a custom uploaded PDF (Transaction Documents
    // tab) goes through Lone Wolf instead of DocuSign, but reuses this exact same
    // recipient-selection dialog — same as DocuSign's, per explicit product requirement. Lone
    // Wolf's OWN forms never reach this function at all (they use
    // handleLoneWolfSendForSignature from the kebab menu directly), so by construction the
    // only doc type that can land here on a Lone-Wolf-pinned transaction is a genuine custom
    // PDF — the isDynamic/_formService check below is just defensive. Whatever the agent
    // selects here is still re-validated server-side against this transaction's real Lone Wolf
    // Contacts before anything is sent — never an arbitrary/made-up participant.
    const isCustomPdfDoc = !doc.isDynamic && !doc._formService;
    if (!isColoradoTransaction && transaction?.esignatureProvider === 'LONEWOLF' && isCustomPdfDoc) {
      doc = { ...doc, _loneWolfDoc: true };
    }

    setSignatureDocument(doc);
    setShowSignatureModal(true);

    // Pre-populate immediately from already-loaded participants prop so data shows at once
    const initialParticipants = filterParticipantsForSignature(participants, currentUserRole);
    setSignatureParticipants(initialParticipants);
    setSelectedParticipantIds(new Set()); // none pre-selected — user picks recipients manually
    setIsLoadingParticipants(initialParticipants.length === 0); // only show spinner if prop is also empty

    try {
      const firebaseToken = await getFirebaseToken();

      console.log('Fetching participants for transaction:', transaction.id);

      const response = await apiClient.get(
        API_ENDPOINTS.transactionParticipants(transaction.id),
        {
          headers: {
            'Authorization': `Bearer ${firebaseToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('Participants fetched:', response.data);

      // Handle different API response formats
      const data = response.data;
      let participantsList = [];
      if (Array.isArray(data)) {
        participantsList = data;
      } else if (data && Array.isArray(data.data)) {
        participantsList = data.data;
      } else if (data && Array.isArray(data.participants)) {
        participantsList = data.participants;
      } else if (data && Array.isArray(data.content)) {
        participantsList = data.content;
      } else if (data && Array.isArray(data.items)) {
        participantsList = data.items;
      } else if (data && Array.isArray(data.list)) {
        participantsList = data.list;
      }

      const filteredParticipants = filterParticipantsForSignature(participantsList, currentUserRole);

      // Use fresh data if available, otherwise keep the prop-populated data
      if (filteredParticipants.length > 0) {
        setSignatureParticipants(filteredParticipants);
        // Keep selection empty here too — the fresh fetch just refines the participant list,
        // it shouldn't silently re-select everyone if the user already unchecked some.
      }
      // If fresh fetch returned empty but prop had data, prop data stays displayed
    } catch (error) {
      console.error('Error fetching participants:', error);
      // If we already have prop data showing, don't close the modal — just log the error
      if (initialParticipants.length === 0) {
        alert('Failed to load participants. Please try again.');
        setShowSignatureModal(false);
      }
    } finally {
      setIsLoadingParticipants(false);
    }
  };

  const handleToggleParticipant = (participantKey) => {
    setSelectedParticipantIds(prev => {
      const next = new Set(prev);
      if (next.has(participantKey)) {
        next.delete(participantKey);
      } else {
        next.add(participantKey);
      }
      return next;
    });
  };

  const getSenderReturnUrl = (documentId) => {
    const params = new URLSearchParams({
      txId: String(transaction.id),
      documentId: String(documentId),
    });
    return `${window.location.origin}/docusign-sender-callback?${params.toString()}`;
  };

  const openSenderViewPopup = (url) => {
    window.open(url, '_blank');
  };

  const handleResumeTagging = async (doc, e) => {
    e.stopPropagation();

    const envelopeId = doc.envelopeId;
    const documentId = doc.id || doc.documentId;
    if (!transaction?.id || !documentId || !envelopeId) {
      showNotification('error', 'Cannot Resume', 'Envelope information is missing for this document.');
      return;
    }

    // DocuSign readiness gate — Resume Tagging only ever applies to a DocuSign envelope, so
    // a Lone-Wolf-pinned transaction (which never creates one) must skip this entirely rather
    // than requiring a DocuSign connection this path doesn't use. Applies identically on
    // dev/stg/prod otherwise. Check first rather than letting the call fail with a generic error.
    if (transaction?.esignatureProvider !== 'LONEWOLF') {
      let readinessToken;
      try {
        readinessToken = await getFirebaseToken();
      } catch (err) {
        alert('Authentication error. Please try again.');
        return;
      }
      const { variant: resumeVariant, roomsUserId: resumeRoomsUserId } = await checkDocuSignReadiness(readinessToken, _agentLicenseState);
      if (resumeVariant) {
        triggerDocGate(resumeVariant, resumeRoomsUserId, 'sendForSignature');
        return;
      }
    }

    try {
      const firebaseToken = await getFirebaseToken();
      const response = await apiClient.post(
        API_ENDPOINTS.refreshSenderView(envelopeId),
        { returnUrl: getSenderReturnUrl(documentId) },
        {
          headers: {
            'Authorization': `Bearer ${firebaseToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const senderViewUrl = response.data?.url;
      if (!senderViewUrl) {
        throw new Error('Missing sender view URL');
      }

      openSenderViewPopup(senderViewUrl);
    } catch (error) {
      console.error('Error resuming sender view:', error);
      showNotification(
        'error',
        'Resume Failed',
        error.response?.data?.message || 'Failed to resume tagging. Please try again.'
      );
    }
  };

  // Resume tagging for a Transaction Documents (GCS) PDF whose envelope is still
  // DRAFT_PENDING_TAGGING — refreshes the one-time Sender View URL and reopens it.
  const handleResumeGcsTagging = async (doc, e) => {
    e.stopPropagation();
    const documentId = doc.documentId || doc.id;
    if (!transaction?.id || !documentId) {
      showNotification('error', 'Cannot Resume', 'Document information is missing.');
      return;
    }

    // DocuSign readiness gate — same reasoning as handleResumeTagging above: skip entirely for
    // a Lone-Wolf-pinned transaction, since this GCS resume-tagging path is DocuSign-only.
    // Applies identically on dev/stg/prod otherwise.
    if (transaction?.esignatureProvider !== 'LONEWOLF') {
      let readinessToken;
      try {
        readinessToken = await getFirebaseToken();
      } catch (err) {
        alert('Authentication error. Please try again.');
        return;
      }
      const { variant: resumeVariant, roomsUserId: resumeRoomsUserId } = await checkDocuSignReadiness(readinessToken, _agentLicenseState);
      if (resumeVariant) {
        triggerDocGate(resumeVariant, resumeRoomsUserId, 'sendForSignature');
        return;
      }
    }

    try {
      const firebaseToken = await getFirebaseToken();
      const response = await apiClient.post(
        API_ENDPOINTS.refreshTransactionFileSenderView(transaction.id, documentId),
        { returnUrl: getSenderReturnUrl(documentId) },
        { headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' } }
      );
      const senderViewUrl = response.data?.senderViewUrl;
      if (!senderViewUrl) {
        throw new Error('Missing sender view URL');
      }
      openSenderViewPopup(senderViewUrl);
    } catch (error) {
      console.error('Error resuming GCS document sender view:', error);
      showNotification('error', 'Resume Failed', error.response?.data?.message || 'Failed to resume tagging. Please try again.');
    }
  };

  // Colorado forms — tries the automatic-tab-placement flow first (one call: TransactionService
  // fetches the PDF, matches signers to the form's known signer roles from its tab-position CSV,
  // creates the envelope, places signature/date tabs, and sends — no DocuSign popup at all). Only
  // falls back to the manual draft-from-pdf + Sender View flow when the backend explicitly says
  // it can't (422 — form has no tab data yet, or none of the signers matched a role on it). Any
  // other failure is a real error and is shown as-is, not silently retried a different way.
  // Transaction Documents (GCS) PDF — Room-free: backend fetches the file's own bytes and
  // creates the draft envelope directly, same as Colorado's form flow but for any uploaded
  // PDF. Returns a Sender View URL since (like any custom PDF) it has no pre-placed tabs.
  const sendGcsDocumentForSignature = async (signers, documentName) => {
    const documentId = signatureDocument.documentId || signatureDocument.id;
    if (!documentId) {
      showNotification('error', 'Error', 'Document ID is missing');
      return;
    }
    setIsSendingEnvelope(true);
    try {
      const firebaseToken = await getFirebaseToken();
      const returnUrl = getSenderReturnUrl(documentId);
      const response = await apiClient.post(
        API_ENDPOINTS.sendTransactionFileForSignature(transaction.id, documentId),
        { signers, emailSubject: `Please sign: ${documentName}`, returnUrl },
        { headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' }, timeout: 120000 },
      );

      setShowSignatureModal(false);
      setSignatureDocument(null);
      setSignatureParticipants([]);
      setSelectedParticipantIds(new Set());

      const senderViewUrl = response.data?.senderViewUrl;
      if (senderViewUrl) {
        openSenderViewPopup(senderViewUrl);
        showNotification('success', 'Tag Before Sending',
          `Add signer tabs for "${documentName}" in DocuSign, then click Send there.`);
      }
      await fetchGcsDocuments();
    } catch (error) {
      console.error('Error sending GCS document for signature:', error);
      const status = error.response?.status;
      const msg = status === 400
        ? 'Only PDF documents can be sent for signature.'
        : status === 409
          ? 'This document has already been sent or completed.'
          : (error.response?.data?.message || error.response?.data?.error || 'Failed to send for signature. Please try again.');
      showNotification('error', 'Failed to Send', msg);
    } finally {
      setIsSendingEnvelope(false);
    }
  };

  // Non-Colorado custom PDF -> Lone Wolf. Recipients come from this same recipient-selection
  // dialog (Rialty's own transaction participants) — the backend re-validates each selected
  // email against this transaction's real Lone Wolf Contacts and silently drops any that don't
  // match, so a participant who isn't yet a Lone Wolf contact just won't receive anything.
  const sendLoneWolfDocumentForSignature = async (signers, documentName) => {
    const documentId = signatureDocument.documentId || signatureDocument.id;
    if (!documentId) {
      showNotification('error', 'Error', 'Document ID is missing');
      return;
    }
    setIsSendingEnvelope(true);
    try {
      const firebaseToken = await getFirebaseToken();
      const redirectUrl = `${window.location.origin}/lonewolf-signing-callback?txId=${transaction.id}&documentId=${documentId}`;
      const response = await apiClient.post(
        API_ENDPOINTS.lonewolfSendGcsDocumentForSignature(transaction.id, documentId),
        signers.map(s => s.email),
        { headers: { 'Authorization': `Bearer ${firebaseToken}` }, params: { redirectUrl } }
      );

      setShowSignatureModal(false);
      setSignatureDocument(null);
      setSignatureParticipants([]);
      setSelectedParticipantIds(new Set());

      const ssoUrl = response.data?.ssoUrl;
      if (ssoUrl) {
        window.open(ssoUrl, '_blank', 'noopener,noreferrer');
        showNotification('success', 'Sent Successfully!',
          `"${documentName}" has been sent to ${signers.length} recipient${signers.length > 1 ? 's' : ''} for signature.`);
      } else {
        showNotification('error', 'Send Failed', 'Lone Wolf did not return a signing link.');
      }
      await fetchGcsDocuments();
      await fetchLonewolfDocumentSignings();
    } catch (error) {
      console.error('Error sending document for signature via Lone Wolf:', error);
      showNotification('error', 'Failed to Send', error.response?.data?.error || 'Failed to send document for signature.');
    } finally {
      setIsSendingEnvelope(false);
    }
  };

  // TDS/SPQ form -> Lone Wolf. There's no stored PDF; the backend downloads the form's blank,
  // fills it from the completed wizard, and sends THOSE filled bytes for signature. Recipients
  // come from this same dialog and are re-validated server-side against the transaction's real
  // Lone Wolf contacts. Mirrors sendLoneWolfDocumentForSignature, keyed by the form's versionId.
  const sendLoneWolfFormForSignature = async (signers, documentName) => {
    const versionId = signatureDocument._versionId;
    if (!versionId) {
      showNotification('error', 'Error', 'Form version is missing');
      return;
    }
    setIsSendingEnvelope(true);
    try {
      const firebaseToken = await getFirebaseToken();
      const redirectUrl = `${window.location.origin}/lonewolf-signing-callback?txId=${transaction.id}&versionId=${versionId}`;
      const response = await apiClient.post(
        API_ENDPOINTS.lonewolfSendFormForSignature(transaction.id, versionId),
        signers.map(s => s.email),
        { headers: { 'Authorization': `Bearer ${firebaseToken}` }, params: { redirectUrl } }
      );

      setShowSignatureModal(false);
      setSignatureDocument(null);
      setSignatureParticipants([]);
      setSelectedParticipantIds(new Set());

      const ssoUrl = response.data?.ssoUrl;
      if (ssoUrl) {
        window.open(ssoUrl, '_blank', 'noopener,noreferrer');
        showNotification('success', 'Sent Successfully!',
          `"${documentName}" has been sent to ${signers.length} recipient${signers.length > 1 ? 's' : ''} for signature.`);
      } else {
        showNotification('error', 'Send Failed', 'Lone Wolf did not return a signing link.');
      }
      await fetchLonewolfDocumentSignings();
    } catch (error) {
      console.error('Error sending TDS/SPQ form for signature via Lone Wolf:', error);
      showNotification('error', 'Failed to Send', error.response?.data?.error || 'Failed to send form for signature.');
    } finally {
      setIsSendingEnvelope(false);
    }
  };

  const sendColoradoFormForSignature = async (signers, documentName) => {
    const packageId = signatureDocument._packageId;
    const instanceId = signatureDocument.id || signatureDocument.documentId;
    if (!packageId || !instanceId) {
      showNotification('error', 'Error', 'Form package information is missing');
      return;
    }
    setIsSendingEnvelope(true);
    try {
      const firebaseToken = await getFirebaseToken();
      const autoResp = await apiClient.post(
        API_ENDPOINTS.coloradoAutoSendForSignature(packageId, instanceId),
        { signers, interestedPropertyId: getCurrentInterestedPropertyId() },
        { headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' }, timeout: 60000 },
      );

      setShowSignatureModal(false);
      setSignatureDocument(null);
      setSignatureParticipants([]);
      setSelectedParticipantIds(new Set());
      showNotification('success', 'Sent for Signature',
        `"${documentName}" was sent to ${autoResp.data?.recipientsMatched || signers.length} recipient(s) — signature boxes were placed automatically.`);
      await fetchDocuments();
      setIsSendingEnvelope(false);
      return;
    } catch (error) {
      if (error.response?.status !== 422) {
        console.error('Error sending Colorado form for signature (auto-tabs):', error);
        const status = error.response?.status;
        const msg = status === 401
          ? 'Connect your DocuSign account first, then try again.'
          : (error.response?.data?.error || error.response?.data?.message || error.message
            || 'Failed to send for signature. Please try again.');
        showNotification('error', 'Failed to Send', msg);
        setIsSendingEnvelope(false);
        return;
      }
      // 422 — this specific form has no tab data yet, or none of the signers matched a role on
      // it. Fall back to the manual flow below rather than failing outright.
      console.info('[Colorado send] auto-tabs not available for this form, falling back to manual tagging:', error.response?.data?.error);
    }
    await sendColoradoFormForSignatureManual(signers, documentName, packageId, instanceId);
  };

  const sendColoradoFormForSignatureManual = async (signers, documentName, packageId, instanceId) => {
    try {
      const firebaseToken = await getFirebaseToken();

      // 1) fetch the flattened, read-only view PDF and base64-encode it
      const pdfResp = await apiClient.get(API_ENDPOINTS.formsDownloadPdf(packageId, instanceId), {
        headers: { 'Authorization': `Bearer ${firebaseToken}` },
        responseType: 'arraybuffer',
      });
      const bytes = new Uint8Array(pdfResp.data);
      let binary = '';
      const CHUNK = 0x8000;
      for (let i = 0; i < bytes.length; i += CHUNK) {
        binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
      }
      const documentBase64 = window.btoa(binary);

      // 2) create the draft envelope from the PDF bytes (no room)
      const draftResp = await apiClient.post(
        API_ENDPOINTS.envelopeDraftFromPdf,
        {
          documentBase64,
          fileName: `${documentName || 'form'}.pdf`,
          emailSubject: `Please sign: ${documentName}`,
          signers,
        },
        { headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' }, timeout: 120000 },
      );
      const envelopeId = draftResp.data?.envelopeId;
      if (!envelopeId) throw new Error('Draft envelope created without an envelopeId');

      // 3) record the envelope on the form instance + lock it from further edits (status → SENT).
      // Do not continue to Sender View if this association fails; otherwise DocuSign can send an
      // envelope while the Rialty form incorrectly remains editable and displays as Draft.
      try {
        await apiClient.patch(
          API_ENDPOINTS.formsMarkSent(packageId, instanceId),
          { envelopeId, interestedPropertyId: getCurrentInterestedPropertyId() },
          { headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' } },
        );
      } catch (markErr) {
        console.error('[Colorado send] could not record envelope on form instance:', markErr);
        throw new Error(
          markErr.response?.data?.message
            || 'The DocuSign draft was created, but the form status could not be updated.'
        );
      }

      // 4) one-time Sender View URL so the agent places tabs + sends in DocuSign
      const svResp = await apiClient.post(
        API_ENDPOINTS.refreshSenderView(envelopeId),
        { returnUrl: getSenderReturnUrl(instanceId) },
        { headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' } },
      );
      const senderViewUrl = svResp.data?.url;

      setShowSignatureModal(false);
      setSignatureDocument(null);
      setSignatureParticipants([]);
      setSelectedParticipantIds(new Set());

      if (senderViewUrl) {
        openSenderViewPopup(senderViewUrl);
        showNotification('success', 'Tag Before Sending',
          `Add signer tabs for "${documentName}" in DocuSign, then click Send there.`);
      } else {
        showNotification('success', 'Draft Created', `A signature draft was created for "${documentName}".`);
      }
      await fetchDocuments();
    } catch (error) {
      console.error('Error sending Colorado form for signature:', error);
      const status = error.response?.status;
      const msg = status === 401
        ? 'Connect your DocuSign account first, then try again.'
        : (error.response?.data?.message
          || error.response?.data?.error
          || error.message
          || 'Failed to send for signature. Please try again.');
      showNotification('error', 'Failed to Send', msg);
    } finally {
      setIsSendingEnvelope(false);
    }
  };

  // Handle sending envelope for signature
  const handleSendEnvelope = async () => {
    if (!transaction?.id || !signatureDocument) {
      showNotification('error', 'Error', 'Transaction or document information is missing');
      return;
    }

    const documentId = signatureDocument.id || signatureDocument.documentId;
    const documentName = signatureDocument.name || signatureDocument.title || 'Document';

    if (!documentId) {
      showNotification('error', 'Error', 'Document ID is missing');
      return;
    }

    // Prepare signers — only checked participants with a valid email.
    // routingOrder is assigned by list position (1st checked = "1", 2nd = "2", …)
    // so the agent controls signing sequence simply by ordering the recipients in the modal.
    const signers = signatureParticipants
      .filter(p => p.email && p.email.includes('@') && selectedParticipantIds.has(p.id || p.email))
      .map((p, idx) => ({
        email: p.email,
        name: `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.name || p.email,
        role: p.role,
        roleName: p.role,
        routingOrder: String(idx + 1),
      }));

    if (signers.length === 0) {
      showNotification(
        'error',
        'No Recipients Selected',
        'Please select at least one recipient to send the document for signature.'
      );
      return;
    }

    // Colorado FormService forms are NOT in a DocuSign Room. Send the flattened view/ PDF
    // straight to DocuSign via /api/envelopes/draft-from-pdf (inline base64) — no room needed.
    if (signatureDocument._formService) {
      await sendColoradoFormForSignature(signers, documentName);
      return;
    }

    // TDS/SPQ Forms-tab form: send the wizard-FILLED PDF (built server-side) through Lone Wolf.
    // Checked before _loneWolfDoc since a form isn't a GCS document and has no documentId.
    if (signatureDocument._loneWolfFormSign) {
      await sendLoneWolfFormForSignature(signers, documentName);
      return;
    }

    // Non-Colorado custom PDF, Lone-Wolf-pinned transaction.
    if (signatureDocument._loneWolfDoc) {
      await sendLoneWolfDocumentForSignature(signers, documentName);
      return;
    }

    // Transaction Documents (GCS) PDFs — Room-free, backend builds the envelope directly
    // from the file's own bytes via TransactionService's own sendForSignature endpoint.
    if (signatureDocument._isGcsDoc) {
      await sendGcsDocumentForSignature(signers, documentName);
      return;
    }

    setIsSendingEnvelope(true);

    try {
      const firebaseToken = await getFirebaseToken();

      const emailSubject = `Please sign: ${documentName}`;
      const returnUrl = getSenderReturnUrl(documentId);

      console.log('Creating envelope for document:', documentId);
      console.log('Signers:', signers);
      console.log('Email subject:', emailSubject);

      const response = await apiClient.post(
        API_ENDPOINTS.createEnvelopeSenderView(transaction.id, documentId),
        { signers, emailSubject, returnUrl, interestedPropertyId: getCurrentInterestedPropertyId() },
        {
          headers: {
            'Authorization': `Bearer ${firebaseToken}`,
            'Content-Type': 'application/json'
          },
          timeout: 120000
        }
      );

      console.log('Envelope created:', response.data);

      // Close signature modal
      setShowSignatureModal(false);
      setSignatureDocument(null);
      setSignatureParticipants([]);
      setSelectedParticipantIds(new Set());

      const senderViewUrl = response.data?.senderViewUrl;

      if (senderViewUrl) {
        openSenderViewPopup(senderViewUrl);
        // Custom PDFs have no pre-placed tabs — agent must tag before sending.
        // DocuSign form library docs have tabs pre-placed by the form template — agent just reviews and sends.
        const isCustomPdf = !signatureDocument?.isDynamic && !signatureDocument?.formId;
        if (isCustomPdf) {
          showNotification(
            'success',
            'Tag Before Sending',
            `Add signer tabs for "${documentName}" in DocuSign, then click Send there.`
          );
        } else {
          showNotification(
            'success',
            'Review and Send in DocuSign',
            `Review "${documentName}" in DocuSign and click Send when ready.`
          );
        }
      } else {
        showNotification(
          'success',
          'Sent Successfully!',
          `"${documentName}" has been sent to ${signers.length} recipient${signers.length > 1 ? 's' : ''} for signature.`
        );
      }

      // Refresh documents list to update status
      await fetchDocuments();

    } catch (error) {
      console.error('Error creating envelope:', error);
      console.error('Error details:', error.response?.data || error.message);
      showNotification(
        'error',
        'Failed to Send',
        error.response?.data?.message || 'Failed to send document for signature. Please try again.'
      );
    } finally {
      setIsSendingEnvelope(false);
    }
  };

  useEffect(() => {
    const handleSenderViewMessage = async (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== 'DOCUSIGN_SENDER_CALLBACK') return;

      const callbackEvent = (event.data?.event || '').toLowerCase();
      if (callbackEvent === 'send') {
        showNotification('success', 'Sent for Signature', 'The DocuSign envelope was sent successfully.');
      } else if (callbackEvent === 'save') {
        showNotification('success', 'Draft Saved', 'The envelope draft was saved. You can resume tagging later.');
      } else if (callbackEvent === 'ttl_expired' || callbackEvent === 'session_timeout') {
        showNotification('error', 'Session Expired', 'The DocuSign session expired. Click Resume Tagging to continue.');
      }

      try {
        // Shared popup for both the Forms tab (DocuSign Rooms custom PDFs) and the
        // Transaction Documents tab (GCS PDFs) — refresh both lists rather than trying
        // to track which tab's document opened this particular popup.
        await Promise.all([fetchDocuments(), fetchGcsDocuments()]);
      } catch (error) {
        console.error('Failed to refresh documents after sender callback:', error);
      }
    };

    window.addEventListener('message', handleSenderViewMessage);
    return () => {
      window.removeEventListener('message', handleSenderViewMessage);
    };
  }, [fetchDocuments, fetchGcsDocuments]);

  // Close signature modal
  const handleCloseSignatureModal = () => {
    if (!isSendingEnvelope) {
      setShowSignatureModal(false);
      setSignatureDocument(null);
      setSignatureParticipants([]);
      setSelectedParticipantIds(new Set());
    }
  };

  // Handle Delete Document — DocuSign docs (agents only, non-signed, non-dynamic, no envelope)
  // Mirrors mobile: documentService.deleteDocuSignDocument(transactionId, doc.documentId)
  const handleDeleteDocument = async (doc, e) => {
    e.stopPropagation();

    const docStatus = doc.status?.toLowerCase();
    const isSigned = doc.isSigned || docStatus === 'signed' || docStatus === 'completed';

    if (isSigned || doc.envelopeId) {
      showNotification('error', 'Cannot Delete', 'This document has been sent for signature and cannot be deleted.');
      return;
    }
    if (doc.isDynamic) {
      showNotification('error', 'Cannot Delete Form', 'Form-based documents cannot be deleted from DocuSign. You can archive it instead or create a new document.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${doc.name || 'this document'}"? This action cannot be undone.`)) return;

    // Use doc.documentId exclusively — mirrors mobile's doc.documentId usage.
    // doc.id may be a DB primary key (different from the DocuSign room document ID).
    const documentId = doc.documentId;
    if (!transaction?.id || !documentId) return;

    try {
      const firebaseToken = await getFirebaseToken();
      await apiClient.delete(
        API_ENDPOINTS.deleteDocusignDocument(transaction.id, documentId),
        { headers: { 'Authorization': `Bearer ${firebaseToken}` } }
      );
      showNotification('success', 'Deleted', 'Document deleted successfully.');
      await fetchDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      // Backend returns { error: "..." }, not { message: "..." } — mirrors mobile's data?.error read
      const msg = error.response?.data?.error || error.response?.data?.message || error.message || '';
      if (msg.toLowerCase().includes('signature') || msg.toLowerCase().includes('envelope')) {
        showNotification('error', 'Cannot Delete', 'This document has been sent for signature and cannot be deleted.');
      } else if (msg.toLowerCase().includes('not found')) {
        // Doc no longer exists in DocuSign (e.g. form-based) — refresh list like mobile does
        showNotification('error', 'Cannot Delete', 'This document cannot be deleted from DocuSign. It may be a form-based document.');
        await fetchDocuments();
      } else {
        showNotification('error', 'Delete Failed', 'Unable to delete this document. It may be in use or already sent for signature.');
      }
    }
  };

  // Handle Delete GCS Document — mirrors mobile's handleGcsDelete → documentService.deleteGcsFile
  // Opens the custom-designed confirmation popup instead of window.confirm.
  const handleGcsDelete = (doc, e) => {
    e.stopPropagation();
    setDeleteConfirmDoc(doc);
    setShowDeleteConfirm(true);
  };

  const closeDeleteConfirm = () => {
    if (isDeletingGcsFile) return;
    setShowDeleteConfirm(false);
    setDeleteConfirmDoc(null);
  };

  const confirmGcsDelete = async () => {
    const doc = deleteConfirmDoc;
    if (!doc) return;

    const documentId = doc.documentId;
    if (!transaction?.id || !documentId) return;

    setIsDeletingGcsFile(true);
    try {
      const firebaseToken = await getFirebaseToken();
      await apiClient.delete(
        API_ENDPOINTS.deleteTransactionFile(transaction.id, documentId),
        { headers: { 'Authorization': `Bearer ${firebaseToken}` } }
      );

      // Cascade delete: if this was a published CMA report, also remove its history
      // entry so the Timeline's "Generated Reports" list doesn't show a dangling PDF.
      // Mirrors mobile DocsTab.tsx's deleteReportByPublishedFilename. Best-effort —
      // the GCS file is already gone, so a failure here shouldn't block the UI.
      if (isCmaFileName(doc.fileName)) {
        try {
          const reportDay = parseCmaReportDate(doc.fileName);
          const address = buildCmaAddress(transaction);
          if (reportDay && address) {
            const historyResp = await apiClient.get(API_ENDPOINTS.cmaHistory, {
              headers: { 'Authorization': `Bearer ${firebaseToken}` },
              params: { address, transactionId: transaction.id },
            });
            const history = Array.isArray(historyResp.data) ? historyResp.data : [];
            const match = history.find(r => formatCmaReportDay(r.createdAt) === reportDay);
            if (match) {
              await apiClient.delete(
                API_ENDPOINTS.cmaReportById(match.reportId),
                { headers: { 'Authorization': `Bearer ${firebaseToken}` } }
              );
            }
          }
        } catch (cascadeError) {
          console.error('Error cascading CMA report delete:', cascadeError);
        }
      }

      showNotification('success', 'Deleted', 'Document deleted successfully.');
      await fetchGcsDocuments();
      setShowDeleteConfirm(false);
      setDeleteConfirmDoc(null);
    } catch (error) {
      console.error('Error deleting GCS file:', error);
      showNotification('error', 'Delete Failed', 'Failed to delete document. Please try again.');
    } finally {
      setIsDeletingGcsFile(false);
    }
  };

  // Handle View GCS Document — same PDF-viewer-modal pattern as handleViewDocument
  // above (Forms tab), just pointed at the GCS file endpoint instead of the
  // DocuSign download endpoints. Lets buyers/sellers preview an uploaded document
  // in-app instead of only being able to download it.
  const handleViewGcsDocument = async (doc, e) => {
    if (e) e.stopPropagation();
    const documentId = doc.documentId;
    if (!transaction?.id || !documentId) return;

    setViewingDocumentId(documentId);
    try {
      const firebaseToken = await getFirebaseToken();
      const response = await apiClient.get(
        API_ENDPOINTS.downloadTransactionFile(transaction.id, documentId),
        {
          headers: { 'Authorization': `Bearer ${firebaseToken}` },
          responseType: 'blob'
        }
      );
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      setPdfDocumentName(displayFileName(doc.fileName) || doc.name || 'Document');
      setPdfSummaryDoc({ doc, area: 'documents' });
      setPdfBlobUrl(blobUrl);
      setShowPdfViewer(true);
    } catch (error) {
      console.error('Error viewing document:', error);
      showNotification('error', 'View Failed', 'Failed to open document. Please try again.');
    } finally {
      setViewingDocumentId(null);
    }
  };

  // Handle Download GCS Document
  // Mirrors mobile: GET /api/v1/transactions/{txId}/files/{docId}/download with auth header
  // Web: fetches as blob + triggers browser download (no local filesystem like mobile)
  const handleGcsDownload = async (doc, e) => {
    e.stopPropagation();
    const documentId = doc.documentId;
    if (!transaction?.id || !documentId) return;

    setDownloadingDocId(documentId);
    try {
      const firebaseToken = await getFirebaseToken();
      const response = await fetch(
        API_ENDPOINTS.downloadTransactionFile(transaction.id, documentId),
        { headers: { 'Authorization': `Bearer ${firebaseToken}` } }
      );
      if (!response.ok) throw new Error(`Download failed (${response.status})`);

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.fileName || doc.name || 'document';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      showNotification('error', 'Download Failed', 'Failed to download file. Please try again.');
    } finally {
      setDownloadingDocId(null);
    }
  };

  // Open Share modal — mirrors mobile's setShowShareModal(true) on doc selection
  const openShareModal = (doc, e) => {
    e.stopPropagation();
    setShareModalDoc(doc);
    setShareRecipientIds(new Set());
    setShowRecipientDropdown(false);
    setRecipientSearch('');
    setShareMessage('');
    setShowShareModal(true);
  };

  const closeShareModal = () => {
    setShowShareModal(false);
    setShareModalDoc(null);
    setShareRecipientIds(new Set());
    setShowRecipientDropdown(false);
    setRecipientSearch('');
    setShareMessage('');
  };

  // Handle Share GCS Document — mirrors mobile's handleGcsShare: resolve the selected
  // transaction parties to emails (ignoring anyone without a valid one), rather than
  // a free-text email field.
  // POST /api/v1/transactions/{txId}/files/{docId}/share
  // Payload: { toEmails, message, expiryHours: 48, shareType: 'email' }
  const handleSendShare = async () => {
    if (!shareModalDoc) return;

    const selectedRecipients = participants.filter(
      p => shareRecipientIds.has(getParticipantId(p)) && p.email && EMAIL_REGEX.test(p.email)
    );
    if (selectedRecipients.length === 0) {
      showNotification('error', 'Pick a recipient', 'Select at least one transaction party with a valid email address.');
      return;
    }

    const documentId = shareModalDoc.documentId;
    if (!transaction?.id || !documentId) return;

    setIsSharingFile(true);
    try {
      const firebaseToken = await getFirebaseToken();
      await apiClient.post(
        API_ENDPOINTS.shareTransactionFile(transaction.id, documentId),
        {
          toEmails: selectedRecipients.map(p => p.email.trim()),
          message: shareMessage.trim() || undefined,
          expiryHours: 48,
          shareType: 'email',
        },
        { headers: { 'Authorization': `Bearer ${firebaseToken}` } }
      );
      const recipientNames = selectedRecipients.length === 1
        ? getParticipantName(selectedRecipients[0])
        : `${selectedRecipients.length} recipients`;
      closeShareModal();
      showNotification('success', 'Shared!', `File shared with ${recipientNames}. Link expires in 48 hours.`);
    } catch (error) {
      console.error('Share error:', error);
      showNotification('error', 'Share Failed', 'Failed to share file. Please try again.');
    } finally {
      setIsSharingFile(false);
    }
  };

  const docCounts = getDocCounts();
  const filteredForms = getFilteredForms();

  // Derive DocuSign-only list (filtered per role visibility, no GCS)
  const getVisibleForms = () => {

    // Build a set of current participant emails for signer filtering (agents only)
    const participantEmails = new Set(
      participants.map(p => (p.email || '').toLowerCase()).filter(Boolean)
    );

    if (isClient) {
      return documents.filter(doc => {
        // Colorado forms: only visible once the current user is an actual matched recipient on
        // the form's envelope, and that recipient has actually been emailed (sent/completed) —
        // a 'created' recipient hasn't been notified yet and must stay hidden. This replaces the
        // old blanket "any sent CO form on this transaction" check, which didn't verify the
        // viewer was actually a signer on that specific document.
        if (doc._formService) {
          return doc._isCurrentUserRecipient &&
            (doc._envelopeRecipientStatus === 'sent' || doc._envelopeRecipientStatus === 'completed');
        }
        const status = doc.status?.toLowerCase();
        if (doc._sharedWith) {
          // For shared-with docs, envelopeId is always set — use signerStatus-derived fields only
          return doc.isSigned || status === 'pending' || status === 'sent' ||
                 status === 'delivered' || status === 'signed' || status === 'completed';
        }
        return doc.envelopeId || doc.isSigned ||
               status === 'pending' || status === 'sent' ||
               status === 'delivered' || status === 'signed' || status === 'completed';
      });
    }

    // For agents: filter out envelopes whose signer is no longer a participant.
    // Mirrors mobile: shared-with endpoint only returns docs for current signers.
    // Safe no-op when the backend omits signer fields (recipientEmail/signerEmail/recipients).
    return documents.filter(doc => {
      // Exclude voided or declined envelopes regardless of role
      const status = doc.status?.toLowerCase();
      if (status === 'voided' || status === 'declined') return false;

      // If the document carries a single-signer email, verify they're still a participant
      const signerEmail = (doc.recipientEmail || doc.signerEmail || '').toLowerCase();
      if (signerEmail) {
        return participantEmails.size === 0 || participantEmails.has(signerEmail);
      }

      // If the document carries a recipients array, keep it only if at least one signer is current
      const recipients = doc.recipients || doc.signers;
      if (Array.isArray(recipients) && recipients.length > 0) {
        return participantEmails.size === 0 ||
          recipients.some(r => participantEmails.has((r.email || '').toLowerCase()));
      }

      return true; // No signer info available — show by default
    });
  };

  // Live envelope status wins over the stored FormService status, same priority as
  // getDocStatusLabel — keeps the "Signed" filter/count in sync with what the badge shows.
  const isFormSigned = (d) => {
    if (isClient) return d._envelopeRecipientStatus === 'completed' || d.isSigned || d.status === 'signed' || d.status === 'completed';
    if (d.envelopeId) return !!d._envelopeAllCompleted;
    return d.isSigned || d.status === 'signed' || d.status === 'completed';
  };

  const getFilteredForms2 = () => {
    const forms = getVisibleForms();
    if (docFilter === 'all') return forms;
    if (docFilter === 'signed') return forms.filter(isFormSigned);
    if (docFilter === 'draft') return forms.filter(d => !d.isSigned && (!d.status || d.status?.toLowerCase() === 'draft'));
    return forms.filter(d => d.status?.toLowerCase() === docFilter);
  };

  // Same file icon used by every doc card today — reused as-is in both the
  // row and icon-tile layouts below.
  const docFileIcon = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="13" y2="17"/>
    </svg>
  );

  // ── Dynamic document card list ──────────────────────────────────────────
  // Renders `docs` as full named rows (icon, name, subtitle, status, date,
  // kebab) at a constant, comfortable height (DYN_ROW_MAX) regardless of
  // count. The card caps at DYN_CARD_HEIGHT and scrolls internally
  // (.txn-doc-dyn-body's overflow-y:auto) once the rows outgrow it — a
  // previous version shrank rows (and eventually collapsed to an icon-only
  // grid) to force everything to fit without scrolling; that made anything
  // past ~12 documents in one folder illegible (no names/dates visible).
  // Text/status/kebab styling all reuse the existing txn-doc-* classes so
  // colors/fonts are unchanged — only the surrounding layout is new.
  const renderDynamicDocs = (docs, cfg) => {
    const rowHeight = DYN_ROW_MAX;

    const renderMenuWrap = (doc, key, extraWrapClass = '', extraBtnClass = '') => {
      const items = cfg.getMenuItems(doc, key);
      if (!items) return null;
      return (
        <div
          className={`txn-doc-kebab-wrap ${extraWrapClass}`.trim()}
          ref={openDocMenuId === key ? docMenuRef : null}
        >
          <button
            type="button"
            className={`txn-doc-kebab-btn ${extraBtnClass}`.trim()}
            aria-label="Document actions"
            onClick={(e) => openDocMenuAt(e, key)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/></svg>
          </button>
          {openDocMenuId === key && (
            <div
              className="txn-doc-kebab-menu"
              style={{ top: docMenuPos.top, left: docMenuPos.left }}
              onClick={(e) => e.stopPropagation()}
            >
              {items}
            </div>
          )}
        </div>
      );
    };

    return (
      <div className="txn-doc-dyn-body" style={{ maxHeight: DYN_CARD_HEIGHT }}>
        <div className="txn-doc-dyn-rows">
          {docs.map((doc, idx) => {
            const key = cfg.getKey(doc, idx);
            const isLoading = cfg.isLoading(doc);
            const isLastRow = idx === docs.length - 1;
            return (
              <React.Fragment key={key}>
              <div
                className={`txn-doc-dyn-row${isLastRow ? ' txn-doc-dyn-row-last' : ''}`}
                style={{ height: rowHeight }}
                onClick={(e) => cfg.onOpen(doc, e)}
              >
                <div className="txn-doc-dyn-icon">{cfg.icon}</div>
                <div className="txn-doc-dyn-main">
                  <div className="txn-doc-name">{cfg.getName(doc)}</div>
                  <div className="txn-doc-type">{cfg.getSubtitle(doc)}</div>
                </div>
                {isLoading ? (
                  <div className="txn-doc-meta-item"><span className="btn-spinner"></span><span>{cfg.getLoadingLabel ? cfg.getLoadingLabel(doc) : 'Loading...'}</span></div>
                ) : (
                  <div className="txn-doc-status" style={cfg.getStatusStyle(doc)}>{cfg.getStatusLabel(doc)}</div>
                )}
                <div className="txn-doc-meta-item txn-doc-dyn-date">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  <span>{cfg.getDate(doc)}</span>
                </div>
                {/* Notes — private to the viewer, attached to this document row.
                    Same record shown in My Notes — not a copy. Trigger (icon+badge)
                    stays inline here; the expanded toggle+stack portals into the
                    full-width block below the row (same portalTargetId pattern as
                    Timeline) instead of a `popover` flyout — the flyout clipped/
                    misaligned inside this list's own scroll container. */}
                <div onClick={(e) => e.stopPropagation()}>
                  <NotesPanel
                    linkType="DOCUMENT"
                    linkId={key}
                    linkLabel={cfg.getName(doc)}
                    transactionId={transaction?.id}
                    transactionName={transaction?.transactionName || transaction?.propertyAddress}
                    portalTargetId={`txn-doc-notes-target-${key}`}
                  />
                </div>
                {renderMenuWrap(doc, key)}
              </div>
              {/* Portal target for this row's Notes toggle+stack — see the
                  NotesPanel usage above. Empty by default; takes no space
                  unless this document has notes or the composer is open. */}
              <div id={`txn-doc-notes-target-${key}`} className="txn-notes-block" />
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );
  };

  // renderDynamicDocs renders one row per array entry (docs.map, not flatMap) — so any document
  // that needs more than one row (the Original+Envelope-versions split) has to be pre-flattened
  // into separate row objects before being handed to it. Custom PDFs and Colorado forms are
  // already one-row-per-entry (Colorado's Original/Envelope split happens at fetch time, in
  // fetchDocuments) and pass through unchanged; only CA DocuSign form-library docs still carry a
  // nested `versions` array that needs exploding here.
  const flattenFormRowsForRendering = (forms) => {
    return forms.flatMap((doc, index) => {
      const isCustomPdf = !doc.isDynamic && !doc.formId;
      if (isCustomPdf || doc._formService) return [doc];

      const docKey = doc.id || doc.documentId || index;
      const envelopeEntries = Array.isArray(doc.versions) && doc.versions.length > 0
        ? doc.versions
        : (doc.envelopeId ? [{
            version: 1,
            envelopeId: doc.envelopeId,
            status: doc.status,
            isSigned: doc.isSigned,
            createdAt: doc.createdDate || doc.date || doc.createdAt,
          }] : []);
      const showVersionNumbers = envelopeEntries.length > 1;

      const originalRow = { ...doc, _rowKey: `${docKey}-original`, _isOriginalRow: true, _isEnvelopeRow: false };
      const envelopeRows = envelopeEntries.map((entry) => ({
        ...doc,
        _rowKey: `${docKey}-envelope-${entry.version || entry.envelopeId}`,
        _isOriginalRow: false,
        _isEnvelopeRow: true,
        _showVersionNumber: showVersionNumbers,
        envelopeId: entry.envelopeId,
        version: entry.version,
        status: entry.status,
        isSigned: entry.isSigned,
        createdDate: entry.createdAt,
      }));
      return [originalRow, ...envelopeRows];
    });
  };

  return (
    <>
      <div className="txn-documents-area">

        {/* ── Tab bar + shared action buttons ─────────────────── */}
        <div className="docs-tab-header">
          <div className="docs-tab-bar">
            <button
              className={`docs-tab ${activeDocTab === 'transaction-documents' ? 'active' : ''}`}
              onClick={() => setActiveDocTab('transaction-documents')}
            >
              Transaction Documents
              <span className="docs-tab-badge">{gcsDocuments.length}</span>
            </button>
            <button
              className={`docs-tab ${activeDocTab === 'forms' ? 'active' : ''}`}
              onClick={() => setActiveDocTab('forms')}
            >
              Forms
              <span className="docs-tab-badge">{getVisibleForms().length}</span>
            </button>
          </div>

          <div className="docs-tab-actions">
            {activeDocTab === 'transaction-documents' && (
            <div
              className={`txn-upload-drop-target${isDocsDragging ? ' drag-over' : ''}`}
              ref={createUploadMenuRef}
              onDragOver={(e) => { e.preventDefault(); if (!isUploading) setIsDocsDragging(true); }}
              onDragLeave={() => setIsDocsDragging(false)}
              onDrop={handleDocsDrop}
              style={{ position: 'relative' }}
            >
              <input type="file" ref={uploadFileInputRef} style={{ display: 'none' }}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                multiple={isClient || isAgentOrBroker}
                onChange={handleFileInputChange}
              />
              {/* Folder-select picker — one folder per click (native OS "Select Folder"
                  dialogs don't support multi-selecting folders); clicking again while the
                  preview card is open adds another folder to the same pending batch, same
                  pattern as "Add more files". Dragging multiple folders at once is the
                  one-gesture alternative, handled by handleDocsDrop above. */}
              <input type="file" ref={uploadFolderInputRef} style={{ display: 'none' }}
                webkitdirectory="" directory="" multiple
                onChange={handleFolderInputChange}
              />
              <button
                type="button"
                className={`txn-create-upload-btn ${showCreateUploadMenu ? 'active' : ''}`}
                onClick={() => setShowCreateUploadMenu(prev => !prev)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                <span>Create or Upload</span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  {showCreateUploadMenu ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
                </svg>
              </button>
              {showCreateUploadMenu && (
                <div className="txn-create-upload-menu">
                  {activeDocTab === 'transaction-documents' && canManage && (
                    <button
                      type="button"
                      className="txn-create-upload-menu-item"
                      onClick={() => { setShowCreateUploadMenu(false); setCreateFolderName(''); setShowCreateFolderModal(true); }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/><line x1="12" y1="11" x2="12" y2="17"/><line x1="9" y1="14" x2="15" y2="14"/></svg>
                      <span>New Folder</span>
                    </button>
                  )}
                  <button
                    type="button"
                    className="txn-create-upload-menu-item"
                    onClick={() => { setShowCreateUploadMenu(false); openUploadPicker(); }}
                    disabled={isUploading || isBatchUploading}
                  >
                    {(isUploading || isBatchUploading)
                      ? <><span className="btn-spinner" style={{ width: 13, height: 13, borderWidth: 2 }}></span><span>Uploading...</span></>
                      : <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg><span>File Upload</span></>}
                  </button>
                  <button
                    type="button"
                    className="txn-create-upload-menu-item"
                    onClick={() => { setShowCreateUploadMenu(false); openFolderPicker(); }}
                    disabled={isUploading || isBatchUploading || isScanningFolder}
                  >
                    {isScanningFolder
                      ? <><span className="btn-spinner" style={{ width: 13, height: 13, borderWidth: 2 }}></span><span>Scanning...</span></>
                      : <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/><polyline points="9 15 12 12 15 15"/><line x1="12" y1="12" x2="12" y2="19"/></svg><span>Folder Upload</span></>}
                  </button>
                </div>
              )}
            </div>
            )}
            {canSeeAddForm && activeDocTab === 'forms' && (
              <button className="txn-create-doc-btn" onClick={handleAddFormClick} disabled={isFetchingRoomId}>
                {isFetchingRoomId
                  ? <><span className="btn-spinner" style={{ width: 13, height: 13, borderWidth: 2 }}></span><span>Loading…</span></>
                  : <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                      </svg>
                      <span>Add Form</span>
                    </>}
              </button>
            )}
          </div>
        </div>

        {/* ── Tab content ──────────────────────────────────────── */}
        <div className="txn-documents-content">

          {/* FORMS TAB */}
          {activeDocTab === 'forms' && (
            <>
              {/* Status filter chips */}
              <div className="txn-filter-chips">
                {[
                  { key: 'all',     label: 'All',     count: getVisibleForms().length },
                  { key: 'signed',  label: 'Signed',  count: getVisibleForms().filter(isFormSigned).length },
                  { key: 'pending', label: 'Pending', count: getVisibleForms().filter(isPendingSignature).length },
                  ...(!isClient ? [{ key: 'draft', label: 'Draft', count: getVisibleForms().filter(d => !d.isSigned && (!d.status || d.status?.toLowerCase() === 'draft')).length }] : []),
                ].map(chip => (
                  <div key={chip.key} className={`txn-filter-chip ${docFilter === chip.key ? 'active' : ''}`} onClick={() => setDocFilter(chip.key)}>
                    {chip.label} <span className="chip-count">({chip.count})</span>
                  </div>
                ))}
              </div>

              {isLoadingDocuments ? (
                <div className="txn-documents-loading">
                  <div className="txn-loading-spinner"></div>
                  <span>Loading forms...</span>
                </div>
              ) : documentsError ? (
                <div className="txn-documents-empty">
                  <div className="txn-empty-icon"><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg></div>
                  <p>{documentsError}</p>
                  <button className="txn-retry-btn" onClick={fetchDocuments}>Try Again</button>
                </div>
              ) : getFilteredForms2().length === 0 ? (
                <div className="txn-documents-empty">
                  <div className="txn-empty-icon"><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="13" y2="17"/></svg></div>
                  <p>{isClient ? 'No forms to sign yet.' : <>No forms added yet. Click <strong>Add Form</strong> to add one.</>}</p>
                </div>
              ) : (
                renderDynamicDocs(flattenFormRowsForRendering(getFilteredForms2()), {
                  icon: docFileIcon,
                  getKey: (doc, index) => doc._rowKey || doc.id || doc.documentId || index,
                  getName: (doc) => doc.name || doc.title || 'Untitled Form',
                  getSubtitle: (doc) => {
                    if (doc._isOriginalRow) return 'Original · Fillable Form';
                    if (doc._isEnvelopeRow) return doc._showVersionNumber ? `Envelope · v${doc.version}` : 'Envelope';
                    return doc.isDynamic ? 'Fillable Form' : (doc.type || 'DocuSign Document');
                  },
                  getStatusLabel: (doc) => {
                    if (doc._isOriginalRow) return 'Draft';
                    if (doc._loneWolf) {
                      // Buyer/Seller/other client rows are marked _clientSignedOnly at the fetch
                      // step (client branch only ever surfaces finalized signings) — always
                      // 'Signed'.
                      if (doc._clientSignedOnly) return doc.userDisplayStatus || 'Signed';
                      // The Lone Wolf "Signing" row (only exists once a signing has been created
                      // for this form) carries the real, live progress; 1="Sent" confirmed
                      // against Lone Wolf's own UI, 0/2/3 inferred from the surrounding
                      // progression (Draft → Sent → In Progress → Signed).
                      if (doc._loneWolfSigningRow) {
                        const statusId = doc._signing?.statusId;
                        if (statusId === 3) return 'Signed';
                        if (statusId === 2) return 'In Progress';
                        if (statusId === 0) return 'Draft';
                        return 'Sent';
                      }
                      // The form row itself (Agent/TC) is the original fillable form — same as
                      // DocuSign's Original row above, its status never changes regardless of any
                      // signing built from it; that's what the separate Signing row is for.
                      return 'Draft';
                    }
                    if (doc._isEnvelopeRow) {
                      // Client (Buyer/Seller): use their own live recipient status, not the
                      // generic stored doc.status — a client-visible row is only ever
                      // 'sent'/'delivered' (→ Received) or 'completed' (→ Signed); 'created'
                      // recipients are already filtered out upstream in getVisibleForms.
                      if (isClient) {
                        if (doc._envelopeRecipientStatus === 'completed') return 'Signed';
                        if (doc._envelopeRecipientStatus === 'sent' || doc._envelopeRecipientStatus === 'delivered') return 'Received';
                      } else if (doc._envelopeAllCompleted) {
                        // Agent/TC: whole-envelope summary — stored doc.status never advances
                        // past SENT (nothing calls markSigned()), so without this it would show
                        // "Sent" forever even after every recipient actually finished signing.
                        return 'Signed';
                      }
                      const s = (doc.status || '').toLowerCase();
                      if (doc.isSigned || s === 'signed' || s === 'completed') return 'Signed';
                      if (s === 'draft_pending_tagging') return 'Draft';
                      if (s === 'sent') return 'Sent';
                      return doc.status || 'Sent';
                    }
                    return doc.userDisplayStatus || getDocStatusLabel(doc);
                  },
                  getStatusStyle: (doc) => {
                    if (doc._isOriginalRow) return { background: '#fff4e6', color: '#b06a00' };
                    if (doc._loneWolf) {
                      const statusId = doc._signing?.statusId;
                      if (doc._clientSignedOnly || statusId === 3) return { background: '#e8f5e9', color: '#2e7d32' };
                      if (statusId === 2) return { background: '#ede7f6', color: '#5B47D6' };
                      if (statusId === 1) return { background: '#ede7f6', color: '#5B47D6' };
                      return { background: '#fff4e6', color: '#b06a00' };
                    }
                    if (doc._isEnvelopeRow) {
                      if (isClient) {
                        if (doc._envelopeRecipientStatus === 'completed') return { background: '#e8f5e9', color: '#2e7d32' };
                        if (doc._envelopeRecipientStatus === 'sent' || doc._envelopeRecipientStatus === 'delivered') return { background: '#ede7f6', color: '#5B47D6' };
                      } else if (doc._envelopeAllCompleted) {
                        return { background: '#e8f5e9', color: '#2e7d32' };
                      }
                      const s = (doc.status || '').toLowerCase();
                      if (doc.isSigned || s === 'signed' || s === 'completed') return { background: '#e8f5e9', color: '#2e7d32' };
                      return { background: '#ede7f6', color: '#5B47D6' };
                    }
                    return getDocStatusStyle(doc);
                  },
                  getDate: (doc) => formatUtcNaiveInstantForViewer(doc.createdDate || doc.date || doc.createdAt).dateLabel || '-',
                  isLoading: (doc) => viewingDocumentId === (doc.id || doc.documentId) || reviewingDocumentId === (doc.id || doc.documentId),
                  onOpen: (doc, e) => {
                    // Buyer/Seller/other client's finalized-signing row: no packageId, routes
                    // to the signed-document viewer instead of the forms-editor path below.
                    if (doc._loneWolf && doc._clientSignedOnly) return handleViewSignedDocument(doc, e);
                    // Agent/TC "Lone Wolf Signing" row: only openable once finalized (the backend
                    // 400s otherwise) — clicking before then does nothing, same as its kebab item
                    // being disabled below.
                    if (doc._loneWolf && doc._loneWolfSigningRow) {
                      const finalized = doc._signing?.finalized === true || doc._signing?.statusId === 3;
                      return finalized ? handleViewSignedDocument(doc, e) : undefined;
                    }
                    // Lone Wolf-attached form: download+show via its own forms-editor API,
                    // checked first — has no DocuSign envelope/room at all.
                    if (doc._loneWolf) return handleViewLoneWolfForm(doc, e);
                    // Colorado rows (Original AND Envelope) always view via FormService's own PDF
                    // path — handleViewInDocuSign resolves documentId against a DocuSign Room,
                    // which Colorado documents never have (checked _formService first for this).
                    if (doc._formService) return handleViewColoradoForm(doc, e);
                    // CA envelope row: view that specific version's own envelope.
                    if (doc._isEnvelopeRow) return handleViewInDocuSign(doc, e);
                    // CA original row: same edit target the permanent Original card always had.
                    if (doc._isOriginalRow) return isCAAgent ? openDocuSignRoomsEditor() : handleReviewDocument(doc, e);
                    // Custom PDFs: unchanged pre-versioning behavior.
                    return isCATransaction ? handleViewInDocuSign(doc, e) : handleViewDocument(doc, e);
                  },
                  getMenuItems: (doc) => {
                    if (!canManage) {
                      // Clients don't get management actions (Edit/Send/Delete), but should
                      // still have a menu with an explicit View entry rather than none at all —
                      // same view target as the row's own onOpen handler above.
                      return (
                        <>
                        <button
                          type="button"
                          className="txn-doc-kebab-item"
                          onClick={(e) => {
                            setOpenDocMenuId(null);
                            if (doc._loneWolf && doc._clientSignedOnly) return handleViewSignedDocument(doc, e);
                            if (doc._loneWolf) return handleViewLoneWolfForm(doc, e);
                            if (doc._formService) return handleViewColoradoForm(doc, e);
                            if (doc._isEnvelopeRow) return handleViewInDocuSign(doc, e);
                            if (doc._isOriginalRow) return isCAAgent ? openDocuSignRoomsEditor() : handleReviewDocument(doc, e);
                            return isCATransaction ? handleViewInDocuSign(doc, e) : handleViewDocument(doc, e);
                          }}
                        >
                          View
                        </button>
                        <AiSummaryMenuItem docAi={docAi} doc={doc} role={currentUserRole} area="forms" onSelect={() => setOpenDocMenuId(null)} />
                        </>
                      );
                    }
                    // Envelope rows are view-only — no management actions apply — but still
                    // get a kebab with an explicit View entry, consistent with every other
                    // row type, rather than no menu at all. Same _formService-first priority
                    // as the row's own onOpen handler above (Colorado envelope rows view via
                    // FormService, not the DocuSign Room path).
                    if (doc._isEnvelopeRow) {
                      return (
                        <>
                        <button
                          type="button"
                          className="txn-doc-kebab-item"
                          onClick={(e) => { setOpenDocMenuId(null); doc._formService ? handleViewColoradoForm(doc, e) : handleViewInDocuSign(doc, e); }}
                        >
                          View
                        </button>
                        <AiSummaryMenuItem docAi={docAi} doc={doc} role={currentUserRole} area="forms" onSelect={() => setOpenDocMenuId(null)} />
                        </>
                      );
                    }

                    const isSignedDoc = doc.isSigned || doc.status === 'signed' || doc.status === 'completed' || doc.status === 'sent';
                    const isCustomPdf = !doc.isDynamic && !doc.formId;
                    const isDraftPendingTagging = doc.status?.toLowerCase() === 'draft_pending_tagging';
                    const showResumeTagging = isDraftPendingTagging && isCustomPdf;
                    const canSendForSignatureRole = canManage &&
                      // Mirrors mobile DocsTab.tsx's canSendForSignature role list — TC is
                      // agent-equivalent for Send for Signature (TransactionDetailScreen.tsx
                      // passes isAgent={isEffectiveAgent || isTC} into DocsTab specifically).
                      ['BUYER_AGENT', 'BUYERS_AGENT', 'CO_BUYERS_AGENT', 'CO_BUYER_AGENT', 'LISTING_AGENT', 'CO_LISTING_AGENT', 'SELLERS_AGENT', 'TRANSACTION_COORDINATOR'].includes(currentUserRole);
                    const handleCardView = (e) => (
                      doc._formService ? handleViewColoradoForm(doc, e) : (isCATransaction ? handleViewInDocuSign(doc, e) : handleViewDocument(doc, e))
                    );
                    const handleCardEdit = (e) => (
                      doc._formService ? handleEditColoradoForm(doc, e) : (isCAAgent ? openDocuSignRoomsEditor() : handleReviewDocument(doc, e))
                    );

                    // Original rows (CA-exploded or Colorado's always-present base row): Edit +
                    // Send for Signature, unconditional — never reflects envelope state, no Delete.
                    if (doc._isOriginalRow) {
                      return (
                        <>
                          <button
                            type="button"
                            className="txn-doc-kebab-item"
                            onClick={(e) => { setOpenDocMenuId(null); handleCardEdit(e); }}
                          >
                            {isCAAgent ? 'Edit in DocuSign' : 'Edit'}
                          </button>
                          <AiSummaryMenuItem docAi={docAi} doc={doc} role={currentUserRole} area="forms" onSelect={() => setOpenDocMenuId(null)} />
                          {canSendForSignatureRole && (
                            <button
                              type="button"
                              className="txn-doc-kebab-item"
                              onClick={(e) => { setOpenDocMenuId(null); handleSendForSignature(doc, e); }}
                            >
                              {isCAAgent ? 'Send for Signature in DocuSign' : 'Send for Signature'}
                            </button>
                          )}
                        </>
                      );
                    }

                    // Lone Wolf-attached form: View (own in-app PDF viewer) + Edit + Send For
                    // Signature (both open Lone Wolf's own hosted forms editor for this form's
                    // package, in a new tab — same link, Lone Wolf's own UI handles sending from
                    // there since our own signing API isn't wired up yet). Still no Delete (forms
                    // can't be removed from the transaction on Lone Wolf's side or ours).
                    // Lone Wolf "Signing" row: its own menu — a single action reflecting the
                    // signing's real state, no Edit/Send (those belong to the original form row
                    // above this one, not the signing itself).
                    if (doc._loneWolf && doc._loneWolfSigningRow) {
                      const finalized = doc._signing?.finalized === true || doc._signing?.statusId === 3;
                      return (
                        <>
                        <button
                          type="button"
                          className="txn-doc-kebab-item"
                          disabled={!finalized || viewingDocumentId === (doc.id || doc.documentId)}
                          onClick={(e) => { setOpenDocMenuId(null); handleViewSignedDocument(doc, e); }}
                        >
                          {finalized ? 'View Signed Document' : 'Awaiting Signatures'}
                        </button>
                        <AiSummaryMenuItem docAi={docAi} doc={doc} role={currentUserRole} area="forms" onSelect={() => setOpenDocMenuId(null)} />
                        </>
                      );
                    }

                    if (doc._loneWolf) {
                      return (
                        <>
                          <button
                            type="button"
                            className="txn-doc-kebab-item"
                            onClick={(e) => { setOpenDocMenuId(null); handleViewLoneWolfForm(doc, e); }}
                          >
                            View
                          </button>
                          <AiSummaryMenuItem docAi={docAi} doc={doc} role={currentUserRole} area="forms" onSelect={() => setOpenDocMenuId(null)} />
                          <button
                            type="button"
                            className="txn-doc-kebab-item"
                            onClick={(e) => { setOpenDocMenuId(null); handleEditLoneWolfForm(doc, e); }}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="txn-doc-kebab-item"
                            onClick={(e) => {
                              setOpenDocMenuId(null);
                              // TDS/SPQ: send the wizard-FILLED PDF through the same recipient-select
                              // + Lone Wolf signing flow as a Transaction Documents PDF (not the LW
                              // forms editor). Every other LW form keeps the editor behavior.
                              if (isTdsSpqLoneWolfForm(doc)) {
                                return handleSendForSignature({ ...doc, _loneWolfFormSign: true }, e);
                              }
                              return handleLoneWolfSendForSignature(doc, e);
                            }}
                          >
                            Send For Signature
                          </button>
                        </>
                      );
                    }

                    // Custom PDFs: unchanged pre-versioning behavior (Draft → Pending Tagging →
                    // Sent, one row whose badge changes in place, with Delete while eligible).
                    const canSendForSignature = canSendForSignatureRole && !isSignedDoc;
                    const canDeleteDoc = canManage && !isSignedDoc && !doc.envelopeId && !doc.isDynamic;
                    return (
                      <>
                        <button
                          type="button"
                          className="txn-doc-kebab-item"
                          disabled={viewingDocumentId === (doc.id || doc.documentId) || reviewingDocumentId === (doc.id || doc.documentId)}
                          onClick={(e) => { setOpenDocMenuId(null); isSignedDoc ? handleCardView(e) : handleCardEdit(e); }}
                        >
                          {isSignedDoc ? (isCATransaction ? 'View in DocuSign' : 'View') : (isCAAgent ? 'Edit in DocuSign' : 'Edit')}
                        </button>
                        <AiSummaryMenuItem docAi={docAi} doc={doc} role={currentUserRole} area="forms" onSelect={() => setOpenDocMenuId(null)} />
                        {canSendForSignature && (
                          <button
                            type="button"
                            className="txn-doc-kebab-item"
                            onClick={(e) => { setOpenDocMenuId(null); showResumeTagging ? handleResumeTagging(doc, e) : handleSendForSignature(doc, e); }}
                          >
                            {showResumeTagging ? 'Resume Tagging' : (isCAAgent ? 'Send for Signature in DocuSign' : 'Send for Signature')}
                          </button>
                        )}
                        {canDeleteDoc && (
                          <button
                            type="button"
                            className="txn-doc-kebab-item danger"
                            onClick={(e) => { setOpenDocMenuId(null); handleDeleteDocument(doc, e); }}
                          >
                            Delete
                          </button>
                        )}
                      </>
                    );
                  },
                })
              )}
            </>
          )}

          {/* TRANSACTION DOCUMENTS TAB — same card design as Forms, now folder-aware */}
          {activeDocTab === 'transaction-documents' && (
            <>
              {/* Breadcrumb ("Documents / Test123") renders FIRST, above the folder cards
                  and file list it describes — otherwise the path context appears below the
                  content it's supposed to explain, which reads backwards ("is Folder1 even
                  inside Test123, or something else?"). Always show WHERE you are before
                  WHAT's there. */}
              {selectedFolder && (
                <div className="txn-folder-detail-header">
                  {/* Back button + current folder name only — standard mobile/native navigation
                      pattern (iOS/Android back button + screen title), not a full path trail.
                      "Transaction Documents" isn't shown — it's not a real folder, just the
                      section title right above. */}
                  <button
                    type="button"
                    className="txn-folder-back-btn"
                    onClick={() => setSelectedFolder(parentFolderPath(selectedFolder) || null)}
                    aria-label="Back"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
                  </button>
                  <span className="txn-folder-current-path">
                    {breadcrumbSegments[breadcrumbSegments.length - 1]?.label}
                  </span>
                </div>
              )}
              {isLoadingGcsDocuments ? (
                <div className="txn-documents-loading">
                  <div className="txn-loading-spinner"></div>
                  <span>Loading documents...</span>
                </div>
              ) : (
              <>
              {/* Folder cards + loose files at the current level coexist together, Windows-
                  drive/Explorer style — no synthetic "Documents" folder wraps the loose
                  ones, and this now applies at ANY depth, not just root: folderGroups is
                  scoped to currentPath's direct children, so subfolders show their own
                  nested subfolder cards the same way root shows top-level ones. */}
              {folderGroups.size > 0 && (
                <div className="txn-folder-grid">
                  {Array.from(folderGroups.values()).map(group => (
                    <div key={group.key} className="txn-folder-card" onClick={() => setSelectedFolder(group.key)}>
                      <div className="txn-folder-icon">
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
                      </div>
                      <div className="txn-folder-info">
                        <div className="txn-folder-name">{group.displayName}</div>
                        <div className="txn-folder-meta">
                          {(() => {
                            // Service-request auto-folder (spec §5): fixed subtitle
                            // instead of the generic count/date meta so it reads as
                            // its own thing on the Requester's transaction Documents.
                            if (group.key === 'service-request') {
                              return 'Auto-created — holds documents marked in from any service request on this transaction';
                            }
                            // "Empty" must account for subfolders too — a folder with 0
                            // direct files but a subfolder inside it is not actually empty.
                            const parts = [];
                            if (group.docs.length > 0) parts.push(`${group.docs.length} ${group.docs.length === 1 ? 'file' : 'files'}`);
                            if (group.subfolderCount > 0) parts.push(`${group.subfolderCount} ${group.subfolderCount === 1 ? 'subfolder' : 'subfolders'}`);
                            if (parts.length === 0) return 'Empty';
                            const base = parts.join(', ');
                            return group.latestUploadedAt ? `${base} • Updated ${formatFolderDateTime(group.latestUploadedAt)}` : base;
                          })()}
                        </div>
                      </div>
                      {/* Every entry in folderGroups is now a real, renameable/deletable
                          folder — root is never a group here, so no special-case needed. */}
                      {canManage ? (
                        <div className="txn-doc-kebab-wrap" ref={openDocMenuId === group.key ? docMenuRef : null}>
                          <button
                            type="button"
                            className="txn-doc-kebab-btn"
                            aria-label="Folder actions"
                            onClick={(e) => openDocMenuAt(e, group.key)}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/></svg>
                          </button>
                          {openDocMenuId === group.key && (
                            <div className="txn-doc-kebab-menu" style={{ top: docMenuPos.top, left: docMenuPos.left }} onClick={(e) => e.stopPropagation()}>
                              <button type="button" className="txn-doc-kebab-item" onClick={(e) => openRenameFolder(group, e)}>
                                Rename
                              </button>
                              <button type="button" className="txn-doc-kebab-item" onClick={(e) => handleDownloadFolder(group, e)}>
                                Download
                              </button>
                              <button type="button" className="txn-doc-kebab-item" onClick={(e) => openShareFolder(group, e)}>
                                Share
                              </button>
                              <button type="button" className="txn-doc-kebab-item danger" onClick={(e) => openDeleteFolder(group, e)}>
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <svg className="txn-folder-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {docsToRender.length === 0 ? (
                // With folder cards already showing at this level and nothing loose to
                // list, render nothing extra — mirrors Explorer showing folders with no
                // stray "this directory has no loose files" message. Applies at any depth.
                folderGroups.size > 0 ? null : (
                  <div className="txn-documents-empty">
                    <div className="txn-empty-icon">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                    </div>
                    <p>No documents uploaded yet. Click <strong>Upload Document</strong> to add one.</p>
                  </div>
                )
              ) : (
                renderDynamicDocs(docsToRender, {
                    icon: docFileIcon,
                    getKey: (doc, index) => doc.documentId || index,
                    getName: (doc) => displayFileName(doc.fileName) || doc.name || 'Document',
                    getSubtitle: (doc) => `${doc.fileType || (doc.fileName ? doc.fileName.split('.').pop()?.toUpperCase() : '') || 'Document'}${doc.fileSize ? ` · ${formatFileSize(doc.fileSize)}` : ''}`,
                    getStatusLabel: (doc) => gcsEnvelopeStatusLabel(doc),
                    getStatusStyle: (doc) => gcsEnvelopeStatusStyle(doc),
                    getDate: (doc) => {
                      const dateLabel = formatUtcNaiveInstantForViewer(doc.uploadedAt || doc.createdAt).dateLabel || '-';
                      const uploadedBy = doc.uploadedByName || doc.uploadedBy;
                      return uploadedBy ? `${dateLabel} · ${uploadedBy}` : dateLabel;
                    },
                    isLoading: (doc) => viewingDocumentId === doc.documentId || downloadingDocId === doc.documentId,
                    getLoadingLabel: (doc) => downloadingDocId === doc?.documentId ? 'Downloading...' : 'Loading...',
                    onOpen: (doc, e) => handleViewGcsDocument(doc, e),
                    getMenuItems: (doc) => {
                      // Signature is a PDF-only action, and only while there's no envelope yet
                      // or it's still a draft being tagged — matches the backend's own guard.
                      const isPdf = doc.fileType === 'application/pdf';
                      const canSend = canManage && isPdf
                        && (!doc.envelopeStatus || doc.envelopeStatus === 'VOIDED' || doc.envelopeStatus === 'DECLINED');
                      const canResume = canManage && isPdf && doc.envelopeStatus === 'DRAFT_PENDING_TAGGING';
                      return (
                        <>
                          <button type="button" className="txn-doc-kebab-item" onClick={(e) => { setOpenDocMenuId(null); handleViewGcsDocument(doc, e); }}>
                            Preview
                          </button>
                          <AiSummaryMenuItem docAi={docAi} doc={doc} role={currentUserRole} onSelect={() => setOpenDocMenuId(null)} />
                          <button type="button" className="txn-doc-kebab-item" onClick={(e) => { setOpenDocMenuId(null); handleGcsDownload(doc, e); }}>
                            Download
                          </button>
                          <button type="button" className="txn-doc-kebab-item" onClick={(e) => { setOpenDocMenuId(null); openShareModal(doc, e); }}>
                            Share
                          </button>
                          {canResume && (
                            <button type="button" className="txn-doc-kebab-item" onClick={(e) => { setOpenDocMenuId(null); handleResumeGcsTagging(doc, e); }}>
                              Resume Tagging
                            </button>
                          )}
                          {canSend && (
                            <button type="button" className="txn-doc-kebab-item" onClick={(e) => { setOpenDocMenuId(null); handleSendForSignature({ ...doc, _isGcsDoc: true }, e); }}>
                              Send for Signature
                            </button>
                          )}
                          {canManage && (
                            <button type="button" className="txn-doc-kebab-item" onClick={(e) => { setOpenDocMenuId(null); openMoveFolderPicker(doc, e); }}>
                              Move to folder…
                            </button>
                          )}
                          {canManage && (
                            <button type="button" className="txn-doc-kebab-item danger" onClick={(e) => { setOpenDocMenuId(null); handleGcsDelete(doc, e); }}>
                              Delete
                            </button>
                          )}
                        </>
                      );
                    },
                  })
                )}
              </>
              )}
              </>
          )}

        </div>
      </div>

      <DocAiSummaryModal docAi={docAi} />

      {/* Delete Confirmation Popup — replaces window.confirm for document deletion */}
      {showDeleteConfirm && deleteConfirmDoc && (
        <div className="txn-delete-modal-overlay" onClick={closeDeleteConfirm}>
          <div className="txn-delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="txn-delete-modal-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </div>
            <div className="txn-delete-modal-title">Delete Document</div>
            <div className="txn-delete-modal-desc">
              Are you sure you want to delete <strong>"{deleteConfirmDoc.fileName || deleteConfirmDoc.name || 'this document'}"</strong>? This action cannot be undone.
            </div>
            <div className="txn-delete-modal-actions">
              <button
                className="txn-delete-modal-btn txn-delete-modal-yes"
                onClick={confirmGcsDelete}
                disabled={isDeletingGcsFile}
              >
                {isDeletingGcsFile ? 'Deleting...' : 'Yes'}
              </button>
              <button
                className="txn-delete-modal-btn txn-delete-modal-no"
                onClick={closeDeleteConfirm}
                disabled={isDeletingGcsFile}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Folder Modal */}
      {showCreateFolderModal && (
        <div className="txn-delete-modal-overlay" onClick={() => !isCreatingFolder && setShowCreateFolderModal(false)}>
          <div className="txn-folder-modal" onClick={(e) => e.stopPropagation()}>
            <div className="txn-delete-modal-title">Create New Folder</div>
            <div className="txn-delete-modal-desc">Give this folder a name to start organizing documents.</div>
            <input
              className="gcs-share-input"
              style={{ marginBottom: 20 }}
              placeholder="Folder name"
              value={createFolderName}
              onChange={(e) => setCreateFolderName(e.target.value)}
              maxLength={40}
              autoFocus
            />
            <div className="txn-folder-modal-actions">
              <button className="txn-folder-modal-btn txn-folder-modal-cancel" onClick={() => setShowCreateFolderModal(false)} disabled={isCreatingFolder}>
                Cancel
              </button>
              <button
                className="txn-folder-modal-btn txn-folder-modal-create"
                onClick={handleCreateFolder}
                disabled={isCreatingFolder || !sanitizeFolderName(createFolderName)}
              >
                {isCreatingFolder ? <span className="btn-spinner" style={{ width: 14, height: 14, borderWidth: 2, borderTopColor: '#fff' }}></span> : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Folder Modal */}
      {renameFolderTarget && (
        <div className="txn-delete-modal-overlay" onClick={() => !isRenamingFolder && setRenameFolderTarget(null)}>
          <div className="txn-folder-modal" onClick={(e) => e.stopPropagation()}>
            <div className="txn-delete-modal-title">Rename Folder</div>
            <input
              className="gcs-share-input"
              style={{ marginBottom: 20 }}
              placeholder="Folder name"
              value={renameFolderNewName}
              onChange={(e) => setRenameFolderNewName(e.target.value)}
              maxLength={40}
              autoFocus
            />
            <div className="txn-folder-modal-actions">
              <button className="txn-folder-modal-btn txn-folder-modal-cancel" onClick={() => setRenameFolderTarget(null)} disabled={isRenamingFolder}>
                Cancel
              </button>
              <button
                className="txn-folder-modal-btn txn-folder-modal-create"
                onClick={confirmRenameFolder}
                disabled={isRenamingFolder || !sanitizeFolderName(renameFolderNewName)}
              >
                {isRenamingFolder ? <span className="btn-spinner" style={{ width: 14, height: 14, borderWidth: 2, borderTopColor: '#fff' }}></span> : 'Rename'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Folder Modal — documents in the folder are never deleted, only moved to
          Transaction Documents (root); the copy below says so explicitly before committing. */}
      {deleteFolderTarget && (
        <div className="txn-delete-modal-overlay" onClick={() => !isDeletingFolder && setDeleteFolderTarget(null)}>
          <div className="txn-delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="txn-delete-modal-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </div>
            <div className="txn-delete-modal-title">Delete Folder</div>
            <div className="txn-delete-modal-desc">
              {(() => {
                // Total counts include nested subfolders — deleting cascades through the
                // whole subtree, unlike the card's own badge (direct children only).
                const total = countDocsUnderFolder(deleteFolderTarget.key);
                const subfolderPrefix = deleteFolderTarget.key + '/';
                const totalSubfolders = Array.from(allFolderPaths).filter(p => p.startsWith(subfolderPrefix)).length;
                if (total === 0 && totalSubfolders === 0) return 'This folder is empty. Delete it?';
                const parts = [];
                if (total > 0) parts.push(`${total} document${total === 1 ? '' : 's'}`);
                if (totalSubfolders > 0) parts.push(`${totalSubfolders} subfolder${totalSubfolders === 1 ? '' : 's'}`);
                return `This folder contains ${parts.join(' and ')}. Documents will move to Transaction Documents — not be deleted. Continue?`;
              })()}
            </div>
            <div className="txn-delete-modal-actions">
              <button className="txn-delete-modal-btn txn-delete-modal-yes" onClick={confirmDeleteFolder} disabled={isDeletingFolder}>
                {isDeletingFolder ? 'Deleting...' : 'Yes'}
              </button>
              <button className="txn-delete-modal-btn txn-delete-modal-no" onClick={() => setDeleteFolderTarget(null)} disabled={isDeletingFolder}>
                No
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Folder share modal — same shape as DisclosureTab's active-share card,
          just scoped to a Transaction Documents folder. Shows the link (with
          Copy) + Revoke; once revoked the state stays visible so the agent
          knows the link is dead before dismissing. */}
      {shareModal && (
        <div className="txn-modal-overlay" onClick={() => setShareModal(null)}>
          <div className="gcs-share-modal" onClick={(e) => e.stopPropagation()}>
            <div className="gcs-share-header">
              <h3 className="gcs-share-title">Share Folder — {shareModal.group.displayName}</h3>
              <button className="gcs-share-close" onClick={() => setShareModal(null)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="gcs-share-body">
              {isCreatingShare || !shareModal.share ? (
                <div className="txn-documents-loading" style={{ padding: '2rem' }}>
                  <div className="txn-loading-spinner"></div>
                  <span>Preparing share link...</span>
                </div>
              ) : (
                <>
                  <p style={{ marginTop: 0, fontSize: 14, color: '#4b5563' }}>
                    {shareModal.share.isRevoked
                      ? 'This share link has been revoked. Anyone who had it can no longer verify or view files.'
                      : 'Anyone with this link can request access. Recipients verify by email (OTP) before they see files.'}
                  </p>
                  <div className="gcs-share-url-row">
                    <input
                      className="gcs-share-url-input"
                      type="text"
                      readOnly
                      value={shareModal.share.shareUrl}
                      onFocus={(e) => e.target.select()}
                    />
                    <button
                      type="button"
                      className="gcs-share-copy-btn"
                      onClick={handleCopyShareLink}
                      disabled={shareModal.share.isRevoked}
                    >
                      {shareLinkCopied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                  <div className="gcs-share-actions">
                    {shareModal.share.isRevoked ? (
                      <span className="gcs-share-revoked-badge">Revoked</span>
                    ) : (
                      <button
                        type="button"
                        className="gcs-share-revoke-btn"
                        onClick={handleRevokeShareLink}
                        disabled={isRevokingShare}
                      >
                        {isRevokingShare ? 'Revoking...' : 'Revoke'}
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Move-to-folder picker — the only remaining use of folderPickerMode now that upload
          never prompts for a folder (files always land wherever the user currently is). */}
      {folderPickerMode === 'move' && (
        <div className="txn-modal-overlay" onClick={() => { setFolderPickerMode(null); setFolderPickerMoveDoc(null); }}>
          <div className="gcs-share-modal" onClick={(e) => e.stopPropagation()}>
            <div className="gcs-share-header">
              <h3 className="gcs-share-title">Move to Folder</h3>
              <button className="gcs-share-close" onClick={() => setFolderPickerMode(null)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="txn-folder-picker-list">
              {/* Every folder in the whole transaction (allFolderPaths), not just the current
                  level's children — a document can move anywhere. Root is synthesized here
                  (it's never an allFolderPaths entry — see the Windows-drive model above) so
                  "move back to root" stays an explicit, available choice. Nested folders use
                  the full "Folder1 › Sub1" breadcrumb label so same-named folders at
                  different depths (or just far from the current view) stay distinguishable. */}
              {['documents', ...Array.from(allFolderPaths).sort()]
                .filter(key => key !== ((folderPickerMoveDoc?.folder || '').trim().toLowerCase() || 'documents'))
                .map(key => {
                  const label = key === 'documents' ? 'Documents' : fullPathDisplayName(key);
                  const count = gcsDocuments.filter(d => ((d.folder || '').trim().toLowerCase() || 'documents') === key).length;
                  return (
                    <label key={key} className={`txn-folder-picker-row${folderPickerSelection === key ? ' selected' : ''}`}>
                      <input type="radio" name="folder-picker" checked={folderPickerSelection === key} onChange={() => setFolderPickerSelection(key)} />
                      <span className="txn-folder-picker-name">{label}</span>
                      <span className="txn-folder-picker-count">{count} doc{count === 1 ? '' : 's'}</span>
                    </label>
                  );
                })}
            </div>
            <div className="gcs-share-actions">
              <button className="gcs-share-cancel-btn" onClick={() => setFolderPickerMode(null)} disabled={isMovingDoc}>
                Cancel
              </button>
              <button
                className="gcs-share-send-btn"
                onClick={confirmMoveToFolder}
                disabled={isMovingDoc}
              >
                {isMovingDoc ? 'Working…' : 'Move'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Select Forms Modal */}
      {showCreateDocModal && (
        <div className="txn-modal-overlay" onClick={() => setShowCreateDocModal(false)}>
          <div className="select-forms-modal" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="select-forms-header">
              <h2 className="select-forms-title">Select forms</h2>
            </div>

            {/* Modal Tabs */}
            <div className="select-forms-tabs">
              <button
                className={`select-forms-tab ${activeModalTab === 'browse' ? 'active' : ''}`}
                onClick={() => setActiveModalTab('browse')}
              >
                BROWSE
              </button>
              <button
                className={`select-forms-tab ${activeModalTab === 'selected' ? 'active' : ''}`}
                onClick={() => setActiveModalTab('selected')}
              >
                SELECTED ({selectedForms.length})
              </button>
            </div>

            {/* Modal Body */}
            <div className="select-forms-body">
              {activeModalTab === 'browse' ? (
                <>
                  {/* Left Sidebar - Libraries from API */}
                  <div className="select-forms-sidebar">
                    {/* Library search input */}
                    <div className="library-search-container">
                      <svg className="library-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                      <input
                        type="text"
                        className="library-search-input"
                        placeholder="Search libraries..."
                        value={librarySearchQuery}
                        onChange={e => setLibrarySearchQuery(e.target.value)}
                      />
                      {librarySearchQuery && (
                        <button className="library-search-clear" onClick={() => setLibrarySearchQuery('')}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                      )}
                    </div>
                    <div className="sidebar-section">
                      <div className="sidebar-section-title">
                        {librarySearchQuery
                          ? `${filteredLibraries.length} of ${formLibraries.filter(l => (l.formCount ?? l.forms?.length ?? l.totalCount ?? l.count ?? 0) > 0).length} LIBRARIES`
                          : `LIBRARIES (${formLibraries.filter(l => (l.formCount ?? l.forms?.length ?? l.totalCount ?? l.count ?? 0) > 0).length})`}
                      </div>
                      {isLoadingForms ? (
                        <div className="sidebar-loading">Loading...</div>
                      ) : formLibraries.length === 0 ? (
                        <div className="sidebar-empty">No libraries available</div>
                      ) : filteredLibraries.length === 0 ? (
                        <div className="sidebar-empty">No libraries match "{librarySearchQuery}"</div>
                      ) : (
                        filteredLibraries.map((library, index) => {
                          const libraryId = library.id || library.libraryId || library.formLibraryId || index;
                          const selectedId = selectedLibrary?.id || selectedLibrary?.libraryId || selectedLibrary?.formLibraryId;
                          const isActive = selectedLibrary && (libraryId === selectedId);

                          const formCount = library.formCount ??
                                           library.forms?.length ??
                                           library.items?.length ??
                                           library.totalCount ??
                                           library.count ??
                                           0;

                          const libraryName = library.name || library.libraryName || library.title || `Library ${index + 1}`;

                          // Highlight matched text
                          const q = librarySearchQuery.trim().toLowerCase();
                          const renderName = () => {
                            if (!q) return <span className="sidebar-item-text">{libraryName}</span>;
                            const idx = libraryName.toLowerCase().indexOf(q);
                            if (idx === -1) return <span className="sidebar-item-text">{libraryName}</span>;
                            return (
                              <span className="sidebar-item-text">
                                {libraryName.substring(0, idx)}
                                <mark className="sidebar-item-highlight">{libraryName.substring(idx, idx + q.length)}</mark>
                                {libraryName.substring(idx + q.length)}
                              </span>
                            );
                          };

                          return (
                            <div
                              key={libraryId}
                              className={`sidebar-item ${isActive ? 'active' : ''}`}
                              onClick={() => handleLibrarySelect(library)}
                            >
                              <span className="sidebar-item-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg></span>
                              {renderName()}
                              <span className="sidebar-item-count">({formCount})</span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>

                  {/* Right Content */}
                  <div className="select-forms-content">
                    {/* Search Box */}
                    <div className="forms-search-container">
                      <input
                        type="text"
                        className="forms-search-input"
                        placeholder="Search forms..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                      <span className="forms-search-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg></span>
                    </div>

                    {/* Selected Library Info */}
                    {selectedLibrary && (
                      <div className="selected-library-info">
                        <span className="selected-library-name">
                          {selectedLibrary.name || selectedLibrary.libraryName || selectedLibrary.title || 'Selected Library'}
                        </span>
                        <span className="selected-library-count">
                          {availableForms.length} form(s)
                        </span>
                      </div>
                    )}

                    {/* Forms Table Header */}
                    <div className="forms-table-header">
                      <div className="forms-table-radio"></div>
                      <div className="forms-table-name">Name</div>
                    </div>

                    {/* Forms List */}
                    <div className="forms-list">
                      {isLoadingForms ? (
                        <div className="forms-loading">
                          <span>Loading libraries...</span>
                        </div>
                      ) : formsError ? (
                        <div className="forms-error">
                          <span>{formsError}</span>
                        </div>
                      ) : !selectedLibrary ? (
                        <div className="forms-empty">
                          <span>Select a library to view forms</span>
                        </div>
                      ) : isLoadingLibraryForms ? (
                        <div className="forms-loading">
                          <div className="forms-loading-spinner"></div>
                          <span>Loading forms...</span>
                        </div>
                      ) : libraryFormsError ? (
                        <div className="forms-error">
                          <span>{libraryFormsError}</span>
                        </div>
                      ) : filteredForms.length === 0 ? (
                        <div className="forms-empty">
                          <span>{searchQuery ? 'No forms match your search' : 'No forms available in this library'}</span>
                        </div>
                      ) : (
                        filteredForms.map((form, index) => (
                          <div
                            key={form.id || form.formId || form.documentId || form.templateId || index}
                            className={`forms-list-item ${isFormSelected(form) ? 'selected' : ''}`}
                            onClick={() => toggleFormSelection(form)}
                          >
                            <div className="forms-item-radio">
                              <input
                                type="radio"
                                name="form-selection"
                                checked={isFormSelected(form)}
                                onChange={() => toggleFormSelection(form)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                            <div className="forms-item-icon">
                              <span className="form-icon-badge">FORM</span>
                            </div>
                            <div className="forms-item-name">
                              {form.name || form.formName || form.title || form.templateName || `Form ${index + 1}`}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </>
              ) : (
                /* Selected Tab Content */
                <div className="selected-forms-content">
                  {selectedForms.length === 0 ? (
                    <div className="forms-empty">
                      <span>No forms selected</span>
                      <p>Go to Browse tab to select forms</p>
                    </div>
                  ) : (
                    <div className="selected-forms-list">
                      {selectedForms.map((form, index) => (
                        <div key={form.id || form.documentId || index} className="selected-form-item">
                          <div className="forms-item-icon">
                            <span className="form-icon-badge">FORM</span>
                          </div>
                          <div className="forms-item-name">
                            {form.name || form.title || `Document ${index + 1}`}
                          </div>
                          <button
                            className="remove-form-btn"
                            onClick={() => toggleFormSelection(form)}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="select-forms-footer">
              <button
                className="add-selected-btn"
                onClick={handleAddSelectedForms}
                disabled={selectedForms.length === 0 || isAddingDocument}
              >
                {isAddingDocument ? 'ADDING...' : 'ADD SELECTED'}
              </button>
              <button
                className="cancel-btn"
                onClick={() => setShowCreateDocModal(false)}
                disabled={isAddingDocument}
              >
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preparing spinner overlay — shown while a Send-for-Signature readiness check
          (DocuSign for Colorado, Lone Wolf otherwise) is in flight, before the modal/editor/gate opens. */}
      {isPreparingSignature && (
        <div className="txn-prepare-overlay">
          <div className="txn-prepare-box">
            <div className="txn-loading-spinner"></div>
            <span className="txn-prepare-label">Preparing…</span>
          </div>
        </div>
      )}

      {/* Send for Signature Modal */}
      {showSignatureModal && (
        <div className="txn-modal-overlay" onClick={handleCloseSignatureModal}>
          <div className="signature-modal" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="signature-modal-header">
              <div className="signature-modal-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M20 14.66V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h5.34"/><polygon points="18 2 22 6 12 16 8 16 8 12 18 2"/></svg>
              </div>
              <h2 className="signature-modal-title">Send for Signature</h2>
              <p className="signature-modal-subtitle">
                Send "<strong>{signatureDocument?.name || signatureDocument?.title || 'Document'}</strong>" to the following recipients:
              </p>
            </div>

            {/* Modal Body */}
            <div className="signature-modal-body">
              <div className="signature-section-label">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                <span>Recipients ({selectedParticipantIds.size} selected of {signatureParticipants.length})</span>
              </div>

              <div className="signature-participants-list">
                {isLoadingParticipants ? (
                  <div className="signature-loading">
                    <div className="signature-loading-spinner"></div>
                    <span>Loading participants...</span>
                  </div>
                ) : signatureParticipants.length === 0 ? (
                  <div className="signature-empty">
                    <div className="signature-empty-icon"><svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg></div>
                    <span>No participants found</span>
                    <p>Add participants to this transaction first</p>
                  </div>
                ) : (
                  signatureParticipants.map((participant, index) => {
                    const name = `${participant.firstName || ''} ${participant.lastName || ''}`.trim() || participant.name || '';
                    const email = participant.email || '';
                    const role = participant.role || participant.participantRole || participant.type || '';
                    const hasEmail = email && email.includes('@');
                    const participantKey = participant.id || email;
                    const isSelected = hasEmail && selectedParticipantIds.has(participantKey);
                    const initials = name
                      ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                      : email ? email[0].toUpperCase() : '?';

                    return (
                      <div
                        key={participant.id || index}
                        className={`signature-participant-card${!hasEmail ? ' no-email' : ''}${isSelected ? ' selected' : ''}`}
                        onClick={() => hasEmail && handleToggleParticipant(participantKey)}
                        style={hasEmail ? {cursor: 'pointer'} : {}}
                      >
                        <div className="participant-avatar">
                          {initials}
                        </div>
                        <div className="participant-info">
                          {name && <div className="participant-name">{name}</div>}
                          {hasEmail
                            ? <div className="participant-email">{email}</div>
                            : <div className="participant-email no-email-warning">No email — add in Parties tab</div>
                          }
                          {role && <div className="participant-role">{role.replace(/_/g, ' ')}</div>}
                        </div>
                        <div className="participant-check">
                          {hasEmail
                            ? <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleToggleParticipant(participantKey)}
                                onClick={e => e.stopPropagation()}
                                style={{width: '18px', height: '18px', cursor: 'pointer', accentColor: '#007AFF'}}
                              />
                            : <span style={{color:'#FF9500'}}>!</span>
                          }
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="signature-modal-footer">
              <button
                className="signature-cancel-btn"
                onClick={handleCloseSignatureModal}
                disabled={isSendingEnvelope}
              >
                Cancel
              </button>
              <button
                className="signature-send-btn"
                onClick={handleSendEnvelope}
                disabled={isLoadingParticipants || selectedParticipantIds.size === 0 || isSendingEnvelope}
              >
                {isSendingEnvelope ? (
                  <>
                    <span className="btn-spinner"></span>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                    <span>Send for Signature</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Popup */}
      {notification.show && (
        <div className="notification-overlay" onClick={closeNotification}>
          <div className="notification-popup" onClick={(e) => e.stopPropagation()}>
            <div className={`notification-icon ${notification.type}`}>
              {notification.type === 'success' ? (
                <span>✓</span>
              ) : (
                <span>!</span>
              )}
            </div>
            <h3 className="notification-title">{notification.title}</h3>
            <p className="notification-message">{notification.message}</p>
            <button className="notification-btn" onClick={closeNotification}>
              OK
            </button>
          </div>
        </div>
      )}

      {/* Duplicate Document Confirmation — replaces the "Upload Failed / already exists" error
          notification with a Yes/No warning. Yes retries the upload under a new, distinct
          filename (existing document is left untouched); No closes with no changes, same as
          the old OK button did. */}
      {duplicateUploadConfirm.show && duplicateUploadConfirm.file && (
        <div className="notification-overlay" onClick={cancelDuplicateUploadConfirm}>
          <div className="notification-popup" onClick={(e) => e.stopPropagation()}>
            <div className="notification-icon warning">
              <span>!</span>
            </div>
            <h3 className="notification-title">Document Already Exists</h3>
            <p className="notification-message">
              This document already exists. Do you want to upload it again as a new version?
            </p>
            <div className="notification-actions">
              <button className="notification-btn notification-btn-secondary" onClick={cancelDuplicateUploadConfirm}>
                No
              </button>
              <button className="notification-btn" onClick={confirmDuplicateUploadAsNewVersion}>
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Per-file "Document Already Exists" prompt for a multi-file batch — mirrors mobile's
          duplicatePrompt. Yes retries this one file under a versioned name; No skips just
          this file and the batch continues with the next one. */}
      {batchDuplicatePrompt && (
        <div className="notification-overlay" onClick={() => batchDuplicatePrompt.resolve(false)}>
          <div className="notification-popup" onClick={(e) => e.stopPropagation()}>
            <div className="notification-icon warning">
              <span>!</span>
            </div>
            <h3 className="notification-title">Document Already Exists</h3>
            <p className="notification-message" title={batchDuplicatePrompt.fileName}>
              "{batchDuplicatePrompt.fileName}" already exists. Do you want to upload it again as a new version?
            </p>
            <div className="notification-actions">
              <button className="notification-btn notification-btn-secondary" onClick={() => batchDuplicatePrompt.resolve(false)}>
                No
              </button>
              <button className="notification-btn" onClick={() => batchDuplicatePrompt.resolve(true)}>
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Compact large-batch upload widget (Google Drive-style) — replaces the reviewable
          multi-upload-card above MULTI_UPLOAD_COMPACT_THRESHOLD files. Non-blocking corner
          panel, not a modal overlay, so the rest of the page stays usable while it uploads. */}
      {compactUpload && (
        <div className="compact-upload-widget">
          <div className="compact-upload-header">
            <span className="compact-upload-title">
              {compactUpload.phase === 'done'
                ? (compactUpload.failed > 0
                    ? `Uploaded ${compactUpload.succeeded} of ${compactUpload.total} items`
                    : `Uploaded ${compactUpload.total} item${compactUpload.total === 1 ? '' : 's'}`)
                : `Uploading ${compactUpload.total} item${compactUpload.total === 1 ? '' : 's'}`}
            </span>
            <div className="compact-upload-header-actions">
              <button
                type="button"
                className="compact-upload-icon-btn"
                onClick={() => setCompactUpload(prev => (prev ? { ...prev, collapsed: !prev.collapsed } : prev))}
                aria-label={compactUpload.collapsed ? 'Expand' : 'Collapse'}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  {compactUpload.collapsed ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
                </svg>
              </button>
              <button
                type="button"
                className="compact-upload-icon-btn"
                onClick={() => setCompactUpload(null)}
                aria-label="Close"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
          </div>
          {!compactUpload.collapsed && (
            <div className="compact-upload-body">
              {compactUpload.phase === 'done' ? (
                <div className="compact-upload-summary">
                  {compactUpload.failed === 0 && compactUpload.skipped === 0 && (
                    <span className="compact-upload-summary-ok">✓ All files uploaded successfully.</span>
                  )}
                  {compactUpload.failed > 0 && (
                    <span className="compact-upload-summary-fail">⚠ {compactUpload.failed} file{compactUpload.failed === 1 ? '' : 's'} failed to upload.</span>
                  )}
                  {compactUpload.skipped > 0 && (
                    <span className="compact-upload-summary-skip"> {compactUpload.skipped} skipped (already existed).</span>
                  )}
                </div>
              ) : (
                <>
                  <div className="compact-upload-status-row">
                    <span>{compactUpload.phase === 'starting' ? 'Starting uploads…' : `Uploading ${compactUpload.current} of ${compactUpload.total}…`}</span>
                    <button type="button" className="compact-upload-cancel-link" onClick={cancelCompactUpload}>
                      Cancel
                    </button>
                  </div>
                  {compactUpload.currentFileName && (
                    <div className="compact-upload-item-row">
                      <span className="compact-upload-item-spinner" />
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                      <span className="compact-upload-item-name" title={compactUpload.currentFileName}>{compactUpload.currentFileName}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Upfront "Upload N files?" confirmation — the very first gate for every multi-file
          batch, before even the duplicate check. In addition to (not instead of) everything
          below it in the flow. */}
      {uploadConfirmDialog && (
        <div className="txn-modal-overlay" onClick={cancelUploadConfirmDialog}>
          <div className="gcs-share-modal" onClick={(e) => e.stopPropagation()}>
            <div className="gcs-share-header">
              <h3 className="gcs-share-title">
                Upload {uploadConfirmDialog.entries.length} file{uploadConfirmDialog.entries.length === 1 ? '' : 's'}?
              </h3>
              <button className="gcs-share-close" onClick={cancelUploadConfirmDialog}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <p className="gcs-share-filename">
              Uploading to {uploadDestinationLabel(uploadConfirmDialog.folder)}.
            </p>
            <div className="gcs-share-actions">
              <button className="gcs-share-cancel-btn" onClick={cancelUploadConfirmDialog}>
                Cancel
              </button>
              <button className="gcs-share-send-btn" onClick={confirmUploadConfirmDialog}>
                {`Upload ${uploadConfirmDialog.entries.length} file${uploadConfirmDialog.entries.length === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upfront bulk-duplicate dialog — shown instead of the multi-upload card below
          whenever any selected file's name already exists in its target folder. */}
      {duplicateUploadDialog && (
        <div className="txn-modal-overlay" onClick={cancelDuplicateUploadDialog}>
          <div className="gcs-share-modal" onClick={(e) => e.stopPropagation()}>
            <div className="gcs-share-header">
              <h3 className="gcs-share-title">
                Upload {duplicateUploadDialog.entries.length} file{duplicateUploadDialog.entries.length === 1 ? '' : 's'}?
              </h3>
              <button className="gcs-share-close" onClick={cancelDuplicateUploadDialog} disabled={isResolvingOverwrite}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <p className="gcs-share-filename">
              {duplicateUploadDialog.duplicateCount} of {duplicateUploadDialog.entries.length} files already exist in this folder.
            </p>
            <div className="duplicate-upload-options">
              <label className="duplicate-upload-option">
                <input type="checkbox" checked={skipDuplicatesChecked} onChange={toggleSkipDuplicates} disabled={isResolvingOverwrite} />
                Skip files with duplicate names
              </label>
              <label className="duplicate-upload-option">
                <input type="checkbox" checked={overwriteAllChecked} onChange={toggleOverwriteAll} disabled={isResolvingOverwrite} />
                Overwrite all files
              </label>
            </div>
            <div className="gcs-share-actions">
              <button className="gcs-share-cancel-btn" onClick={cancelDuplicateUploadDialog} disabled={isResolvingOverwrite}>
                Cancel
              </button>
              <button className="gcs-share-send-btn" onClick={confirmDuplicateUploadDialog} disabled={isResolvingOverwrite}>
                {isResolvingOverwrite
                  ? <span className="btn-spinner" style={{ width: 14, height: 14, borderWidth: 2, borderTopColor: '#fff' }}></span>
                  : `Upload ${duplicateUploadDialog.entries.length} file${duplicateUploadDialog.entries.length === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Multi-file upload card — just confirms the picked files before uploading; no
          signature question here, "Send for Signature" is a per-document action afterward. */}
      {showMultiUploadCard && (
        <div className="txn-modal-overlay" onClick={cancelMultiUpload}>
          <div className="gcs-share-modal multi-upload-modal" onClick={(e) => e.stopPropagation()}>
            <div className="gcs-share-header">
              <h3 className="gcs-share-title">{multiUploadFiles.length} file{multiUploadFiles.length === 1 ? '' : 's'} selected</h3>
              <button className="gcs-share-close" onClick={cancelMultiUpload} disabled={isBatchUploading}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            <div className="multi-upload-file-list">
              {multiUploadFiles.map((entry) => (
                <div className="multi-upload-file-row" key={entry.key}>
                  <div className="multi-upload-file-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  </div>
                  <div className="multi-upload-file-info">
                    <div className="multi-upload-file-name" title={entry.file.name}>{entry.file.name}</div>
                    <div className="multi-upload-file-meta">
                      {formatFileSize(entry.file.size)}
                      {/* Only shown for folder-upload entries whose target subfolder is nested
                          deeper than wherever the modal was opened from — a bare top-level
                          file doesn't need a path caption, it's obviously going to currentPath
                          like every other entry. */}
                      {entry.relativePath && entry.relativePath.includes('/') && (
                        <span className="multi-upload-file-path"> · {fullPathDisplayName(entry.relativePath.split('/').slice(0, -1).join('/'))}</span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="multi-upload-remove-btn"
                    onClick={() => removeMultiUploadFile(entry.key)}
                    disabled={isBatchUploading}
                    aria-label={`Remove ${entry.file.name}`}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
              ))}
            </div>

            <input type="file" ref={multiUploadAddMoreRef} style={{ display: 'none' }}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg" multiple
              onChange={handleAddMoreMultiUploadFiles}
            />
            <button
              type="button"
              className="multi-upload-add-more"
              onClick={() => multiUploadAddMoreRef.current?.click()}
              disabled={isBatchUploading}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Add more files
            </button>

            <div className="gcs-share-actions">
              <button className="gcs-share-cancel-btn" onClick={cancelMultiUpload} disabled={isBatchUploading}>
                Cancel
              </button>
              <button
                className="gcs-share-send-btn"
                onClick={confirmMultiUpload}
                disabled={isBatchUploading || multiUploadFiles.length === 0}
              >
                {isBatchUploading
                  ? <><span className="btn-spinner" style={{ width: 14, height: 14, borderWidth: 2, borderTopColor: '#fff' }}></span><span>{batchUploadProgress && batchUploadProgress.total > 1 ? `Uploading ${batchUploadProgress.current} of ${batchUploadProgress.total}...` : 'Uploading...'}</span></>
                  : `Upload ${multiUploadFiles.length} file${multiUploadFiles.length === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Document Modal — mirrors mobile's share modal */}
      {showShareModal && shareModalDoc && (
        <div className="txn-modal-overlay" onClick={closeShareModal}>
          <div className="gcs-share-modal" onClick={(e) => e.stopPropagation()}>
            <div className="gcs-share-header">
              <h3 className="gcs-share-title">Share Document</h3>
              <button className="gcs-share-close" onClick={closeShareModal}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <p className="gcs-share-filename" title={shareModalDoc.fileName || shareModalDoc.name}>
              {shareModalDoc.fileName || shareModalDoc.name}
            </p>
            <div className="gcs-share-field" ref={recipientPickerRef}>
              <label className="gcs-share-label">Recipients <span className="gcs-share-required">*</span></label>
              {(() => {
                const selectedParties = participants.filter(p => shareRecipientIds.has(getParticipantId(p)));
                const search = recipientSearch.trim().toLowerCase();
                const filteredParties = !search ? participants : participants.filter(p => {
                  const hay = [getParticipantName(p), p.email, formatShareRole(p.role)].filter(Boolean).join(' ').toLowerCase();
                  return hay.includes(search);
                });
                const toggleParty = (id, emailValid) => {
                  if (!emailValid) return;
                  setShareRecipientIds(prev => {
                    const next = new Set(prev);
                    if (next.has(id)) next.delete(id); else next.add(id);
                    return next;
                  });
                };
                return (
                  <>
                    {selectedParties.length > 0 && (
                      <div className="gcs-share-chip-row">
                        {selectedParties.map(p => (
                          <span className="gcs-share-chip" key={`chip-${getParticipantId(p)}`}>
                            {getParticipantName(p)}
                            <button
                              type="button"
                              className="gcs-share-chip-remove"
                              onClick={() => toggleParty(getParticipantId(p), true)}
                              aria-label={`Remove ${getParticipantName(p)}`}
                            >
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    <button
                      type="button"
                      className={`gcs-share-picker-btn ${showRecipientDropdown ? 'active' : ''}`}
                      onClick={() => setShowRecipientDropdown(prev => !prev)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                      <span className="gcs-share-picker-text">
                        {selectedParties.length === 0 ? 'Add recipients' : `${selectedParties.length} recipient${selectedParties.length === 1 ? '' : 's'}`}
                      </span>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginLeft: 'auto' }}>
                        {showRecipientDropdown ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
                      </svg>
                    </button>
                    {showRecipientDropdown && (
                      <div className="gcs-share-dropdown">
                        <div className="gcs-share-search-wrap">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                          <input
                            className="gcs-share-search-input"
                            placeholder="Search parties by name, email or role"
                            value={recipientSearch}
                            onChange={(e) => setRecipientSearch(e.target.value)}
                          />
                        </div>
                        {participants.length === 0 ? (
                          <p className="gcs-share-empty">No transaction parties on file yet. Add participants to share this document.</p>
                        ) : filteredParties.length === 0 ? (
                          <p className="gcs-share-empty">No parties match "{recipientSearch}".</p>
                        ) : (
                          <div className="gcs-share-party-list">
                            {filteredParties.map(p => {
                              const id = getParticipantId(p);
                              const emailValid = !!p.email && EMAIL_REGEX.test(p.email);
                              const selected = shareRecipientIds.has(id);
                              return (
                                <button
                                  type="button"
                                  key={id}
                                  className={`gcs-share-party-row ${selected ? 'selected' : ''} ${!emailValid ? 'disabled' : ''}`}
                                  onClick={() => toggleParty(id, emailValid)}
                                  disabled={!emailValid}
                                >
                                  <span className={`gcs-share-avatar ${selected ? 'selected' : ''}`}>{getParticipantInitials(p)}</span>
                                  <span className="gcs-share-party-body">
                                    <span className="gcs-share-party-name">
                                      {getParticipantName(p)}
                                      <span className="gcs-share-party-role"> · {formatShareRole(p.role)}</span>
                                    </span>
                                    <span className={`gcs-share-party-email ${!emailValid ? 'missing' : ''}`}>
                                      {p.email || 'No email on file'}
                                    </span>
                                  </span>
                                  {selected && (
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        )}
                        <div className="gcs-share-dropdown-footer">
                          <span className="gcs-share-dropdown-count">
                            {selectedParties.length === 0
                              ? 'No recipients selected'
                              : `${selectedParties.length} selected`}
                          </span>
                          <button
                            type="button"
                            className="gcs-share-dropdown-done"
                            onClick={() => {
                              setShowRecipientDropdown(false);
                              setRecipientSearch('');
                            }}
                          >
                            Done
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
            <div className="gcs-share-field">
              <label className="gcs-share-label">Message (optional)</label>
              <textarea
                className="gcs-share-input gcs-share-textarea"
                placeholder="Add a message..."
                value={shareMessage}
                onChange={(e) => setShareMessage(e.target.value)}
                rows={3}
              />
            </div>
            <p className="gcs-share-expiry">Link expires in 48 hours</p>
            <div className="gcs-share-actions">
              <button className="gcs-share-cancel-btn" onClick={closeShareModal} disabled={isSharingFile}>
                Cancel
              </button>
              <button className="gcs-share-send-btn" onClick={handleSendShare} disabled={isSharingFile || shareRecipientIds.size === 0}>
                {isSharingFile ? (
                  <><span className="btn-spinner" style={{ width: 14, height: 14, borderWidth: 2, borderTopColor: '#fff' }}></span><span>Sending...</span></>
                ) : (
                  shareRecipientIds.size > 1 ? `Send Link (${shareRecipientIds.size})` : 'Send Link'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DocuSign readiness gate — shown when Add Form / Send for Signature is blocked
          by a disconnected/not-ready DocuSign account. Mirrors mobile's
          useDocuSignReadiness modal (not-connected / pending / no-provider). Room
          creation itself now happens silently (spinner tab), no separate prompt.
          Applies identically on dev/stg/prod. */}
      {docGateVariant && DOC_GATE_CONFIG[docGateVariant] && createPortal(
        <div className="txn-room-not-ready-overlay" onClick={closeDocGate}>
          <div className="txn-room-not-ready-modal" onClick={e => e.stopPropagation()}>
            <div className="txn-room-not-ready-icon-wrap">
              {DOC_GATE_CONFIG[docGateVariant].icon === 'link-off' && (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                  <line x1="2" y1="2" x2="22" y2="22"/>
                </svg>
              )}
              {DOC_GATE_CONFIG[docGateVariant].icon === 'clock' && (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
              )}
              {DOC_GATE_CONFIG[docGateVariant].icon === 'file-alert' && (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="12" y1="18" x2="12.01" y2="18" strokeWidth="3"/>
                  <line x1="12" y1="10" x2="12" y2="14"/>
                </svg>
              )}
            </div>
            <h2 className="txn-room-not-ready-title">{DOC_GATE_CONFIG[docGateVariant].title}</h2>
            <p className="txn-room-not-ready-message">
              {docGateVariant === 'not_connected' && docGateAction === 'sendForSignature'
                ? 'Please connect to DocuSign to send documents for signature.'
                : DOC_GATE_CONFIG[docGateVariant].body}
            </p>

            <div className="txn-room-not-ready-actions">
              <button
                className="txn-room-not-ready-btn"
                onClick={handleDocGatePrimary}
                disabled={docGateVariant === 'pending' && docGateResendingInvite}
              >
                {docGateVariant === 'pending' && docGateResendingInvite
                  ? <span className="txn-room-not-ready-spinner" />
                  : DOC_GATE_CONFIG[docGateVariant].primaryLabel}
              </button>
              <button className="txn-room-not-ready-btn-cancel" onClick={closeDocGate}>
                Cancel
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Lone Wolf readiness gate — non-Colorado equivalent of the DocuSign gate above,
          same modal markup/CSS classes for identical visual treatment. Shown when Add Form
          / Send For Signature is blocked by a disconnected/not-Active Lone Wolf account. */}
      {loneWolfGateVariant && LONEWOLF_GATE_CONFIG[loneWolfGateVariant] && createPortal(
        <div className="txn-room-not-ready-overlay" onClick={closeLoneWolfGate}>
          <div className="txn-room-not-ready-modal" onClick={e => e.stopPropagation()}>
            <div className="txn-room-not-ready-icon-wrap">
              {LONEWOLF_GATE_CONFIG[loneWolfGateVariant].icon === 'link-off' && (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                  <line x1="2" y1="2" x2="22" y2="22"/>
                </svg>
              )}
              {LONEWOLF_GATE_CONFIG[loneWolfGateVariant].icon === 'clock' && (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
              )}
            </div>
            <h2 className="txn-room-not-ready-title">{LONEWOLF_GATE_CONFIG[loneWolfGateVariant].title}</h2>
            <p className="txn-room-not-ready-message">
              {loneWolfGateVariant === 'not_connected' && loneWolfGateAction === 'sendForSignature'
                ? 'Please connect your Lone Wolf account to send forms for signature.'
                : LONEWOLF_GATE_CONFIG[loneWolfGateVariant].body}
            </p>

            <div className="txn-room-not-ready-actions">
              <button className="txn-room-not-ready-btn" onClick={handleLoneWolfGatePrimary}>
                {LONEWOLF_GATE_CONFIG[loneWolfGateVariant].primaryLabel}
              </button>
              <button className="txn-room-not-ready-btn-cancel" onClick={closeLoneWolfGate}>
                Cancel
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* CA-DRE permission-required modal (TC-only) — single-action, reuses the
          Room-not-ready modal's visual shape. Mirrors mobile's InfoModal usage
          in DocsTab.tsx's CA-DRE guard (PR #161). */}
      {permissionModal && createPortal(
        <div className="txn-room-not-ready-overlay" onClick={() => setPermissionModal(null)}>
          <div className="txn-room-not-ready-modal" onClick={e => e.stopPropagation()}>
            <div className="txn-room-not-ready-icon-wrap">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            </div>
            <h2 className="txn-room-not-ready-title">{permissionModal.title}</h2>
            <p className="txn-room-not-ready-message">{permissionModal.body}</p>

            <div className="txn-room-not-ready-actions">
              <button className="txn-room-not-ready-btn" onClick={() => setPermissionModal(null)}>
                OK
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* PDF Viewer Modal */}
      {showPdfViewer && pdfBlobUrl && createPortal(
        <div className="pdf-viewer-overlay" onClick={handleClosePdfViewer}>
          <div className="pdf-viewer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pdf-viewer-header">
              <div className="pdf-viewer-title-section">
                <span className="pdf-viewer-icon">📄</span>
                <h3 className="pdf-viewer-title">{pdfDocumentName}</h3>
              </div>
              <div className="pdf-viewer-actions">
                {pdfSummaryDoc && (
                  // Closes the viewer, then opens the summary pop-up for the same row.
                  <AiSummaryMenuItem
                    docAi={docAi}
                    doc={pdfSummaryDoc.doc}
                    role={currentUserRole}
                    area={pdfSummaryDoc.area}
                    className="pdf-viewer-summary-btn"
                    onSelect={handleClosePdfViewer}
                  />
                )}
                <a
                  href={pdfBlobUrl}
                  download={pdfDocumentName}
                  className="pdf-viewer-download-btn"
                  title="Download"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                </a>
                <button className="pdf-viewer-close-btn" onClick={handleClosePdfViewer} title="Close">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="pdf-viewer-body">
              <iframe
                src={pdfBlobUrl}
                title={pdfDocumentName}
                className="pdf-viewer-iframe"
              />
            </div>
          </div>
        </div>,
        document.body
      )}

      {editingCoPdf && (
        <ColoradoFormPdfEditor
          doc={editingCoPdf}
          getToken={getFirebaseToken}
          onClose={() => setEditingCoPdf(null)}
          onSaved={() => { fetchDocuments(); }}
        />
      )}

      {editingCoForm && createPortal(
        <div
          onClick={() => !isSavingCoForm && setEditingCoForm(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(20,20,40,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, width: 'min(560px, 92vw)', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }}>
            <div style={{ padding: '18px 22px', borderBottom: '1px solid #eee' }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#2D1B69' }}>Edit Form — {editingCoForm.name}</div>
              <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>Correct any field, then save to re-generate the PDF.</div>
            </div>
            <div style={{ padding: '8px 22px', overflowY: 'auto', flex: 1 }}>
              {Object.keys(editCoFields).length === 0 ? (
                <div style={{ color: '#6b7280', padding: '24px 0', textAlign: 'center' }}>
                  No fields populated yet — this form has no data (the transaction/broker fill endpoints aren't wired yet).
                </div>
              ) : (
                Object.keys(editCoFields).sort().map((key) => (
                  <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '8px 0', borderBottom: '1px solid #f2f2f5' }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{key}</label>
                    <input
                      type="text"
                      value={editCoFields[key] ?? ''}
                      onChange={(e) => setEditCoFields((prev) => ({ ...prev, [key]: e.target.value }))}
                      style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}
                    />
                  </div>
                ))
              )}
            </div>
            <div style={{ padding: '16px 22px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setEditingCoForm(null)} disabled={isSavingCoForm}
                style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleSaveColoradoOverrides} disabled={isSavingCoForm}
                style={{ padding: '9px 16px', borderRadius: 8, border: 'none', background: '#2D1B69', color: '#fff', cursor: 'pointer', opacity: isSavingCoForm ? 0.7 : 1 }}>
                {isSavingCoForm ? 'Saving…' : 'Save & Re-generate'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Folder ZIP download — bottom-right "Preparing download" panel, mirrors Google
          Drive's own async-download indicator (a fixed corner card that tracks progress
          without blocking the rest of the page). */}
      {folderDownload && createPortal(
        <div className="txn-folder-download-panel">
          <div className="txn-folder-download-header">
            <span>{folderDownload.status === 'zipping' ? 'Preparing download' : 'Download'}</span>
            <button
              type="button"
              className="txn-folder-download-close"
              aria-label="Dismiss"
              onClick={() => setFolderDownload(null)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div className="txn-folder-download-row">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
            <span className="txn-folder-download-name">
              {folderDownload.status === 'zipping' && `Zipping ${folderDownload.name}`}
              {folderDownload.status === 'done' && `${folderDownload.name}.zip`}
              {folderDownload.status === 'error' && `${folderDownload.name} — failed`}
            </span>
            {folderDownload.status === 'zipping' && <span className="txn-folder-download-spinner" />}
            {folderDownload.status === 'done' && (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2e7d32" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            )}
            {folderDownload.status === 'error' && (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#c0392b" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            )}
          </div>
        </div>,
        document.body
      )}

    </>
  );
};

export default TransactionDocuments;
